import { useOperationKeyboard, usePadKeys } from './operation-keyboard.js';
import { AccountField, CategoryField, ArithmeticIcon, ChoiceSheet, type ChoiceOption, IncomeLink, NoteField, PickRow } from './operation-fields.js';
import { incomeStreamOptions, partialIncome, pendingRest, restToRecord } from './income-options.js';
import { categoryDeletable, deleteCategory, saveCategory } from './category-store.js';
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  ZERO, add, bufferView, clampPos, computeAvailable, isPlanned, computePlan, diagnose,
  computeRunway, expectedIncomes, focusOptions, forecastCoverage, formatMoney, paymentInstances,
  amortize, computeInsightCandidates, loanProgress, progressLabel, prioritize, staleEntries,
  visibleInsights, obligationSchedule, repeatOf, endOf, repeatText,
  DEFAULT_REPEAT, NEVER_ENDS,
  type InsightCandidate,
  accountIcon, addDays, cadenceTitle, monthShare as periodMonths, planPeriodOf, liquidOf, needsDecision, reservedOf,
  share, throughDay,
  activeAllocation, allocationView, confirmed, needsReview, proposeAllocation,
  awaited, expectationState, expectedRest, isAfter, isBefore, nextForStream, previousOccurrence,
  unresolvedExpectations, unsettledInstances, backedReserves, incomeOutlook, outlookGap,
  cardPayments, notCovered,
  nextIncomeDate, resolveHorizon, rhythmOf, rhythmText, roleOfStream, streamFingerprint,
  type IncomeRhythm,
  classifyItem, collapseTail, daysInMonth, diffDays, evalAmount, kop, minM, monthFacts, monthOf,
  monthReport,
  nextOccurrence, prevOccurrenceInclusive,
  obligationsOwed,
  goalEta, monthlyForDeadline,
  monthTitle, nextOccurrenceFrom, orderGoals, pace, pd, savedPerYear, savingsNudge,
  scheduleLabel, monthlyEquivalentFactor,
  shiftMonth, sub, totalDebt,
  DEBTS_RULE, METHOD_RULE, annualCost, debtQueue, debtReason, debtWhy,
  type DebtItem, type DebtMethod,
  type AccountKind,
  type AcceptedAllocation,
  type IncomeStream,
  type Goal,
  type PriorityKind,
  type AvailableResult, type Coverage, type Focus, type FocusKind, type Money,
  type PaymentInstance, type PlainDate,
  type PlanPart,
  type Tier,
  type GoalPriority,
  type PlanLineKind,
  type PlanResult,
} from '@skoka/core';
import {
  aiConfigured, categorizeBasket, classifyBasket, parsePhrase, parseReceiptPhoto,
  type ParsedPhrase, type ParsedReceipt, type ReceiptItem,
} from './ai.js';
import { ARCHIVE_SIDE, prepareReceipt } from './image.js';
import { isNewCategory, PART_OPTIONS, PART_QUESTION, PART_TIP, PART_TIP_SHEET, modelCatalog, topLevel, type Category } from './categories.js';
import { NewCatsAsk, NewCatsLines, NewCatsModeRow, NewModeQuestion, useNewCategories } from './new-categories.js';
import { CategoryGlyph, colorOf, type CategoryColor } from './category-icon.js';
import { bankOf } from './banks.js';
import { Icon } from './icon.js';
import { flash as flashMessage } from './flash.js';
import { Operations, OpDays, byDay, AccountSheet as AccountPick } from './operations.js';
import { LoanScreen, MoneyOverview, queueTitle } from './money-overview.js';
import { nextPaymentInstance } from './next-payment.js';
import { GoalScreen } from './goal-screen.js';
import { AccountCard, CardRow, OpTypeSwitch, QuickCategories, SwipeCard } from './op-cards.js';
import { sharedFilter } from './period-filter.js';
import { bankName } from './bank-shots.js';
import { AdjustmentSheet, AdjustSheet, checkedText } from './adjust.js';
import { BalanceShotsSheet } from './balance-shots.js';
import { VoiceSheet } from './voice-sheet.js';
import { CreateAccountContext, CreateDebtContext, CreateGoalContext } from './create-context.js';
import { debtOptions, splitDebt, type DebtOption } from './debt-pay.js';
import { nameKey, rememberedCategories } from './category-memory.js';
import { ReceiptCamera } from './receipt-camera.js';
import { GoalRow, NewGoalRow } from './goal-row.js';
import { defaultGoalAccount, goalAccountId, goalsOnAccount, savingAccounts } from './goal-account.js';
import { downloadOperationsCsv } from './export-csv.js';

import { Onboarding } from './onboarding.js';
import { DebtSheet, loanToDraft, newLoan, saveDraftLoan, saveLoanEdit, type DLoan } from './debt-sheet.js';
import { IncomeSheet, draftRhythm, newIncome, type IncomeDraft } from './income-form.js';
import { BigAmount, CardDays, CardHead, CardInput, CardLook, CardMoney, CardToggle, Segs } from './card-fields.js';
import { AccountSheet, GraceRows, KIND_NAME, createAccount, newAccount, type AccKind } from './account-form.js';
import { GoalSheet, newGoal } from './goal-form.js';
import { ObligationSheet } from './obligation-form.js';
import { PrepaySheet } from './prepay.js';
import { AmountInput } from './amount.js';
import { PencilIcon, SheetWrap } from './sheet.js';
import { Collapse, DateButton, WhenField } from './date-picker.js';
import { recall, recallType, remember, rememberType, type OpType } from './op-memory.js';
import { PaymentSheet } from './payment-sheet.js';
import { useInbox } from './inbox.js';
import { InboxSheet, type InboxStart } from './inbox-ui.js';
import { HelpSheet } from './help.js';
import { Tip } from './tip.js';
import { ImportRow } from './import-row.js';
import { ReceiptBar, ReceiptHead, ReceiptItems, ReceiptSelectors, SplitBanner } from './receipt-view.js';
import { CloseX } from './close-icon.js';
import { currentPath, navigate, resolveRoute, type SessionState } from './routes.js';
import { ForgotPassword, ResetPassword } from './password-reset.js';
import { Welcome, Plans } from './welcome.js';
import { DOC_ACCEPT, ReceiptDoc } from './receipt-doc.js';
import { CheckPurchase } from './check.js';
import { DistributeIncome } from './distribute-ui.js';
import { EditGoal, GoalTerms, goalPlan, termsMissing } from './goal-edit.js';
import { budgetOf, keepMonthPlan } from './plan-version.js';
import { currentPeriodOf, keepPeriod, keepPeriodBaseline, periodBudget } from './period-view.js';
import { Analytics, analyticsView } from './analytics-screen.js';
import { ScreenMark, activeTime, sheetKey, track } from './track.js';
import { ProfileCapsule, ProfileSheet } from './profile.js';
import { SetRow } from './set-row.js';
import { HomeTour, tourWanted } from './home-tour.js';
import { isSubscription, serviceOf } from './services.js';
import { NetBadge, type NetState } from './net-badge.js';
import { Documents, DOC_VERSION, LEGAL_URL, currentConsent } from './documents.js';
import { PasswordField } from './password-field.js';
import { DeleteAccount } from './delete-account.js';
import { InsightCards } from './insight-cards.js';
import { AfterIncomeSheet, HeroCard, longDate, PayTimeline, PaymentsSheet, ReserveSheet, TodoRail, usualSource, type HeroStrip, type HomeTodo, type PayRowView, type ReserveTarget } from './home-parts.js';
import { AllocateSheet } from './allocate.js';
import { DistributeRest } from './distribute-rest.js';
import { ShortfallSheet } from './shortfall.js';
import { ModeSheet } from './mode-sheet.js';
import { ArrivalSheet, StableSheet } from './stable.js';
import { EffectRows, goalChange, obligationChange } from './change-preview.js';
import { RemedySheet } from './remedy.js';
import {
  ScheduleFields, scheduleOf, scheduleReady, type ScheduleDraft,
} from './schedule-fields.js';
import {
  AskModal, emptyTalk, lastReviewAt, purchasePrefill, type AskIntent, type Talk,
} from './ask-modal.js';
import { PlanFromIncome, allocationLabel, type PlanTarget } from './plan-income.js';
import { FulfilAllocations, type FulfilTarget } from './fulfil.js';
import {
  accessChanged, advisorGate, dayLabel, fetchAccess, onAccessChanged, featureView, setTier, startTrial, trialOfferSeen, UNKNOWN_ACCESS, useAccess,
  type AccessState, type FeatureView,
} from './access.js';
import { ProLockup, ProPaid, ProPayWait, ProPlans, ProScreen, TrialEnded, TrialOffer, type ProEntry } from './pro-screen.js';
import { fetchBilling, paymentState, startCheckout, SubscriptionSheet } from './subscription.js';
import { ProContext, useOpenPro, type ProOpen } from './pro-context.js';
import { dayMonth, dayMonthYear, plural, pluralWord } from './text.js';
import { AccountGlyph, accountColor } from './account-glyph.js';
import { importAccountId } from './import-account.js';
import { accountGroups, groupOf, payAccounts } from './account-groups.js';
import {
  login as apiLogin, logout as apiLogout, me as apiMe, onServerReach, passwordResetAvailable, pull, push, register as apiRegister, snapshotPrint,
  toPayload, getMarketing, setMarketing, type Conflict, type Marketing, type User,
} from './sync.js';
import {
  addAccount, addCreditCard, addExpense, addGoal, addIncome, addIncomeTx, addLoanPrepayment, addObligation, deleteLoan, setCardRate,
  parseRateInput,
  addTransfer, archiveAccount, restoreAccount,
  deleteGoal, deleteTransaction, restoreTransaction, formatAmountInput, groupExpr, loadDraft, loadState, parseAmountInput,
  addExpectedIncome, applyDistribution, allocationsToExecute, cancelAllocation, cancelExpectedIncome,
  allocationProposal, allocationViewOf, closeExpectation, decideInstance, deleteReserve,
  forgetInsights, stableMonth, stableSnapshot, stableSuggested,
  incomeFacts, newRequestId, planInput,
  receiveExpectedIncome,
  receivedIncomes, recordConsent, resetEverything, saveTalk, loadTalk, clearTalk,
  getLocalChangedAt, linkPaymentTx, getReceiptPhoto, pushedPrints, rememberPushed, getSyncUnsent, getSyncVersion, putReceiptPhoto, replaceLocalState,
  fundGoal, setBaselines, setCreditReserved, setReserve, skipGoalThisCycle,
  setSyncUnsent, setSyncVersion, settleObligation, todayPd, unsettleObligation,
  updateAccount, updateGoal, updateIncome, updateTransaction,
  updateObligation, upsertCreditTerms, deleteIncome, deleteObligation,
  type AppState, type DeletedTx, type Tx, type TxItem,
} from './store.js';
import { quietly } from './db.js';
import { paymentFor, paymentTxs, splitLine, splitNote, splitPayment } from './payment-match.js';
import { dismissInsight, markInsightShown, saveAccountOrder, setBaselineAmounts, setNewCategoryMode, setPeriodChoice, setUsualPinned, topUpReserve } from './store.js';
import { valueOr, type Amount, type Obligation } from '@skoka/core';
import moreDots from './assets/nav/more.svg';
import { Wordmark } from './logo.js';
import { SpendSheet } from './spend-sheet.js';
import { useSwipeTabs } from './swipe-tabs.js';
import { SWIPE_OPTABS_HINT, SwipeCoach, coachSeen, coachWanted } from './swipe-coach.js';
import type { SpendFocus } from './spend-steps.js';

/**
 * Пять разделов внизу — по одному на вопрос человека.
 *
 * Третьим стоит «Аналитика», а план месяца живёт внутри неё, в «Обзоре».
 * Отдельная вкладка «План» с «Принять план» и раскладкой оказалась
 * непонятной: она повторяла настройки и ни на что не влияла, а вопрос
 * «уложился ли я» человек задаёт там же, где смотрит, на что ушли деньги.
 *
 * Четвёртая — «Деньги» (14.10.2026): счета, доход, платежи с долгами и
 * цели — всё про деньги человека в одной вкладке. Внутри у неё id
 * 'accounts', как было у «Счетов». «Ещё» — профиль и редкие настройки.
 */
/*
  Меню с 27.10.2026 (docs/design/goals-tab-2026-10-27): «Цели» — на месте
  «Ещё», «Ещё» — листом «Настройки» за шестерёнкой в шапке. У «Денег»
  вкладок больше нет: обзор, а из него — экраны счёта, кредита и списки.
*/
type Tab = 'today' | 'tx' | 'stats' | 'accounts' | 'goals';
/** Задачи, у которых «главное число» — это нехватка денег. */
const MONEY_SHORT: PriorityKind[] = [
  'survive_to_income', 'cover_obligations', 'avoid_interest_or_overdue', 'income_gap',
];

type Sheet =
  | null | 'expense'
  /** Записать приход: та же форма, сразу на «Приход» и с этой зарплатой — из напоминания о задержке. */
  | { recordIncome: string }
  /** Записать с экрана счёта: счёт и вид уже выбраны. */
  | { recordOn: { accountId: string; type: 'expense' | 'transfer' } }
  | 'account' | 'income' | 'obligation' | 'baseline' | 'menu' | 'goal' | 'receipt' | 'settings'
  /** Новая цель с подсказки пустого экрана «Целей»: название и вид уже стоят. */
  | { newGoal: { name: string; kind: 'emergency_fund' | 'purchase' | 'debt_payoff' } }
  | 'receipt_doc' | 'check' | { distribute: string }
  | { part: 'need' | 'want' }
  | { reserve: string } | { editAccount: string } | { editCard: string }
  /** Скорректировать остаток; закрыли — назад к форме счёта. */
  | { adjust: string; from?: 'ops' } | { balanceShots: File[] }
  | { envelope: string } | { editIncome: string } | { editObligation: string }
  /** Лист платежа: «Оплатить», «Отложить», правка — тап в «Платежах». */
  | { payment: string }
  /** «Центр уведомлений»: с какой вкладки или сразу «Написать в поддержку». */
  | { inbox: InboxStart }
  /** «Помощь»: как пользоваться и частые вопросы. */
  | 'guides' | 'faq'
  | { editGoal: string } | { settle: { id: string; due: string; amount: Money } }
  | { prepay: string } | { fund: string } | { editLoan: string }
  /** Новый кредит: из «Долгов» или из подсказки нового платежа. */
  | { newLoan: DLoan }
  | { remedy: Money; from?: Coverage } | 'subscriptionNew' | 'why' | 'auth' | 'conflict' | 'allocate' | 'mode' | 'stable'
  /** Распределить нераспределённый остаток раскладки. */
  | 'rest'
  /** Нехватка на конкретный платёж: арифметика и варианты. */
  | { short: Coverage }
  /** Ручная форма, открытая сразу на разборе фразы. */
  | { expensePhrase: true }
  /** Пришли внешние деньги: куда их разложить при стабильном бюджете. */
  | { arrival: Money }
  /** Назначение будущих денег на конкретное наступление платежа. */
  | 'profile' | 'documents' | 'deleteAccount' | 'subscription'
  | { planIncome: PlanTarget }
  /** Приход случился: предложение исполнить назначенное. */
  | { fulfil: FulfilTarget }
  /** «Как посчитано»: с «Можно тратить» или с плитки под ним. */
  | { spend: SpendFocus };

export function App() {
  const [state, setState] = useState<AppState | null>(null);
  const [tab, setTab] = useState<Tab>('today');
  /** Раздел «Денег». Ссылки из других экранов открывают нужный сразу. */
  /** Экран внутри «Денег»: счёт, кредит, календарь; другая вкладка — назад к обзору. */
  const [moneyScreen, setMoneyScreen] = useState<null | 'plans' | 'order' | 'queue' | { account: string } | { loan: string }>(null);
  const moneyScreenRef = useRef<unknown>(moneyScreen);
  moneyScreenRef.current = moneyScreen;
  /** Архив счетов — листом поверх «Денег». */
  const [archiveOpen, setArchiveOpen] = useState(false);
  // Знакомство с главной при первом входе (03.11.2026): закрыли — до перезапуска не показывать.
  const [tourOn, setTourOn] = useState(true);
  useEffect(() => { if (tab !== 'accounts') setMoneyScreen(null); }, [tab]);
  /** Открытая цель во вкладке «Цели». */
  const [goalScreen, setGoalScreen] = useState<string | null>(null);
  const goalScreenRef = useRef(goalScreen);
  goalScreenRef.current = goalScreen;
  useEffect(() => { if (tab !== 'goals') setGoalScreen(null); }, [tab]);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [goalScreen]);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [moneyScreen]);
  /*
    Другой раздел открывается сверху. Прокручивается вся страница, а не
    экран, и раньше новый раздел открывался на той высоте, где человек ушёл
    из прошлого, — середина «Операций» вместо начала «Главной».
  */
  const shownTab = useRef(tab);
  useEffect(() => {
    if (shownTab.current === tab) return;
    shownTab.current = tab;
    window.scrollTo({ top: 0 });
  }, [tab]);
  const [sheet, setSheetState] = useState<Sheet>(null);
  /*
    Окно, открытое из «Настроек» или меню, закрывается назад к ним, а не на
    главную (02.11.2026: «Обычные траты» → карандаш → крестик выкидывал из
    настроек; так же «Как пользоваться» и «Частые вопросы»). Родитель
    вспоминается после закрытия, если человек остался на том же экране:
    ушёл из окна на «Цели» или в «Деньги» — настройки обратно не всплывают.
  */
  const sheetParent = useRef<Sheet>(null);
  const backTo = useRef<{ sheet: Sheet; tab: Tab; goal: string | null; money: unknown } | null>(null);
  const setSheet = useCallback((x: Sheet) => {
    if (x === null && sheetParent.current) {
      backTo.current = { sheet: sheetParent.current, tab: shownTab.current, goal: goalScreenRef.current, money: moneyScreenRef.current };
      sheetParent.current = null;
    }
    setSheetState(x);
  }, []);
  /** Открыть окно поверх родителя: закроют — вернётся родитель. */
  const openFrom = (parent: Sheet) => (x: Sheet) => { sheetParent.current = parent; setSheetState(x); };
  useEffect(() => {
    const back = backTo.current;
    if (!back) return;
    backTo.current = null;
    if (sheet === null && tab === back.tab && goalScreen === back.goal && moneyScreen === back.money) setSheetState(back.sheet);
  }, [sheet, tab, goalScreen, moneyScreen]);
  const [operationDraft, setOperationDraft] = useState<GoalOperationDraft | undefined>();
  const [goalInspection, setGoalInspection] = useState<{
    draft: GoalOperationDraft; sheet: Sheet; tab: Tab;
    goalScreen: typeof goalScreen; moneyScreen: typeof moneyScreen;
  } | null>(null);
  const returnToOperation = (chooseGoal?: string) => {
    if (!goalInspection) return;
    const back = goalInspection;
    const different = chooseGoal !== undefined && chooseGoal !== back.draft.goalId;
    const goal = state?.goals.find((g) => g.id === chooseGoal);
    setOperationDraft({ ...back.draft,
      ...(chooseGoal ? { goalId: chooseGoal } : {}),
      ...(different ? { toId: goal && state ? goalAccountId(state, goal) ?? '' : '' } : {}),
      reopenGoalPicker: chooseGoal === undefined,
    });
    setGoalInspection(null); setTab(back.tab); setGoalScreen(back.goalScreen);
    setMoneyScreen(back.moneyScreen); setSheet(back.sheet);
  };
  const navigateTab = (next: Tab) => {
    setGoalInspection(null); setOperationDraft(undefined); setTab(next);
  };
  useEffect(() => {
    if (!state || !goalScreen || state.goals.some((g) => g.id === goalScreen)) return;
    if (goalInspection) returnToOperation();
    else setGoalScreen(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, goalScreen]);
  // Какие листы открывают — для отчёта владельцу: имя листа, без содержимого.
  const openSheet = sheetKey(sheet);
  useEffect(() => { if (openSheet) track('sheet_open', { sheet: openSheet }); }, [openSheet]);
  // Куда вернуть человека из «Условий»: в профиль, если пришёл оттуда, иначе
  // туда, где был, — например, в разговор с советником.
  const [docsBack, setDocsBack] = useState<Sheet>(null);
  /** Выбор счёта поверх «Скорректировать остаток». */
  const [adjustPick, setAdjustPick] = useState(false);
  /** Документы поверх формы аккаунта, открытой листом. */
  const [authDocs, setAuthDocs] = useState(false);
  useEffect(() => { if (sheet !== 'auth') setAuthDocs(false); }, [sheet]);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  /** Центр уведомлений: перечитывается при входе в приложение и раз в две минуты. */
  const { inbox, refresh: refreshInbox } = useInbox(user !== null);
  /*
    Доступ к ИИ — для кнопки «Спроси SKOKA» в шапке. Спрашиваем при входе и при смене
    человека; пока ответа нет, кнопки нет: показать и отобрать хуже, чем
    показать на секунду позже.
  */
  const aiAccess = useAccess([user?.email]) ?? UNKNOWN_ACCESS;
  /*
    Экран Pro — слой поверх всего (docs/design/pro-2026-10-21). Один на все
    входы: голос, чек, выписка, вопрос, Аналитика. После закрытия человек там
    же, где был: листы под экраном остаются открытыми.
  */
  const [pro, setPro] = useState<{ entry: ProEntry; question?: string } | null>(null);
  const [trialOffer, setTrialOffer] = useState(false);
  const [proBusy, setProBusy] = useState(false);
  const [proError, setProError] = useState<string | null>(null);
  /** Откуда человек пришёл в Pro — туда же вернуть после оплаты (§2.1). */
  const lastEntry = useRef<{ entry: ProEntry; question?: string }>({ entry: 'general' });
  const openPro: ProOpen = useCallback((entry, opts) => {
    track('plans_opened', { from: entry });
    setProError(null);
    lastEntry.current = { entry, ...(opts?.question ? { question: opts.question } : {}) };
    setPro({ entry, ...(opts?.question ? { question: opts.question } : {}) });
  }, []);
  const [plans, setPlans] = useState<false | 1 | 3 | 6 | 12>(false);
  /*
    Возврат со страницы ЮKassa: `?pay=<id>`. Pro включает сервер по
    подтверждению банка; здесь только ждём ответа и показываем его.
  */
  const [pay, setPay] = useState<{ id: string; state: 'pending' | 'succeeded' | 'canceled'; until: string | null; reason: string | null } | null>(null);
  /*
    После оплаты с автопродлением — «Добавить в платежи» (04.11.2026): следующее
    списание приложение знает само, и «Можно тратить» должно его учитывать.
    Цена следующего — с сервера: первый год по скидке стоит меньше продления.
  */
  const [proOffer, setProOffer] = useState<{ amount: Money; months: number; next: PlainDate } | null>(null);
  useEffect(() => {
    if (pay?.state !== 'succeeded' || !pay.until) return;
    const intent = payIntent();
    if (!intent?.renew || state?.obligations.some((o) => o.isActive && serviceOf(o.name)?.id === 'skoka')) return;
    void fetchBilling().then((b) => {
      const s = b.subscription;
      if (!s?.renews || !s.nextAmount || !s.months) return;
      setProOffer({ amount: s.nextAmount as Money, months: s.months, next: pay.until!.slice(0, 10) as PlainDate });
    }).catch(() => undefined);
  }, [pay?.state, pay?.until]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const id = q.get('pay');
    if (q.has('pay') || q.has('sub') || q.has('inbox')) window.history.replaceState(null, '', window.location.pathname);
    if (id) setPay({ id, state: 'pending', until: null, reason: null });
    if (q.has('sub')) setSheet('subscription');
    // Ссылка из письма с ответом поддержки — сразу в чаты.
    if (q.has('inbox')) setSheet({ inbox: q.get('inbox') === 'chats' ? 'chats' : 'notes' });
  }, []);
  useEffect(() => {
    if (!pay || pay.state !== 'pending') return;
    let tries = 0;
    let stop = false;
    const tick = async () => {
      if (stop) return;
      tries += 1;
      const st = await paymentState(pay.id).catch(() => null);
      if (stop) return;
      if (st?.status === 'succeeded') { accessChanged(); setPay({ ...pay, state: 'succeeded', until: st.until }); return; }
      if (st?.status === 'canceled') { setPay({ ...pay, state: 'canceled', reason: st.reason }); return; }
      if (tries < 30) setTimeout(() => void tick(), 2000);
    };
    void tick();
    return () => { stop = true; };
  }, [pay?.id, pay?.state]);
  const buyPro = async (months: 1 | 3 | 6 | 12, renew: boolean) => {
    setProBusy(true); setProError(null);
    try {
      track('pay_pressed', { months, renew });
      // Страница ЮKassa уводит из приложения: что человек делал и какой срок
      // выбрал — помним до возврата, чтобы вернуть туда и не выбирать заново.
      try { sessionStorage.setItem(PAY_INTENT, JSON.stringify({ ...lastEntry.current, months, renew })); } catch { /* приватный режим */ }
      window.location.href = await startCheckout(months, renew);
    } catch (e) {
      setProError(e instanceof Error ? e.message : 'Не получилось открыть оплату');
      setProBusy(false);
    }
  };
  /** Вернуть к тому, ради чего человек платил: Pro уже включён. */
  const resume = (intent: { entry: ProEntry; question?: string }) => {
    if (intent.entry === 'voice') setSheet({ expensePhrase: true });
    else if (intent.entry === 'scan' || intent.entry === 'file') setSheet('expense');
    else if (intent.entry === 'ask' || intent.entry === 'insight') setAsk({ mode: 'ask', ...(intent.question ? { question: intent.question } : {}) });
    else if (intent.entry === 'analytics' || intent.entry === 'review') setTab('stats');
    else if (intent.entry === 'balances') {
      // Остатки — с основного счёта для трат; «Все остатки разом» — в том же листе.
      const id = state?.accounts.find((a) => !a.archived && a.isLiquid && a.kind !== 'credit_card')?.id;
      if (id) setSheet({ adjust: id, from: 'ops' });
    }
  };
  const runTrial = async () => {
    setProBusy(true);
    try {
      await startTrial();
      track('trial_started');
      setPro(null);
      setTrialOffer(false);
    } catch (e) {
      setProError(e instanceof Error ? e.message : 'Не получилось включить пробный период');
    } finally { setProBusy(false); }
  };
  /*
    «7 дней с Pro» — один раз после конца пробного: что человек успел. Отметка
    на устройстве: показать второй раз на другом — не беда, а не показать
    вовсе — потеря самого честного довода.
  */
  // Ключ — номер аккаунта, а не почта: адрес в хранилище браузера ни к чему
  // (22.10.2026). Прежние ключи с почтой переносятся и удаляются.
  const trialEndKey = `skoka.trialEnded:${user?.id ?? ''}`;
  useEffect(() => {
    try {
      for (const k of Object.keys(localStorage)) {
        if (!k.startsWith('skoka.trialEnded:') || !k.includes('@')) continue;
        if (user && k === `skoka.trialEnded:${user.email}`) localStorage.setItem(trialEndKey, localStorage.getItem(k) ?? '');
        localStorage.removeItem(k);
      }
    } catch { /* приватный режим */ }
  }, [user, trialEndKey]);
  const [trialEndSeen, setTrialEndSeen] = useState(() => {
    try { return localStorage.getItem(trialEndKey) !== null; } catch { return true; }
  });
  useEffect(() => {
    try { setTrialEndSeen(localStorage.getItem(trialEndKey) !== null); } catch { setTrialEndSeen(true); }
  }, [trialEndKey]);
  // Ничего не попробовал — три нуля ни о чём не говорят: экран не показываем.
  const usage = aiAccess.trial.usage;
  const trialEnd = !trialEndSeen && aiAccess.tier === 'free' && usage
    && usage.phrase + usage.receipt + usage.analysis > 0 ? usage : null;
  const seeTrialEnd = () => {
    try { localStorage.setItem(trialEndKey, new Date().toISOString()); } catch { /* приватный режим */ }
    setTrialEndSeen(true);
  };
  // Четыре состояния запуска вместо «user === null»: пока сессия проверяется,
  // нельзя показывать ни приветствие, ни анкету — вернувшийся человек увидел
  // бы чужой экран на долю секунды.
  const [session, setSession] = useState<SessionState>('checking');
  const [path, setPath] = useState<string>(currentPath());
  // Незавершённая настройка продолжается, а не начинается заново.
  const [hasDraft, setHasDraft] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  /**
   * Данные аккаунта, встретившиеся с данными устройства.
   *
   * Не `null` — значит, человек ещё не выбрал, какую картину оставить, и до
   * его решения ничего не меняется ни здесь, ни на сервере.
   */
  /** Проверенная покупка, которую человек решил записать. */
  const [buy, setBuy] = useState<{ amount: Money; date: PlainDate; accountId?: string } | null>(null);
  /**
   * Ключ принимаемой раскладки.
   *
   * Один на открытый экран: повтор после ошибки — то же действие и не должен
   * ни удваивать приход, ни закреплять конверты второй раз.
   */
  const [distributeKey, setDistributeKey] = useState(() => newRequestId());
  /**
   * Разговор со SKOKA. Живёт в приложении, а не в окне: закрыть окно, чтобы
   * посмотреть на числа, и вернуться — обычное дело, и начатое уточнение при
   * этом пропадать не должно.
   */
  const [talk, setTalkState] = useState<Talk>(emptyTalk);
  /**
   * Разговор переживает перезагрузку.
   *
   * Он жил только в памяти вкладки: обновил страницу — и разбор, за который
   * списана единица пакета, исчезал вместе с ней. Человек справедливо
   * спрашивал, где история.
   */
  const setTalk = useCallback((next: Talk) => {
    setTalkState(next);
    void saveTalk(next);
  }, []);
  useEffect(() => { void loadTalk<Talk>().then((t) => { if (t) setTalkState(t); }); }, []);
  /** Зачем открыт советник: разбор картины или вопрос. */
  const [ask, setAsk] = useState<AskIntent | null>(null);
  const [merge, setMerge] = useState<
    { accounts: number; transactions: number; savedAt?: string; liquid?: Money } | null
  >(null);
  const [skipOnboarding, setSkipOnboarding] = useState(false);
  /** Данные аккаунта едут с сервера: показываем загрузку, а не анкету. */
  const [restoring, setRestoring] = useState(false);

  const reload = useCallback(async () => {
    try {
      let next = await loadState();
      // План месяца сохраняется сам: кнопки «Принять план» больше нет, а
      // без сохранённой версии правка дохода переписала бы прошлые месяцы.
      // Так же сами закрепляются источник периодов аналитики и основа
      // сравнения идущего периода. Уедут на сервер со следующей синхронизацией.
      // Пересчёт сам собой — не изменение человека: телефон от него не
      // становится «новее» сервера в листе расхождения версий.
      if (!next.isEmpty) {
        const snap = next;
        const changed = await quietly(async () => {
          const month = await keepMonthPlan(snap);
          const period = await keepPeriod();
          const baseline = await keepPeriodBaseline();
          return month || period || baseline;
        });
        if (changed) next = await loadState();
      }
      setState(next);
      // Уборка журнала наблюдений: повод исчез — отметка «отложено» больше
      // ничего не описывает. Если то же случится снова, это будет новый повод,
      // а не продолжение старого, и человек должен его увидеть.
      if (next.insightLog.length > 0 && !next.isEmpty) {
        const stale = staleEntries(
          computeInsightCandidates({
            today: next.today,
            available: computeAvailable(next),
            goals: next.goals,
            incomes: next.transactions
              .filter((t) => t.type === 'income' && t.balanceApplied !== false)
              .map((t) => ({ date: t.occurredDate, amount: t.amount })),
          }),
          next.insightLog,
        );
        if (stale.length > 0) await forgetInsights(stale.map((j) => j.key));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось открыть локальное хранилище');
    }
  }, []);

  /**
   * Отправка состояния на сервер.
   *
   * Вызывается после каждого изменения, но только при входе в аккаунт: без
   * аккаунта приложение работает целиком локально. Ошибка сети не мешает
   * работе — она лишь помечается, потому что данные уже лежат на устройстве.
   */
  /*
    Отправки — по одной.

    Каждое действие отправляло весь снимок сразу, и пять быстрых нажатий
    давали пять одновременных записей: на сервере они вставали в очередь за
    одной строкой и занимали все соединения с базой. Теперь, пока идёт одна
    отправка, следующие собираются в одну — с самым свежим снимком.
  */
  const pushing = useRef(false);
  const pushAgain = useRef(false);
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null);

  /*
    Связь с сервером — значок в шапке главной (макет 15.10.2026).

    `trouble` — последняя попытка не дошла: следующая отправка покажет
    «Отправляю», а удачная — «Сохранено» на две секунды. Если отправлять
    нечего, значок уходит сразу, как только связь вернулась.
  */
  const [online, setOnline] = useState(() => navigator.onLine);
  const [serverDown, setServerDown] = useState(false);
  const [recover, setRecover] = useState<'sending' | 'saved' | null>(null);
  const trouble = useRef(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const on = (): void => setOnline(true);
    const off = (): void => setOnline(false);
    addEventListener('online', on);
    addEventListener('offline', off);
    return () => {
      removeEventListener('online', on);
      removeEventListener('offline', off);
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);
  const net: NetState = !online ? 'offline'
    : recover === 'sending' ? 'sending'
      : serverDown ? 'server'
        : recover === 'saved' ? 'saved' : 'ok';

  const pushState = useCallback(async () => {
    if (!user) return;
    if (pushing.current) { pushAgain.current = true; return; }
    pushing.current = true;
    setSyncing(true);
    if (trouble.current) setRecover('sending');
    let failed = false;
    try {
      const s = await loadState();
      const base = (await getSyncVersion()) ?? 0;
      const payload = toPayload(s);
      const print = await snapshotPrint(payload.state);
      await rememberPushed(print);
      const res = await push(payload, base);
      if ('conflict' in res) {
        /*
          На сервере наш же снимок — значит, ответ на прошлую отправку
          потерялся, а не кто-то изменил данные с другого устройства.
          Спрашивать не о чем: берём номер версии сервера и досылаем
          свежее. Лист — только при настоящих чужих изменениях.
        */
        const theirs = res.state?.state ? await snapshotPrint(res.state.state) : null;
        if (theirs && (await pushedPrints()).includes(theirs)) {
          await setSyncVersion(res.version);
          if (theirs !== print) pushAgain.current = true;
          setSyncNote(`Сохранено · версия ${res.version}`);
        } else {
          pushAgain.current = false;
          track('sync_conflict');
          setConflict(res);
          setSheet('conflict');
          setSyncNote('Расхождение версий');
        }
      } else {
        await setSyncVersion(res.version);
        setSyncNote(`Сохранено · версия ${res.version}`);
      }
    } catch (e) {
      failed = true;
      // Без сети не отправилось — это не сбой: данные уйдут, когда сеть будет.
      if (navigator.onLine) track('sync_failed');
      await setSyncUnsent(true);
      setSyncNote(e instanceof Error ? `Не отправлено: ${e.message}` : 'Не отправлено');
    } finally {
      pushing.current = false;
      setSyncing(false);
    }
    if (failed) {
      trouble.current = true;
      // Система знает, что сети нет, — «Нет интернета»; не знает (iPhone
      // часто не говорит) — «Нет связи с сервером».
      setOnline(navigator.onLine);
      setServerDown(navigator.onLine);
      setRecover(null);
    } else {
      setServerDown(false);
      if (trouble.current) {
        trouble.current = false;
        setRecover('saved');
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => { savedTimer.current = null; setRecover(null); }, 2000);
      }
    }
    if (pushAgain.current) {
      pushAgain.current = false;
      void pushState();
    } else if (failed && !retry.current) {
      // Сервер занят или сеть пропала — ещё раз через полминуты, не сразу.
      retry.current = setTimeout(() => { retry.current = null; void pushState(); }, 30_000);
    }
  }, [user]);
  useEffect(() => () => { if (retry.current) clearTimeout(retry.current); }, []);

  /*
    Недосланное — дослать: при входе в аккаунт и когда вернулась сеть.

    Без этого ответ, данный без связи, лежал бы только на устройстве до
    следующей записи операции, а другое устройство видело бы старую картину.
  */
  useEffect(() => {
    if (!user) return;
    const resend = (): void => { void getSyncUnsent().then((u) => { if (u) void pushState(); }); };
    resend();
    addEventListener('online', resend);
    return () => removeEventListener('online', resend);
  }, [user, pushState]);

  /*
    Проверка связи — самим: раз в 15 секунд, пока приложение на экране, и
    сразу, когда на него вернулись.

    Прежде значок ждал, что система скажет о пропаже сети, или что сорвётся
    отправка. На iPhone в приложении с главного экрана система молчит:
    `navigator.onLine` остаётся `true`, события `offline` нет, — и в
    авиарежиме значок не появлялся (владелец, 15.10.2026). Ответ сервера
    надёжнее: не ответил — значок, ответил — досылаем накопленное.

    Слушаем и любой другой запрос к серверу: лист записи говорил «Нужна
    сеть», а значка не было — у каждого была своя проверка (15.10.2026).
  */
  useEffect(() => {
    let stopped = false;
    let busy = false;
    let resending = false;
    /** Ответ сервера — от проверки или от любого запроса приложения. */
    const seen = (ok: boolean): void => {
      if (stopped) return;
      if (!ok) {
        trouble.current = true;
        setOnline(navigator.onLine);
        setServerDown(navigator.onLine);
        return;
      }
      setOnline(true);
      setServerDown(false);
      // Связь вернулась: накопленное — дослать («Отправляю» → «Сохранено»),
      // а если досылать нечего, значок просто уходит. Ответ самой отправки
      // приходит сюда же — второй раз её не начинаем.
      if (!trouble.current || pushing.current || resending) return;
      resending = true;
      void getSyncUnsent().then((unsent) => {
        resending = false;
        if (stopped || pushing.current) return;
        if (unsent && user) void pushState();
        else trouble.current = false;
      });
    };
    const check = async (): Promise<void> => {
      if (busy || document.visibilityState !== 'visible') return;
      busy = true;
      let ok = false;
      try {
        const ask = new AbortController();
        const late = setTimeout(() => ask.abort(), 8000);
        try {
          ok = (await fetch('/api/health', { cache: 'no-store', signal: ask.signal })).ok;
        } finally { clearTimeout(late); }
      } catch { ok = false; }
      busy = false;
      seen(ok);
    };
    const unlisten = onServerReach(seen);
    void check();
    const every = setInterval(() => { void check(); }, 15_000);
    const now = (): void => { void check(); };
    document.addEventListener('visibilitychange', now);
    addEventListener('focus', now);
    addEventListener('online', now);
    addEventListener('offline', now);
    return () => {
      stopped = true;
      clearInterval(every);
      document.removeEventListener('visibilitychange', now);
      removeEventListener('focus', now);
      removeEventListener('online', now);
      removeEventListener('offline', now);
      unlisten();
    };
  }, [user, pushState]);

  /**
   * Выход из аккаунта: данные с устройства стираются.
   *
   * Оставить их нельзя — следующий человек на этом устройстве увидел бы чужие
   * счета и суммы. Перед стиранием состояние отправляется на сервер, иначе
   * несохранённые изменения исчезли бы вместе с ним; если отправка не удалась,
   * выход отменяется и человек видит причину — молча уничтожить данные хуже,
   * чем не выйти.
   */
  const logout = useCallback(async () => {
    setSyncing(true);
    try {
      const s = await loadState();
      const base = (await getSyncVersion()) ?? 0;
      const res = await push(toPayload(s), base);
      if ('conflict' in res) {
        setConflict(res);
        setSheet('conflict');
        setSyncNote('Сначала разберись с расхождением версий');
        return;
      }
      await apiLogout();
      await resetEverything();
      await setSyncVersion(0);
      setUser(null);
      setConflict(null);
      setSkipOnboarding(false);
      setSyncNote(null);
      setTab('today');
      await reload();
    } catch (e) {
      setSyncNote(e instanceof Error ? `Выход отменён: ${e.message}` : 'Выход отменён');
    } finally {
      setSyncing(false);
    }
  }, [reload]);

  const reloadAndPush = useCallback(async () => {
    await reload();
    void pushState();
  }, [reload, pushState]);

  /** Забрать состояние с сервера и заменить локальное. */
  const pullState = useCallback(async (merge?: 'remote' | 'local') => {
    setSyncing(true);
    try {
      const remote = await pull();

      // Две непустые картины встречаются — выбирает человек (SF-03).
      //
      // Молча взять серверную значило бы стереть то, что человек только что
      // ввёл гостем; молча отправить локальную — стереть его прежний аккаунт.
      // Обе потери необратимы и незаметны.
      //
      // Спрашиваем только там, где вопрос настоящий: данные этого устройства
      // ни разу не синхронизировались с аккаунтом (`версия 0`), а в аккаунте
      // уже что-то есть. Нажатие «Загрузить с сервера» у того, кто давно
      // вошёл, — не встреча двух картин, а обычная загрузка, и задавать ему
      // тревожный вопрос про потерю данных не за что.
      const neverSynced = ((await getSyncVersion()) ?? 0) === 0;
      if (remote.state && merge === undefined && neverSynced) {
        const here = await loadState();
        const there = remote.state as {
          state?: { accounts?: { balance?: number; isLiquid?: boolean }[]; transactions?: unknown[] };
        };
        const remoteAccounts = there.state?.accounts?.length ?? 0;
        if (!here.isEmpty && remoteAccounts > 0) {
          setMerge({
            accounts: remoteAccounts,
            transactions: there.state?.transactions?.length ?? 0,
            liquid: (there.state?.accounts ?? [])
              .filter((a) => a.isLiquid !== false)
              .reduce((n, a) => n + (typeof a.balance === 'number' ? a.balance : 0), 0) as Money,
            ...(typeof (remote.state as { savedAt?: string }).savedAt === 'string'
              ? { savedAt: (remote.state as { savedAt?: string }).savedAt }
              : {}),
          });
          return;
        }
      }

      if (merge === 'local') {
        // Человек выбрал картину устройства: отправляем её, перезаписав чужую.
        const s = await loadState();
        const res = await push(toPayload(s), remote.version, true);
        if (!('conflict' in res)) {
          await setSyncVersion(res.version);
          setSyncNote(`Данные устройства отправлены · версия ${res.version}`);
        }
        await reload();
        return;
      }

      if (remote.state) {
        const res = await replaceLocalState(remote.state);
        // Отклонённый снимок — не молчаливая неудача: человек должен понять,
        // почему на этом устройстве осталось старое, и что делать дальше.
        if (!res.applied) {
          setSyncNote(`Снимок не принят: ${res.fatal[0] ?? 'данные повреждены'}. Локальные данные сохранены`);
          return;
        }
        await setSyncVersion(remote.version);
        setSyncNote(res.errors.length > 0
          ? `Загружено с сервера · версия ${remote.version} · не прочитано полей: ${res.errors.length}`
          : `Загружено с сервера · версия ${remote.version}`);
      } else {
        // На сервере пусто — отправляем то, что есть на устройстве.
        const s = await loadState();
        const res = await push(toPayload(s), 0);
        if (!('conflict' in res)) {
          await setSyncVersion(res.version);
          setSyncNote(`Отправлено на сервер · версия ${res.version}`);
        }
      }
      await reload();
    } catch (e) {
      setSyncNote(e instanceof Error ? e.message : 'Не удалось синхронизировать');
    } finally {
      setSyncing(false);
    }
  }, [reload]);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    let known = false;
    let missed = false;
    let later: ReturnType<typeof setTimeout> | null = null;
    const ask = (): void => {
      void apiMe()
        .then(async (u) => {
          // Пока сервер не отвечал, записанное не отправлялось и не помечалось:
          // кто вошёл, было неизвестно. Узнали — дослать один раз.
          if (u && missed) await setSyncUnsent(true);
          if (u) known = true;
          // Значок связи: сервер ответил. Досылать есть что только аккаунту.
          trouble.current = u !== null && missed;
          setServerDown(false);
          setUser(u); setSession(u ? 'user' : 'guest');
        })
        // Сервер не ответил — это не «гость»: локальные данные остаются
        // доступными, но пустой аккаунт из этого не следует. Спросим ещё раз:
        // занятый сервер через полминуты обычно отвечает.
        .catch(() => {
          missed = true;
          trouble.current = true;
          setServerDown(navigator.onLine);
          setSession('unavailable');
          if (!later) later = setTimeout(() => { later = null; if (!known) ask(); }, 20_000);
        });
    };
    ask();
    // Запуск без сети: кто вошёл, спрашиваем снова, когда сеть вернётся, —
    // иначе недосланное осталось бы на устройстве до следующего запуска.
    // Гость при этом остаётся гостем.
    const again = (): void => { if (!known) ask(); };
    addEventListener('online', again);
    return () => { removeEventListener('online', again); if (later) clearTimeout(later); };
  }, []);
  useEffect(() => { void loadDraft().then((dr) => setHasDraft(dr !== null)); }, []);
  /**
   * Восстановление данных аккаунта на пустом устройстве.
   *
   * У приложения, установленного на домашний экран, своё хранилище: сессия из
   * браузера ему достаётся, а данные — нет. Человек открывал его и попадал в
   * анкету, хотя вся картина лежала в аккаунте; заполнив её заново, он получил
   * бы вдобавок встречу двух картин при первой же синхронизации.
   *
   * Тянем один раз за запуск: `pullState` при пустом устройстве берёт
   * серверное как есть и ничего не перезаписывает.
   */
  const restored = useRef(false);
  useEffect(() => {
    if (session !== 'user' || !state || !state.isEmpty || restored.current) return;
    restored.current = true;
    setRestoring(true);
    void pullState().finally(() => setRestoring(false));
  }, [session, state, pullState]);
  useEffect(() => {
    const onPop = (): void => setPath(currentPath());
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, []);

  if (error) {
    return (
      <Shell>
        <div className="blk"><div className="blkhead"><b>Хранилище недоступно</b></div>
          <div className="note">{error}. В приватном окне браузера IndexedDB может быть
          отключён — открой обычное окно.</div>
        </div>
      </Shell>
    );
  }
  if (!state) return <Shell><div className="skel" /></Shell>;

  const route = resolveRoute({
    path,
    session,
    hasWorkingData: !state.isEmpty,
    hasDraft,
    restoring,
  });

  if (route === 'loading') return <Shell><div className="skel" /></Shell>;
  if (route === 'plans') return <><ScreenMark name="plans" /><Plans onBack={() => navigate('welcome')} /></>;
  if (route === 'reset') {
    return (
      <Shell>
        <ScreenMark name="reset" />
        <ResetPassword
          onLogin={() => navigate('login')}
          onDone={async (u) => {
            setUser(u);
            setSession('user');
            // Как после входа: сперва данные аккаунта, потом главная.
            await pullState();
            navigate('app');
          }} />
      </Shell>
    );
  }
  if (route === 'welcome' && !skipOnboarding) {
    return (
      <>
        <ScreenMark name="welcome" />
        <Welcome
          onStart={() => { track('onb_start'); navigate('onboarding'); }}
          onLogin={() => navigate('login')} />
      </>
    );
  }
  // Вход и регистрация — один экран с двумя вкладками, но приходят на него
  // по-разному: «Войти» со старта и «Зарегистрироваться» после разбора. Адрес
  // решает, какая вкладка открыта, иначе человек, звавший регистрацию, видит
  // форму входа и решает, что ошибся.
  if (route === 'login' || route === 'signup') {
    return (
      <Shell>
        <ScreenMark name={route} />
        <Auth
          mode={route === 'signup' ? 'register' : 'login'}
          saved={route === 'signup' && !state.isEmpty}
          onDocs={() => setSheet('documents')}
          {...(route === 'signup' && !state.isEmpty
            ? { onLater: () => navigate('app') }
            : {})}
          onClose={() => navigate(state.isEmpty ? 'welcome' : 'app')}
          onDone={async (u) => {
            setUser(u);
            setSession('user');
            // Сначала восстановление данных владельца аккаунта, потом главная:
            // пустое локальное состояние на сервер не отправляется.
            await pullState();
            navigate('app');
          }} />
        {/* Документы открываются прямо здесь: читать их человек должен до
            того, как поставит отметки, а не после создания аккаунта. */}
        {sheet === 'documents' && (
          <Documents state={state} onClose={() => setSheet(null)}
            onSaved={async () => { await reload(); }} />
        )}
      </Shell>
    );
  }
  if (state.isEmpty && !skipOnboarding) {
    return (
      <>
      <ScreenMark name="onboarding" />
      <Onboarding
        onDone={async () => {
          await reloadAndPush();
          // Принятый план сохранён на устройстве. Регистрация — следующий шаг,
          // а не условие: отказаться от неё можно, ничего не потеряв.
          navigate(user ? 'app' : 'signup');
        }}
        onSkip={() => { track('onb_skip_all'); setSkipOnboarding(true); navigate('app'); }}
        onLogin={() => navigate('login')} />
      </>
    );
  }
  if (state.isEmpty) return <Setup onDone={reload} />;

  const result = computeAvailable({
    ...state,
    // Ожидания влияют только на выбор горизонта и только те, дату которых
    // назвал человек. В сумму «Доступно» обещанные деньги не входят.
    expectations: state.expectedIncomes,
    incomeFacts: incomeFacts(state.transactions),
  });
  const plan = computePlan({
    incomeStreams: state.incomeStreams, obligations: state.obligations,
    needBaseline: state.needBaseline, goals: state.goals, accounts: state.accounts,
    // Режим распределения — выбор человека, а не константа в коде.
    // Пока он не выбран, остаётся прежнее «поровну».
    creditTerms: state.creditTerms, mode: state.planMode ?? 'balanced', today: state.today,
  });

  /*
    «Спроси SKOKA» — в шапке экранов (22.10.2026, docs/design/nav-2026-10-22):
    плавающая кнопка над «+» закрывала низ списков. Видят все: это самый
    заметный вход в Pro. У Pro, в пробный и пока есть бесплатный первый
    разбор — разговор; когда разбор уже был — экран Pro «Спроси».
  */
  const openAsk = () => {
    const gate = advisorGate(aiAccess).kind;
    if (gate === 'offer') openPro('ask');
    else if (gate === 'signup') navigate('signup');
    else setAsk({ mode: 'ask' });
  };
  const askHead = aiAccess.offline ? null : <AskHeadButton onClick={openAsk} />;

  return (
    <CreateAccountContext.Provider value={(done) => (
      <AddAccount onClose={() => done(null)} onSaved={async (account) => { track('account_added'); await reloadAndPush(); done(account ?? null); }} />
    )}>
    <CreateGoalContext.Provider value={(done) => (
      <AddGoal state={state} onClose={() => done(null)} onSaved={async (id) => { track('goal_added'); await reloadAndPush(); done(id ?? null); }} />
    )}>
    <CreateDebtContext.Provider value={(done) => (
      <DebtSheet init={newLoan('', 'amortizing')} isNew today={state.today}
        accounts={payAccounts(state.accounts)}
        payments={state.obligations.filter((o) => o.isActive && !o.prepay && !state.loans.some((l) => l.obligationId === o.id))}
        onClose={() => done(null)}
        onSave={async (v, { accountId, target }) => {
          const from = accountId ?? payAccounts(state.accounts)[0]?.id ?? state.accounts[0]?.id;
          if (!from) { done(null); return; }
          const loan = await saveDraftLoan(v, from, target);
          track('loan_added');
          await reloadAndPush();
          done(loan.id);
        }} />
    )}>
    <ProContext.Provider value={openPro}>
    <Shell app>
      <ScreenMark name={openSheet ? `sheet:${openSheet}`
        : tab === 'today' ? 'home' : tab === 'tx' ? 'ops' : tab === 'goals' ? (goalScreen ? 'goals:goal' : 'goals')
          : tab === 'accounts' ? `money:${moneyScreen === null ? 'overview' : typeof moneyScreen === 'string' ? moneyScreen : 'account' in moneyScreen ? 'account' : 'loan'}`
            : `analytics:${analyticsView()}`} />
      {tab === 'today' && (
        <Today
          state={state} result={result} plan={plan}
          onMenu={() => setSheet('settings')}
          pro={aiAccess.tier === 'pro'}
          {...(!aiAccess.offline ? { onAskHead: openAsk } : {})}
          onAddGoal={() => setSheet('goal')}
          onFocus={(gap) => setSheet({ remedy: gap })}
          onWhy={() => setSheet('why')}
          onCheck={() => setSheet('check')}
          onDistribute={(id) => { setDistributeKey(newRequestId()); setSheet({ distribute: id }); }}
          onFund={(id) => setSheet({ fund: id })}
          onSettle={(x) => setSheet({ settle: x })}
          onEditCard={(id) => setSheet({ editCard: id })}
          onPlanIncome={(t) => setSheet({ planIncome: t })}
          onFulfil={(t) => setSheet({ fulfil: t })}
          onAllocate={() => setSheet('allocate')}
          onMode={() => setSheet('mode')}
          onStable={() => setSheet('stable')}
          onAdd={() => setSheet('expense')}
          onSpend={(focus) => setSheet({ spend: focus })}
          onRest={() => setSheet('rest')}
          onShort={(c) => setSheet({ short: c })}
          onEditObligation={(id) => setSheet({ editObligation: id })}
          onDecide={async (obligationId, date, decision, required) => {
            await decideInstance({
              obligationId, scheduledDate: date, decision, requiredAt: required,
            });
            await reloadAndPush();
          }}
          onRelease={async (id) => { await deleteReserve(id); await reloadAndPush(); }}
          onArrived={async (id) => { await receiveExpectedIncome(id); await reloadAndPush(); }}
          onCloseArrival={async (id) => { await closeExpectation(id); await reloadAndPush(); }}
          onDropArrival={async (id) => { await cancelExpectedIncome(id); await reloadAndPush(); }}
          onCancelPlan={async (id) => { await cancelAllocation(id); await reloadAndPush(); }}
          onEditGoal={(id) => { setTab('goals'); setGoalScreen(id); }}
          onPart={(part) => setSheet({ part })}
          onUsual={() => setSheet('baseline')}
          onProfile={() => setSheet('profile')}
          bell={user !== null ? (inbox ? inbox.unread.notes + inbox.unread.chats : 0) : null}
          onBell={() => setSheet({ inbox: inbox && inbox.unread.chats > 0 && inbox.unread.notes === 0 ? 'chats' : 'notes' })}
          net={net} account={user !== null}
          guest={session === 'guest'}
          onFill={(what) => {
            const first = state.accounts.find((a) => a.isLiquid && !a.archived && a.kind !== 'credit_card');
            const card = state.accounts.find((a) => a.kind === 'credit_card' && !a.archived);
            if (what === 'balance' && first) setSheet({ editAccount: first.id });
            else if (what === 'card' && card) setSheet({ editCard: card.id });
            else if (what === 'income' || what === 'account' || what === 'obligation' || what === 'baseline') setSheet(what);
          }}
          onSignup={() => setSheet('auth')}
          onAsk={setAsk}
          hasTalk={talk.turns.length > 0}
          {...(lastReviewAt(talk) ? { lastReview: lastReviewAt(talk)! } : {})}
          onChanged={reloadAndPush}
          onInsight={(insight) => {
            // Наблюдение ведёт туда, где его решают. Разговор — только если
            // человек нажал именно на него: сами по себе поводы модель не
            // зовут и денег не тратят.
            const target = insight.action?.target ?? '';
            if (target.startsWith('ask:')) {
              // Разбор уже был, Pro нет — экран Pro с этим же вопросом.
              if (advisorGate(aiAccess).kind === 'offer') openPro('insight', { question: insight.text ? `${insight.text}. Что делать?` : target.slice(4) });
              else setAsk({ mode: 'ask', question: target.slice(4) });
            }
            else if (target.startsWith('goal:')) setSheet({ editGoal: target.slice(5) });
            else if (target.startsWith('card:')) setSheet({ editCard: target.slice(5) });
            else if (target === 'distribute') {
              const stream = state.incomeStreams.find((x) => x.isActive);
              if (stream) { setDistributeKey(newRequestId()); setSheet({ distribute: stream.id }); }
            // Назначить деньги на платёж — с того счёта, где они лежат.
            } else setTab('accounts');
          }}
        />
      )}
      {tab === 'tx' && <Transactions state={state} ask={askHead} onAdd={() => setSheet('expense')} onChanged={reloadAndPush}
        onAdjust={(id) => setSheet({ adjust: id, from: 'ops' })} />}
      {tab === 'stats' && (
        <TxEditLayer state={state} onChanged={reloadAndPush}>
          {(open, remove) => (
            <Analytics state={state} onMode={() => setSheet('mode')} onOperations={() => setTab('tx')}
              onAddIncome={(streamId) => setSheet({ recordIncome: streamId })} onChanged={reloadAndPush}
              onEditTx={open} onDeleteTx={remove} ask={askHead} />
          )}
        </TxEditLayer>
      )}
      {sheet === 'settings' && (() => {
        const more = (
        <More
          inSheet
          state={state}
          ask={askHead}
          onPick={openFrom('settings')}
          user={user}
          pro={aiAccess.tier === 'pro' || aiAccess.tier === 'trial'}
          onCategoriesChanged={() => void reloadAndPush()}
          onRerunOnboarding={async () => {
            if (confirm('Настроить картину заново? Текущие данные будут удалены.')) {
              await resetEverything(); setSkipOnboarding(false); await reload();
            }
          }}
          onReset={async () => {
            if (confirm('Удалить все данные? Это необратимо.')) { await resetEverything(); await reloadAndPush(); }
          }} />
        );
        // Шестерёнка в шапке: всё, что было в «Ещё», — листом «Настройки» (docs/design/goals-tab-2026-10-27).
        return <SheetWrap title="Настройки" className="settings" onClose={() => setSheet(null)}>{more}</SheetWrap>;
      })()}
      {tab === 'goals' && goalScreen && (
        <TxEditLayer state={state} onChanged={reloadAndPush}>
          {(open, remove) => (
            <GoalScreen state={state} goalId={goalScreen} needFloor={plan.needFloor}
              onBack={() => goalInspection ? returnToOperation() : setGoalScreen(null)}
              backLabel={goalInspection ? 'К выбору цели' : 'Цели'}
              onChanged={reloadAndPush}
              onFund={() => goalInspection ? returnToOperation(goalScreen) : setSheet({ fund: goalScreen })}
              onSettings={() => setSheet({ editGoal: goalScreen })}
              history={(txs, allocations) => <OpDays state={state} days={byDay(txs)} allocations={allocations} accountId={null} onOpen={open} onDelete={remove} />} />
          )}
        </TxEditLayer>
      )}
      {tab === 'goals' && !goalScreen && (
        <GoalsPage state={state} needFloor={plan.needFloor} ask={askHead}
          onAddGoal={(preset) => setSheet(preset ? { newGoal: preset } : 'goal')}
          onGoal={(id: string) => setGoalScreen(id)}
          onFund={(id: string) => setSheet({ fund: id })}
          onStable={() => setSheet('stable')} />
      )}
      {tab === 'accounts' && (() => {
        // «Деньги» вокруг вещей (27.10.2026): обзор, а из него — экран счёта, кредита, календарь.
        const back = () => setMoneyScreen(null);
        const sub = (title: string, body: ReactNode) => (
          <>
            <button className="obj-back" onClick={back}><Icon name="chevron-left" size={14} />Деньги</button>
            <h1 className="obj-title">{title}</h1>
            {body}
          </>
        );
        // «Доход и платежи» — те же списки, что были вкладками: там их настраивают.
        if (moneyScreen === 'plans') {
          return sub('Доход и платежи', (
            <div className="mn-plain">
              <IncomeSection state={state} plan={plan} onPick={(x) => setSheet(x)} />
              <PaymentsSection state={state} result={result} onPick={(x) => setSheet(x)} noDebts
                onPrepay={(id) => setSheet({ prepay: id })}
                onAddLoan={() => setSheet({ newLoan: newLoan('', 'amortizing') })}
                onEditLoan={(id) => setSheet({ editLoan: id })}
                onEditCard={(id) => setSheet({ editCard: id })} />
            </div>
          ));
        }
        if (moneyScreen === 'order') return <AccountOrder state={state} onDone={() => { setMoneyScreen(null); void reloadAndPush(); }} />;
        // «Что гасить первым» — прежний блок «Долги» с очередью и досрочным.
        if (moneyScreen === 'queue') {
          const owed = state.loans.filter((l) => l.kind !== 'credit_card' && l.principal > ZERO).length
            + state.accounts.filter((a) => a.kind === 'credit_card' && !a.archived && a.balance < ZERO).length;
          return sub(queueTitle(owed), (
            <Debts state={state} result={result} onPrepay={(id) => setSheet({ prepay: id })}
              onAdd={() => setSheet({ newLoan: newLoan('', 'amortizing') })}
              onEdit={(id) => setSheet({ editLoan: id })} onEditCard={(id) => setSheet({ editCard: id })} />
          ));
        }
        if (moneyScreen && typeof moneyScreen === 'object' && 'account' in moneyScreen) {
          const id = moneyScreen.account;
          return (
            <AccountScreen state={state} accountId={id} needFloor={plan.needFloor}
              onBack={back}
              onRecord={() => setSheet({ recordOn: { accountId: id, type: 'expense' } })}
              onTransfer={() => setSheet({ recordOn: { accountId: id, type: 'transfer' } })}
              onAdjust={() => setSheet({ adjust: id })}
              onSettings={() => setSheet({ editAccount: id })}
              onPayment={(x) => setSheet({ payment: x })}
              onGoal={(x) => { setTab('goals'); setGoalScreen(x); }}
              onAllOps={() => { sharedFilter.accountId = id; setTab('tx'); }}
              onChanged={() => void reloadAndPush()} />
          );
        }
        if (moneyScreen && typeof moneyScreen === 'object' && 'loan' in moneyScreen) {
          const id = moneyScreen.loan;
          const loan = state.loans.find((l) => l.id === id);
          return (
            <TxEditLayer state={state} onChanged={reloadAndPush}>
              {(open) => (
                <LoanScreen state={state} loanId={id} onBack={back}
                  onPay={() => setSheet(loan?.obligationId ? { payment: loan.obligationId } : { editLoan: id })}
                  onPrepay={() => setSheet({ prepay: id })}
                  onBalance={() => setSheet({ editLoan: id })}
                  onTerms={() => setSheet({ editLoan: id })}
                  onTx={open} />
              )}
            </TxEditLayer>
          );
        }
        return (
          <MoneyOverview state={state} result={result}
            onAccount={(id) => setMoneyScreen({ account: id })}
            onLoan={(id) => setMoneyScreen({ loan: id })}
            onCard={(id) => setSheet({ editCard: id })}
            onPlans={() => setMoneyScreen('plans')}
            onAddAccount={() => setSheet('account')}
            onAddLoan={() => setSheet({ newLoan: newLoan('', 'amortizing') })}
            onGoals={() => setTab('goals')}
            onOrder={() => setMoneyScreen('order')}
            onArchive={() => setArchiveOpen(true)}
            onQueue={() => setMoneyScreen('queue')}
            onCheck={() => {
              // Сверка — с основного счёта для трат; «Все остатки разом» — в том же листе.
              const id = state.accounts.find((a) => !a.archived && a.isLiquid && a.kind !== 'credit_card')?.id;
              if (id) setSheet({ adjust: id, from: 'ops' });
            }} />
        );
      })()}
      {archiveOpen && <AccountArchive state={state} onClose={() => setArchiveOpen(false)} onChanged={() => void reloadAndPush()} />}

      {/*
        Порядок — привычный (22.10.2026, docs/design/nav-2026-10-22): «Главная»
        слева, как в банковских приложениях, «Ещё» — справа. Перевёрнутый
        порядок под большой палец (15.10.2026) путал: самое частое действие и
        так на «+», который стоит над меню справа.
      */}
      {tab === 'today' && sheet === null && !state.isEmpty && tourOn && tourWanted(state.dismissedHints) && (
        <HomeTour onDone={() => setTourOn(false)} />
      )}
      <nav className="tabs tabs-5">
        <TabBtn active={tab === 'today'} onClick={() => navigateTab('today')} label="Главная" glyph="house" />
        {/* Тап по открытым «Деньгам» — к обзору: с экрана счёта или кредита назад одним нажатием. */}
        <TabBtn active={tab === 'accounts'} onClick={() => { navigateTab('accounts'); setMoneyScreen(null); }} label="Деньги" glyph="wallet" />
        <TabBtn active={tab === 'tx'} onClick={() => navigateTab('tx')} label="Операции" glyph="list-ul" />
        <TabBtn active={tab === 'stats'} onClick={() => navigateTab('stats')} label="Аналитика" glyph="chart-pie" />
        {/* «Цели» — на место «Ещё» (27.10.2026): остальные пункты не двигаются, «Ещё» — шестерёнкой в шапке. */}
        <TabBtn active={tab === 'goals'} onClick={() => { navigateTab('goals'); setGoalScreen(null); }} label="Цели" glyph="bullseye-arrow" />
      </nav>
      <button className="fab" onClick={() => setSheet('expense')} aria-label="Записать операцию"><Icon name="plus" size={22} solid /><span className="fab-label">Записать</span></button>
      {/* Фирменный градиент искорок: днём темнее — на белом стекле салатовый не читается. */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <linearGradient id="skoka-ai" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#A7F432" /><stop offset="1" stopColor="#34D099" /></linearGradient>
          <linearGradient id="skoka-ai-day" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#4E8A12" /><stop offset="1" stopColor="#0E8055" /></linearGradient>
          {/* Кольцо цели днём — светлее искорок: тонкая дуга тёмным градиентом читалась почти чёрной. */}
          <linearGradient id="skoka-goal" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#8BD62A" /><stop offset="1" stopColor="#22B07D" /></linearGradient>
        </defs>
      </svg>

      {(sheet === 'expense' || (typeof sheet === 'object' && sheet !== null && ('expensePhrase' in sheet || 'recordIncome' in sheet || 'recordOn' in sheet || 'fund' in sheet))) && (
        <AddOperation state={state} draft={operationDraft}
          {...(typeof sheet === 'object' && sheet !== null && 'fund' in sheet ? { startGoal: sheet.fund } : {})}
          onViewGoal={(id, draft) => {
            setGoalInspection({ draft, sheet, tab, goalScreen, moneyScreen });
            setSheet(null); setTab('goals'); setGoalScreen(id);
          }} {...(typeof sheet === 'object' && sheet !== null && 'recordIncome' in sheet ? { startIncome: sheet.recordIncome } : {})}
          {...(typeof sheet === 'object' && sheet !== null && 'recordOn' in sheet ? { startOn: sheet.recordOn } : {})}
          openPhrase={typeof sheet === 'object' && sheet !== null && 'expensePhrase' in sheet} onClose={() => { setOperationDraft(undefined); setSheet(null); setBuy(null); void reloadAndPush(); }}
          onPlans={(entry) => openPro(entry ?? 'general')}
          {...(buy ? { prefill: buy } : {})}
          onSaved={async (saved) => {
            setOperationDraft(undefined); setBuy(null);
            await reloadAndPush();
            // Пришли внешние деньги, а режим включён: показываем раскладку
            // прихода — сколько в запас, сколько на платежи, сколько остаётся
            // решить. Сам в буфер не уходит ни рубль.
            setSheet(saved?.income && stableSnapshot(state)
              ? { arrival: saved.income }
              : null);
          }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'distribute' in sheet && (
        <DistributeIncome state={state} incomeStreamId={sheet.distribute}
          onClose={() => setSheet(null)}
          onDiscuss={(q) => {
            if (advisorGate(aiAccess).kind === 'offer') { openPro('insight', { question: q }); return; }
            setSheet(null); setAsk({ mode: 'ask', question: q });
          }}
          onApply={async (planned) => {
            const streamId = typeof sheet === 'object' && sheet !== null && 'distribute' in sheet
              ? sheet.distribute : '';
            await applyDistribution({
              accountId: planned.accountId,
              amount: planned.amount,
              date: planned.date,
              source: planned.source,
              reserves: planned.reserves,
              goals: planned.goals,
              // Связь с ожидаемым доходом: без неё пришедшая зарплата и та же
              // зарплата в прогнозе живут порознь, и деньги считаются дважды.
              incomeStreamId: streamId,
              // Ключ живёт столько же, сколько открытый экран: повтор после
              // ошибки — то же действие, а новая раскладка открывается заново.
              requestId: distributeKey,
            });
            const waiting = await allocationsToExecute(streamId, planned.date);
            await reloadAndPush();
            // Факт сохранён, картина пересчитана — и только теперь предлагаем
            // исполнить назначенное. Тихо закреплять деньги нельзя.
            setSheet(waiting.length > 0
              ? { fulfil: {
                incomeStreamId: streamId,
                date: planned.date,
                accountId: planned.accountId,
                arrived: planned.amount,
              } }
              : null);
          }} />
      )}
      {ask && (
        <AskModal
          state={state} talk={talk} setTalk={setTalk} intent={ask}
          onClose={() => setAsk(null)}
          onDocs={() => { setDocsBack(null); setSheet('documents'); }}
          onClear={async () => { await clearTalk(); setTalkState(emptyTalk()); }}
          // Окно остаётся открытым: в карточке должно быть видно «Применено».
          onApplied={async () => { await reloadAndPush(); }}
          onPlans={() => openPro('ask')}
          onSignup={() => { setAsk(null); navigate('signup'); }}
          onEdit={(p) => {
            setAsk(null);
            // «Изменить» ведёт туда, где это правится руками: предложение —
            // повод открыть форму, а не приказ её заполнить.
            if (p.kind === 'goal_change') setSheet({ editGoal: p.goalId });
            else {
              const buyIt = purchasePrefill(p);
              if (buyIt) {
                setBuy({
                  amount: buyIt.amount, date: buyIt.date ?? todayPd(),
                  ...(buyIt.accountId ? { accountId: buyIt.accountId } : {}),
                });
                setSheet('expense');
              }
            }
          }} />
      )}
      {(sheet === 'guides' || sheet === 'faq') && <HelpSheet kind={sheet} onClose={() => setSheet(null)} />}
      {typeof sheet === 'object' && sheet !== null && 'inbox' in sheet && (
        <InboxSheet inbox={inbox} start={sheet.inbox} signedIn={user !== null}
          onClose={() => { setSheet(null); refreshInbox(); }}
          onChanged={refreshInbox}
          onAuth={() => setSheet('auth')}
          onAction={(a) => {
            if (a === 'pro') { setSheet(null); openPro('general'); } else setSheet(a);
          }} />
      )}
      {sheet === 'profile' && (
        <ProfileSheet state={state} user={user} syncing={syncing} syncNote={syncNote} onSync={pullState}
          onClose={() => setSheet(null)}
          onAuth={() => setSheet('auth')}
          onLogout={() => { setSheet(null); void logout(); }}
          onDocs={() => { setDocsBack('profile'); setSheet('documents'); }}
          onDelete={() => setSheet('deleteAccount')}
          onSaved={async () => { await reloadAndPush(); }}
          mail={<MailSettings state={state} user={user} onSaved={async () => { await reloadAndPush(); }} />}>
          <Subscription onManage={() => setSheet('subscription')} />
        </ProfileSheet>
      )}
      {sheet === 'documents' && (
        <Documents state={state} onClose={() => setSheet(docsBack)}
          onSaved={async () => { await reloadAndPush(); }} />
      )}
      {sheet === 'deleteAccount' && user && (
        <DeleteAccount user={user} onClose={() => setSheet('profile')}
          onDone={async () => {
            setSheet(null);
            setUser(null);
            setSession('guest');
            // Данных больше нет ни на сервере, ни здесь: приложение начинается
            // заново, а не показывает пустую главную поверх прежней сессии.
            await reload();
            navigate('welcome');
          }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'planIncome' in sheet && (
        <PlanFromIncome state={state} target={sheet.planIncome}
          onClose={() => setSheet(null)}
          onSaved={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'fulfil' in sheet && (
        <FulfilAllocations state={state} target={sheet.fulfil}
          onClose={() => setSheet(null)}
          onDone={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {merge !== null && (
        <MergeChoice
          local={state} remote={merge}
          onClose={() => setMerge(null)}
          onKeepRemote={async () => { setMerge(null); await pullState('remote'); }}
          onKeepLocal={async () => { setMerge(null); await pullState('local'); }} />
      )}
      {sheet === 'check' && (
        <CheckPurchase state={state} onClose={() => setSheet(null)}
          onBuy={(amount, date) => { setBuy({ amount, date }); setSheet('expense'); }} />
      )}
      {sheet === 'receipt_doc' && (
        <ReceiptDoc state={state} onClose={() => { setSheet(null); void reloadAndPush(); }} onManual={() => setSheet('expense')}
          onPlans={() => openPro('file')}
          onSaved={() => { void reloadAndPush(); }} />
      )}
      {sheet === 'receipt' && (
        <ScanReceipt state={state} onClose={() => { setSheet(null); void reloadAndPush(); }}
          onSaved={async () => { setSheet(null); await reloadAndPush(); }} onFile={() => setSheet('receipt_doc')} />
      )}
      {sheet === 'account' && (
        <AddAccount onClose={() => setSheet(null)} onSaved={async () => { track('account_added'); setSheet(null); await reloadAndPush(); }} />
      )}
      {sheet === 'income' && (
        <EditIncome state={state} onClose={() => setSheet(null)} onSaved={async () => { track('income_added'); setSheet(null); await reloadAndPush(); }} />
      )}
      {(sheet === 'obligation' || sheet === 'subscriptionNew') && (
        <AddObligation state={state} onClose={() => setSheet(null)} subscription={sheet === 'subscriptionNew'}
          onLoan={(init) => setSheet({ newLoan: init })}
          onSaved={async () => { track('obligation_added'); setSheet(null); await reloadAndPush(); }} />
      )}
      {sheet === 'baseline' && (
        <SetBaseline state={state} onClose={() => setSheet(null)} onSaved={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {sheet === 'auth' && (
        <Auth onClose={() => setSheet(null)} onLater={() => setSheet(null)}
          onDocs={() => setAuthDocs(true)}
          onDone={async (u) => { setUser(u); setSheet(null); await pullState(); }} />
      )}
      {/* Документ из согласия — поверх формы: отметки и почта не сбрасываются. */}
      {sheet === 'auth' && authDocs && (
        <Documents state={state} onClose={() => setAuthDocs(false)}
          onSaved={async () => { await reloadAndPush(); }} />
      )}
      {sheet === 'conflict' && conflict && (
        <ConflictSheet conflict={conflict} local={state}
          onClose={() => { setSheet(null); setConflict(null); }}
          onTakeRemote={async () => {
            setSheet(null); setConflict(null); await pullState();
          }}
          onForceLocal={async () => {
            const s = await loadState();
            const res = await push(toPayload(s), conflict.version, true);
            if (!('conflict' in res)) {
              await setSyncVersion(res.version);
              setSyncNote(`Отправлено поверх · версия ${res.version}`);
            }
            setSheet(null); setConflict(null); await reload();
          }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'settle' in sheet && (
        <SettleObligation state={state} target={sheet.settle} onClose={() => setSheet(null)}
          onSaved={async () => { track('payment_settled', { via: 'sheet' }); setSheet(null); await reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'editIncome' in sheet && (
        <EditIncome state={state} id={sheet.editIncome} onClose={() => setSheet(null)}
          onSaved={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'payment' in sheet && (
        <PaymentSheet state={state} obligationId={sheet.payment} onClose={() => setSheet(null)}
          onPay={(t) => setSheet({ settle: t })} onEdit={() => setSheet({ editObligation: sheet.payment })}
          onChanged={() => { void reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'editObligation' in sheet && (
        <EditObligation state={state} id={sheet.editObligation} onClose={() => setSheet(null)}
          onSaved={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'editGoal' in sheet && (
        <EditGoal state={state} id={sheet.editGoal} onClose={() => setSheet(null)}
          onSaved={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'remedy' in sheet && (
        <RemedySheet state={state} gap={sheet.remedy} available={result.available}
          {...(sheet.from ? {
            exclude: sheet.from.instanceId.startsWith('card:')
              ? { kind: 'credit' as const, id: sheet.from.instanceId.slice(5) }
              : { kind: 'obligation' as const, id: sheet.from.instanceId.split(':')[0] ?? '' },
            onPlanIncome: () => setSheet({ planIncome: planTargetOf(sheet.from!) }),
          } : {})}
          daysLeft={result.daysToHorizon} needBy={result.horizonDate}
          holds={result.deductions.filter((d) => d.kind === 'goal')}
          onClose={() => setSheet(null)}
          onDone={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {sheet === 'mode' && (
        <ModeSheet state={state} onClose={() => setSheet(null)}
          {...(state.goals.find((g) => g.status === 'active')
            ? {
              onRaiseGoal: () => setSheet({
                editGoal: state.goals.find((g) => g.status === 'active')!.id,
              }),
            }
            : {})}
          onSaved={async () => {
            // Режим — как делить свободное между тратами и целями в плане.
            // Раскладки больше нет: сохранили и закрыли.
            setSheet(null);
            await reloadAndPush();
          }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'spend' in sheet && (
        <SpendSheet state={state} result={result} focus={sheet.spend} onClose={() => setSheet(null)} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'short' in sheet && (
        <ShortfallSheet coverage={sheet.short}
          onClose={() => setSheet(null)}
          onRemedy={(gap) => setSheet({ remedy: gap, from: sheet.short })}
          /* У кредитки «Уже внесено» — в её настройке; будущий приход назначается на её платёж (03.11.2026). */
          {...(sheet.short.instanceId.startsWith('card:') ? { onCard: () => setSheet({ editCard: sheet.short.instanceId.slice(5) }) } : {})}
          onPlanIncome={() => setSheet({ planIncome: planTargetOf(sheet.short) })}
          onSettle={() => setSheet({ settle: {
            id: sheet.short.instanceId.split(':')[0] ?? '',
            due: sheet.short.dueDate,
            amount: sheet.short.amount,
          } })} />
      )}
      {sheet === 'rest' && (
        <DistributeRest state={state} onClose={() => setSheet(null)}
          onDone={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {sheet === 'allocate' && (
        <AllocateSheet state={state} onClose={() => setSheet(null)}
          onMode={() => setSheet('mode')}
          onDone={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'arrival' in sheet && (
        <ArrivalSheet state={state} amount={sheet.arrival}
          onClose={() => setSheet(null)}
          onDone={async () => { await reloadAndPush(); setSheet('allocate'); }} />
      )}
      {sheet === 'stable' && (
        <StableSheet state={state} onClose={() => setSheet(null)}
          onSaved={async () => { await reloadAndPush(); }}
          onAllocate={() => setSheet('allocate')} />
      )}
      {sheet === 'why' && (
        <WhySheet state={state} onClose={() => setSheet(null)}
          onGoals={(id) => setSheet({ editGoal: id })} />
      )}
      {(sheet === 'goal' || (typeof sheet === 'object' && sheet !== null && 'newGoal' in sheet)) && (
        <AddGoal state={state} {...(typeof sheet === 'object' && sheet !== null && 'newGoal' in sheet ? { preset: sheet.newGoal } : {})} onClose={() => setSheet(null)}
          onSaved={async () => { track('goal_added'); setSheet(null); await reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'newLoan' in sheet && (
        <DebtSheet init={sheet.newLoan} isNew today={state.today}
          accounts={payAccounts(state.accounts)}
          // С этими платежами можно связать новый долг: ими не владеет другой.
          payments={state.obligations.filter((o) => o.isActive && !o.prepay
            && !state.loans.some((l) => l.obligationId === o.id))}
          onClose={() => setSheet(null)}
          onSave={async (v, { accountId, target }) => {
            // Платёж — с выбранного счёта; без счетов для трат — с первого.
            const from = accountId ?? payAccounts(state.accounts)[0]?.id ?? state.accounts[0]?.id;
            if (!from) return;
            await saveDraftLoan(v, from, target);
            track('loan_added');
            setSheet(null);
            await reloadAndPush();
          }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'editLoan' in sheet && (() => {
        const loan = state.loans.find((l) => l.id === sheet.editLoan);
        if (!loan) return null;
        const ob = state.obligations.find((o) => o.id === loan.obligationId);
        const isCard = loan.kind === 'credit_card';
        return (
          <DebtSheet key={loan.id} init={loanToDraft(loan, state.obligations, state.today)} isNew={false}
            title={loan.name} today={state.today} accounts={payAccounts(state.accounts)}
            {...(ob?.accountId ?? loan.accountId ? { accountId: (ob?.accountId ?? loan.accountId)! } : {})}
            {...(ob ? { linkedTitle: ob.name } : {})}
            {...(isCard ? { card: { debt: loan.principal } } : {})}
            onClose={() => setSheet(null)}
            onSave={async (v, { accountId }) => {
              await saveLoanEdit(loan, v, accountId);
              setSheet(null);
              await reloadAndPush();
            }}
            // Карта уходит вместе со счётом — «Убрать счёт» в её карточке.
            {...(isCard ? {} : {
              onDelete: async () => {
                if (!confirm(`Удалить «${loan.name}»? ${ob ? 'Его платёж уйдёт из «Платежей». ' : ''}`
                  + 'Прошлые операции останутся.')) return;
                await deleteLoan(loan.id);
                setSheet(null);
                await reloadAndPush();
              },
            })} />
        );
      })()}
      {typeof sheet === 'object' && sheet !== null && 'prepay' in sheet && (
        <PrepaySheet state={state} loanId={sheet.prepay} onClose={() => setSheet(null)}
          onSaved={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'envelope' in sheet && (
        <Envelopes state={state} accountId={sheet.envelope}
          horizon={result.horizon.basis === 'confirmed' ? result.horizonDate : null}
          onClose={() => setSheet(null)}
          onSaved={async () => { await reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'part' in sheet && (() => {
        /*
          Таблицу месячного плана этот экран больше не строит: он разбирает
          принятую раскладку. Месяц отвечает за итоги и живёт в аналитике.
        */
        return (
          <PartSheet
            part={sheet.part} state={state}
            planned={sheet.part === 'need' ? state.needBaseline : state.wantBaseline}
            spent={monthReport(state.transactions.filter((t) => t.link?.kind !== 'obligation' && t.link?.kind !== 'goal'), monthOf(state.today))}
            onSaved={async () => { setSheet(null); await reloadAndPush(); }}
            onClose={() => setSheet(null)} />
        );
      })()}
      {typeof sheet === 'object' && sheet !== null && 'editCard' in sheet && (
        <EditCard state={state} result={result} accountId={sheet.editCard}
          onAdjust={(id) => setSheet({ adjust: id })}
          onClose={() => setSheet(null)}
          onSaved={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'editAccount' in sheet && (
        <EditAccount state={state} accountId={sheet.editAccount}
          onClose={() => setSheet(null)}
          onSaved={async () => { setSheet(null); await reloadAndPush(); }}
          onChanged={() => void reloadAndPush()}
          onEnvelopes={(id) => setSheet({ envelope: id })}
          onAdjust={(id) => setSheet({ adjust: id })} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'adjust' in sheet && (() => {
        const id = sheet.adjust;
        const from = sheet.from;
        // Из «Операций» — обратно в «Операции»; из карточки счёта — в неё.
        const back = () => setSheet(from === 'ops' ? null
          : state.accounts.find((a) => a.id === id)?.kind === 'credit_card' ? { editCard: id } : { editAccount: id });
        return <>
          <AdjustSheet key={id} state={state} accountId={id} onClose={back}
            onDone={async () => { track('balance_adjusted'); await reloadAndPush(); back(); }}
            onPick={() => setAdjustPick(true)}
            shots={{
              locked: !featureView('receipt_import', aiAccess).allowed,
              onLocked: () => { setSheet(null); openPro('balances'); },
              onFiles: (files) => setSheet({ balanceShots: files }),
            }} />
          {adjustPick && (
            <AccountPick state={state} value={id} title="Какой счёт" all={false} action="Выбрать"
              context="Давно не сверенные — жёлтым."
              detail={(a) => checkedText(state, a.id)}
              onClose={() => setAdjustPick(false)}
              onApply={(next) => { setAdjustPick(false); if (next) setSheet({ adjust: next, ...(from ? { from } : {}) }); }} />
          )}
        </>;
      })()}
      {typeof sheet === 'object' && sheet !== null && 'balanceShots' in sheet && (
        <BalanceShotsSheet state={state} files={sheet.balanceShots} onClose={() => setSheet(null)}
          onDone={async () => { track('balance_adjusted', { via: 'shots' }); setSheet(null); await reloadAndPush(); }} />
      )}
      {typeof sheet === 'object' && sheet !== null && 'reserve' in sheet && (
        <SetReserve
          state={state} accountId={sheet.reserve}
          onClose={() => setSheet(null)}
          onSaved={async () => { setSheet(null); await reloadAndPush(); }} />
      )}
      {sheet === 'menu' && (
        <Menu
          onClose={() => setSheet(null)}
          onPick={openFrom('menu')}
        />
      )}
      {pro && createPortal(
        <ProScreen entry={pro.entry} access={aiAccess} billing={aiAccess.billing} busy={proBusy} error={proError}
          {...(pro.question ? { question: pro.question } : {})}
          mine={manualLine(state)}
          onClose={() => {
            setPro(null);
            // Отказ от покупки — один раз лист «Неделя — бесплатно». Пока
            // оплаты нет, пробный предлагает сам экран Pro, и второй раз не нужен.
            if (aiAccess.billing && aiAccess.trial.available && !aiAccess.trial.offerSeen) {
              setTrialOffer(true);
              void trialOfferSeen().catch(() => {});
            }
          }}
          onTrial={() => void runTrial()}
          onBuy={(period) => void buyPro(period, false)}
          onPlans={() => { setPro(null); setPlans(12); }}
          onTerms={() => { window.open(LEGAL_URL.offer, '_blank', 'noopener'); }} />,
        document.body,
      )}
      {plans && createPortal(
        <ProPlans access={aiAccess} busy={proBusy} error={proError} initial={plans}
          onClose={() => { setPlans(false); setProError(null); }}
          onBuy={(m, renew) => void buyPro(m, renew)}
          onTerms={() => { window.open(LEGAL_URL.offer, '_blank', 'noopener'); }} />,
        document.body,
      )}
      {pay && pay.state === 'succeeded' && createPortal(
        <ProPaid until={pay.until} next={NEXT_TITLE[payIntent()?.entry ?? 'general']} offer={proOffer}
          onDone={async (addToPayments) => {
            const intent = payIntent(); clearPayIntent(); setPay(null);
            if (addToPayments && proOffer) {
              const from = state?.accounts.find((a) => !a.archived && a.isLiquid && a.kind !== 'credit_card')?.id ?? '';
              await addObligation({
                name: 'SKOKA Pro', kind: 'subscription', accountId: from, amount: proOffer.amount,
                dayOfMonth: Number(proOffer.next.slice(8, 10)),
                // Расписание — с даты следующего списания: оплаченный сегодня период платежом не считается.
                schedule: {
                  kind: 'every', n: proOffer.months === 12 ? 1 : proOffer.months, unit: proOffer.months === 12 ? 'year' : 'month',
                  anchor: proOffer.next, shiftRule: 'exact', from: proOffer.next,
                },
              });
              flashMessage('SKOKA Pro — в платежах');
              await reloadAndPush();
            }
            setProOffer(null);
            if (intent) resume(intent);
          }} />,
        document.body,
      )}
      {pay && pay.state !== 'succeeded' && createPortal(
        <ProPayWait state={pay.state} reason={pay.reason} onClose={() => setPay(null)}
          onRetry={() => { setPay(null); setPlans(payIntent()?.months ?? 12); }} />,
        document.body,
      )}
      {sheet === 'subscription' && (
        <SubscriptionSheet onClose={() => setSheet(null)} onPay={() => { setSheet(null); setPlans(12); }} />
      )}
      {trialOffer && createPortal(
        <TrialOffer days={aiAccess.trial.days} busy={proBusy}
          onTrial={() => void runTrial()} onNo={() => setTrialOffer(false)} />,
        document.body,
      )}
      {!pro && trialEnd && createPortal(
        <TrialEnded billing={aiAccess.billing} earlyYear={aiAccess.earlyYear}
          stats={{ voice: trialEnd.phrase, receipts: trialEnd.receipt, answers: trialEnd.analysis }}
          onBuy={() => { seeTrialEnd(); openPro('general'); }}
          onFree={seeTrialEnd} />,
        document.body,
      )}
    </Shell>
    </ProContext.Provider>
    </CreateDebtContext.Provider>
    </CreateGoalContext.Provider>
    </CreateAccountContext.Provider>
  );
}

/**
 * Его собственная цифра для экрана Pro: сколько операций в этом месяце
 * вписано руками и сколько это времени. ≈ 27 секунд на ручную запись против
 * ≈ 4 голосом — по замерам «op_saved». Меньше пяти — не говорим ничего.
 */
const PAY_INTENT = 'skoka.payIntent';
type PayIntent = { entry: ProEntry; question?: string; months?: 1 | 3 | 6 | 12; renew?: boolean };
const payIntent = (): PayIntent | null => {
  try { return JSON.parse(sessionStorage.getItem(PAY_INTENT) ?? 'null') as PayIntent | null; } catch { return null; }
};
const clearPayIntent = () => { try { sessionStorage.removeItem(PAY_INTENT); } catch { /* приватный режим */ } };
/** Кнопка после оплаты — то, ради чего человек пришёл. */
const NEXT_TITLE: Record<ProEntry, string> = {
  voice: 'Записать голосом', scan: 'Снять чек', file: 'Загрузить файл', balances: 'Обновить остатки', ask: 'Спросить SKOKA',
  insight: 'Спросить SKOKA', analytics: 'Открыть Аналитику', review: 'Открыть разбор периода', general: 'Продолжить',
};

/** С какого примера начать экран Pro: что человек хотел сделать. */
const entryOf = (view: FeatureView): ProEntry =>
  view.feature === 'receipt_photo' ? 'scan' : view.feature === 'receipt_import' ? 'file' : 'voice';

/** Куда назначить будущий приход по листу нехватки: наступление платежа или кредитка. */
function planTargetOf(c: Coverage): PlanTarget {
  const card = c.instanceId.startsWith('card:') ? c.instanceId.slice(5) : null;
  return {
    instanceId: c.instanceId,
    obligationId: card ? '' : c.instanceId.split(':')[0] ?? '',
    ...(card ? { cardId: card } : {}),
    label: c.label,
    dueDate: c.dueDate,
    need: c.gap,
  };
}

function manualLine(state: AppState): string | null {
  const month = state.today.slice(0, 7);
  const n = state.transactions.filter((t) => t.source === 'manual' && !t.link
    && (t.type === 'expense' || t.type === 'income') && t.occurredDate.startsWith(month)).length;
  if (n < 5) return null;
  const names = ['январе', 'феврале', 'марте', 'апреле', 'мае', 'июне', 'июле', 'августе', 'сентябре', 'октябре', 'ноябре', 'декабре'];
  const ops = plural(n, 'операцию', 'операции', 'операций');
  return `В ${names[Number(month.slice(5, 7)) - 1]} ты записал вручную ${ops} — это ≈ ${Math.max(1, Math.round((n * 27) / 60))} мин. Голосом было бы ≈ ${Math.max(1, Math.round((n * 4) / 60))}.`;
}

/** `app` — экраны с меню: на компьютере меню встаёт панелью слева. */
const Shell = ({ children, app }: { children: React.ReactNode; app?: boolean }) => (
  <div className={app ? 'phone app' : 'phone'}><div className="bgart" /><main>{children}</main></div>
);

/*
  Вкладка нижнего меню — по макету Figma «SKOKA / Нижнее меню»: сплошная
  иконка 20 px, подпись 9,5 px, у выбранной — салатовая подложка. «Ещё» —
  три точки из макета; они рисуются маской, чтобы цвет шёл от подписи.
*/
const TabBtn = (p: { active: boolean; onClick: () => void; label: string; glyph: string }) => (
  <button className={`tab${p.active ? ' on' : ''}`} onClick={p.onClick} aria-current={p.active ? 'page' : undefined}>
    {p.glyph === 'ellipsis'
      ? <span className="tab-more" aria-hidden="true" style={{ WebkitMaskImage: `url("${moreDots}")`, maskImage: `url("${moreDots}")` }} />
      : <Icon name={p.glyph} size={20} solid />}
    <span className="tab-label">{p.label}</span>
  </button>
);

/** «Спроси SKOKA» в шапке экрана — стеклянный кружок рядом с профилем. */
function AskHeadButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="netbtn askbtn" onClick={onClick} aria-label="Спроси SKOKA">
      <Icon name="messages-question" size={18} /></button>
  );
}

// ─────────────────────────────────────────────────────────── главная

function Today(props: {
  state: AppState; result: AvailableResult; plan: PlanResult;
  onMenu: () => void; onAddGoal: () => void;
  /** «Спроси SKOKA» в шапке; нет — кнопки нет (нет связи с сервером). */
  onAskHead?: () => void;
  /** Подключён Pro — у логотипа метка «Pro». */
  pro?: boolean;
  /** Открыть варианты решения. Передаём нехватку: её знает задача. */
  onFocus: (gap: Money) => void;
  onFund: (goalId: string) => void;
  /** «Почему это главное»: порядок задач словами и важности целей. */
  onWhy: () => void;
  /** Проверка покупки: главный вопрос продукта, считает ядро. */
  onCheck: () => void;
  /** Распределение пришедших денег по платежам, жизни и целям. */
  onDistribute: (incomeStreamId: string) => void;
  onSettle: (x: { id: string; due: string; amount: Money }) => void;
  /** Взнос по карте отмечается там же, где заданы её условия. */
  onEditCard: (accountId: string) => void;
  /** Назначить будущий приход на платёж, которому не хватает денег. */
  onPlanIncome: (t: PlanTarget) => void;
  /** Приход случился: предложить исполнить назначенное. */
  onFulfil: (t: FulfilTarget) => void;
  /** «Больше по этому приходу не будет»: закрыть остаток ожидания. */
  onCloseArrival: (id: string) => void;
  /** Разложить деньги: открыть предложение новой версии раскладки. */
  onAllocate: () => void;
  /** Выбрать, как делится свободный остаток. */
  onMode: () => void;
  /** Стабильный бюджет: включение или управление выплатами себе. */
  onStable: () => void;
  /** Записать операцию: главное ежедневное действие. */
  onAdd: () => void;
  /** «Как посчитано»: с какого числа главной открыли. */
  onSpend: (focus: SpendFocus) => void;
  /** Распределить нераспределённый остаток. */
  onRest: () => void;
  /** Нехватка на конкретный платёж: арифметика и варианты. */
  onShort: (c: Coverage) => void;
  /** Открыть редактор платежа: расписание правится там же, где живёт. */
  onEditObligation: (id: string) => void;
  /** Решение по наступлению: долг подтверждён или платежа не было. */
  onDecide: (
    obligationId: string, date: PlainDate, decision: 'unpaid' | 'not_applicable', required: Money,
  ) => void;
  /** Снять конверт, оставшийся под уже оплаченный платёж. */
  onRelease: (reserveId: string) => void;
  /** Разовый приход состоялся: записать деньги и закрыть ожидание. */
  onArrived: (id: string) => void;
  /** Разовый приход не состоялся: ожидание снимается, история остаётся. */
  onDropArrival: (id: string) => void;
  /** Отменить назначение будущих денег. */
  onCancelPlan: (id: string) => void;
  /** Открыть цель — её экран во вкладке «Цели» (27.10.2026); правка — там, «Настроить». */
  onEditGoal: (id: string) => void;
  /** Открыть разбор части плана: откуда взялось число и что с ним делать. */
  onPart: (part: 'need' | 'want') => void;
  /** Назвать обычные траты в месяц — пока они неизвестны, без них ничего не оценить. */
  onUsual: () => void;
  /** Аккаунта нет: данные только на этом телефоне — об этом строка на главной. */
  guest: boolean;
  /** Вписать то, чего не хватает: доход, остаток, платежи, обычные траты. */
  onFill: (what: MissingOpen) => void;
  onSignup: () => void;
  /** Профиль: аккаунт, подписка, документы, выход. */
  onProfile: () => void;
  /** Колокольчик в капсуле: сколько нового; `null` — без аккаунта колокольчика нет. */
  bell: number | null;
  onBell: () => void;
  /** Связь с сервером — значок в шапке. */
  net: NetState;
  /** Вошёл в аккаунт: без него записи никуда не отправляются. */
  account: boolean;
  /** Открыть советника: разбор картины либо вопрос. */
  onAsk: (intent: AskIntent) => void;
  /** Время последнего разбора, ISO. Пусто — разборов ещё не было. */
  lastReview?: string;
  /** Разговор уже начат: к нему можно вернуться без нового разбора. */
  hasTalk: boolean;
  /** Действие по наблюдению: куда вести, решает главная. */
  onInsight: (insight: InsightCandidate) => void;
  /** Журнал наблюдений изменился: картину надо перечитать. */
  onChanged: () => void;
}) {
  const { state, result, plan } = props;
  const p = pace({
    available: result.available, daysLeft: result.daysToHorizon,
    needBaseline: state.needBaseline, wantBaseline: state.wantBaseline,
  });
  /*
    Обеспечены ли платежи до следующего прихода.

    «Доступно» уже вычитает их из свободных денег, но человек этого не
    видит: он смотрит на цифру и отдельно держит в голове ипотеку двадцатого.
    Строка отвечает на его тревогу прямо — и говорит, сколько не хватает,
    когда денег правда мало.
  */
  /*
    «Пришли деньги?» показывается рядом с датой прихода, а не всегда.

    Блок про раскладку висел на главной постоянно — и двадцать дней из
    тридцати спрашивал о том, чего не случилось. Вопрос уместен накануне и в
    первые дни после прихода: тогда деньги правда лежат нетронутыми и их есть
    смысл разложить.
  */
  /*
    Разовые приходы, которых ещё ждут.

    Список сознательно живёт рядом с «Пришли деньги?», а не в расчёте: это
    обещание, а не деньги. В «Доступно» оно не входит — и не должно,
    пока человек не подтвердит, что деньги на счёте.
  */
  const awaitedMoney = awaited(state.expectedIncomes)
    .filter((e) => expectationState(e, state.today) !== 'unresolved');
  const stale = unresolvedExpectations(state.expectedIncomes, state.today);
  /*
    Просрочка: наступления, о которых приложение не знает ответа.

    Тем же селектором, что и деньги, — иначе карточка и «Доступно»
    разойдутся. Показываем только уже наступившие: будущее живёт в
    «Ближайших платежах».
  */
  const overdue = unsettledInstances({
    obligations: state.obligations,
    reserves: state.reserves,
    today: state.today,
    ...(state.trackingStartedAt ? { trackingStartedAt: state.trackingStartedAt } : {}),
  }).filter((i) => !isAfter(i.scheduledDate, state.today) && i.unpaid > ZERO);
  /** Наступление, по которому человек открыл лист решений. */
  const [decide, setDecide] = useState<PaymentInstance | null>(null);
  const incomeNear = state.incomeStreams.some((x) => {
    // По ритму, а не по числу месяца: у недельного дохода «накануне» бывает
    // каждую неделю, а у сдельщика не бывает вовсе.
    const next = nextForStream(x, state.today);
    const last = previousOccurrence(x, state.today);
    return (next !== null && diffDays(state.today, next) <= 1)
      || (last !== null && diffDays(last, state.today) <= 3);
  });
  /*
    Доступ спрашиваем один раз на экран: от него зависят и кнопка «Спроси
    SKOKA» под главной цифрой, и вид секции разбора ниже. Пока ответа нет,
    считаем, что платного входа нет: показать кнопку и отобрать её через
    секунду хуже, чем показать на секунду позже.
  */
  const access = useAccess([props.hasTalk]) ?? UNKNOWN_ACCESS;
  const openPro = useOpenPro();
  const paidAdvisor = access.tier === 'pro' || access.tier === 'trial';
  /** Когда был последний разбор: берём время последнего ответа-разбора. */
  const lastReview = props.lastReview;
  const paymentsSecured = result.available >= ZERO;
  const paymentsMissing = kopAbs(result.available);
  /*
    Раскладка — источник чисел для виджетов.

    Пока её нет, остаётся прежний месячный план: он хотя бы что-то говорит.
    Как только человек принял раскладку, виджеты читают её — и «Повседневные
    нужды» со «Свободными тратами» перестают обещать деньги, которых нет.
  */
  const allocation = activeAllocation(state.allocations);
  const view = allocation ? allocationViewOf(state, allocation) : null;
  /*
    Стабильный бюджет: свой ответ на вопрос «когда следующие деньги».

    Он приходит не от заказчика, а из буфера, и об этом надо сказать прямо
    рядом с датой. Человек, читающий «до выплаты 12 дней», иначе примет её
    за обещанный заработок — и потратит вперёд деньги, которые сам себе
    отложил.
  */
  const stable = stableSnapshot(state);
  // Стабильный бюджет — для тех, кому есть что раскладывать: у нового
  // человека без денег и дохода предложение «доход нерегулярный?» — шум.
  const stableOffer = !stable && stableSuggested(state)
    && (state.incomeStreams.some((x) => x.isActive) || liquidOf(state.accounts) > ZERO);

  /*
    Три числа главного экрана — по определениям, а не по удобству.

    «На счетах» — деньги на счетах для трат. «Распределено» — настоящие
    конверты, а не строки раскладки: источник правды один. «Не закреплено» —
    разность, и равенство выполняется по построению, а не проверяется вручную.
  */
  const onAccounts = liquidOf(state.accounts);
  const reservedNow = reservedOf(state.accounts, state.reserves);
  const available = sub(onAccounts, reservedNow);
  /*
    «Можно тратить» — сразу, без раскладки.

    Деньги для трат минус отложенное минус платежи до прихода, под которые
    ещё не отложено (и взнос в цель за месяц, и платёж по кредитке до
    прихода) — расчёт ядра (`computeAvailable`). Раньше крупно стояли
    корзины принятой раскладки: человек записал зарплату, отложил под
    платежи — а «Можно тратить» оставалось 0 ₽, пока он не нажмёт
    «Разложить» и не разберётся в «перерасходе корзин».
  */
  const byPlan = clampPos(result.available);
  /*
    Срок называется, только когда он твёрдый. Оценка по истории подписана
    оценкой, а неизвестность не превращается в тридцать дней.
  */
  const horizonDate = result.horizon.basis === 'confirmed' ? result.horizonDate : null;
  const estimatedHorizon = result.horizon.basis === 'estimated';
  const daysToHorizon = horizonDate ? Math.max(1, diffDays(state.today, horizonDate)) : 0;
  const perDayByPlan = result.perDay;
  /*
    Конверты — насколько их держат деньги на своём счёте.

    Под кредит авто и рассрочку у владельца отложено 21 131 ₽ на счёте, где
    лежит 16 152: главная писала «отложено» у обоих. Для покрытия платежей
    берутся урезанные конверты, для «Отложено» в трёх числах — записанные.
  */
  const backed = backedReserves({
    accounts: state.accounts, reserves: state.reserves,
    obligations: state.obligations, today: state.today,
  });
  /*
    Раскладка устарела, если изменились условия, под которые она собрана:
    платежи, цели, доход, обычная жизнь, горизонт.

    Не тем отпечатком, что защищает применение от гонки: тот меняется от
    самого принятия, от каждой траты и от смены даты — и карточка висела
    на главной всегда, даже сразу после пересчёта. Версии без отпечатка
    решений приняты до его появления, и сравнивать их не с чем.
  */
  const stalePlan = allocation !== null && allocation.structureVersion !== undefined
    && allocationProposal(state).structureVersion !== allocation.structureVersion;
  const spentThisMonth = monthReport(state.transactions, monthOf(state.today));
  /*
    Траты на жизнь — без платежей и взносов в цели: карточки отвечают «сколько
    уходит на продукты и кафе», а платежи считает число наверху.
  */
  const livingThisMonth = monthReport(
    state.transactions.filter((t) => t.link?.kind !== 'obligation' && t.link?.kind !== 'goal'),
    monthOf(state.today),
  );
  // Платежи месяца, которые ещё не оплачены: они сидят внутри плана «на
  // жизнь», но потратить их нельзя. Считаются от начала месяца, а не от
  // сегодня: просроченный неоплаченный платёж связывает деньги ровно так же,
  // как завтрашний, — и запись платежа не должна дёргать карточку.
  const owedThisMonth = obligationsOwed(
    state.obligations, state.today, state.trackingStartedAt,
  );
  // Какая часть месяца прошла — чтобы сравнивать траты с календарём, а не с
  // абстрактным процентом плана.
  const monthGone = monthShare(state.today);
  /*
    Карточки частей — по периоду до прихода, как «Можно тратить» и
    «Аналитика» (02.11.2026). По календарному месяцу 1-го числа они
    обнулялись и обещали целый месяц, хотя период ещё шёл: сверху «осталось
    22 тысячи», ниже «ещё можно 150». Плана периода нет (обычные траты не
    названы) — прежний месяц.
  */
  const nowPeriod = currentPeriodOf(state);
  const nowBudget = periodBudget(state, nowPeriod);
  const nowEnd = nowPeriod.plannedTo ?? nowPeriod.expectedTo;
  /*
    План части на период — «обычно в месяц» на длину периода (у нужд так же
    считает и «Аналитика»). Не свободный остаток плана периода: тот у
    свободных трат бывает втрое больше обычного, и карточка снова обещала бы
    лишнее. Траты — факты периода, без платежей и взносов в цели.
  */
  const nowTerm = planPeriodOf(nowPeriod);
  const nowMonths = periodMonths(nowTerm.from, nowTerm.to);
  const partTerm = nowBudget.known && nowEnd
    ? {
      // План — до рубля: доля месяца даёт копейки, а «57 171,61 ₽» плана никто не называл.
      need: { plan: toRub(share(state.needBaseline, nowMonths)), spent: nowBudget.plan.lines.need.actual },
      want: { plan: toRub(share(state.wantBaseline, nowMonths)), spent: nowBudget.plan.lines.want.actual },
      gone: nowBudget.plan.elapsed, from: nowPeriod.observedFrom, until: addDays(nowEnd, 1),
    }
    : null;
  /*
    За какой срок карточки — словами на самих карточках и над ними
    (02.11.2026): «Можно тратить» сверху — до ближайшего прихода, а части —
    за период, и без подписи это читалось как одно и то же. При двух и
    больше доходах период — календарный месяц (ядро, period.ts).
  */
  const calendarPeriod = nowPeriod.kind === 'month';
  const periodMonth = Number(nowPeriod.from.slice(5, 7)) - 1;
  /*
    Откуда считать период — явным выбором (макет 02.11.2026). При двух и
    больше доходах ядро само берёт месяц, и человек об этом не знал. Варианты:
    календарный месяц и каждый доход, приходящий раз в месяц одним числом.
    МАКЕТ: выбор пока не сохраняется — для этого ядру нужен отмеченный
    человеком главный доход.
  */
  const periodStreams = state.incomeStreams.filter((x) => x.isActive && !x.advance && roleOfStream(x) === 'checkpoint'
    && (() => { const r = rhythmOf(x); return r.kind === 'dates' && r.days.length === 1; })());
  // Выбранное человеком, даже если ещё не вступило (вступит со следующего прихода); иначе — то, что идёт.
  const periodValue = state.periodChoice
    ? (state.periodChoice.kind === 'month' ? 'month' : state.periodChoice.sourceId)
    : nowPeriod.kind === 'salary' && nowPeriod.sourceId ? nowPeriod.sourceId : 'month';
  // Как выбор части плана: крупно ответ, ниже цветом — что это, мелко — даты (02.11.2026).
  const periodOptions = [
    { id: 'month', answer: 'С 1-го числа', title: 'Календарный месяц', icon: 'calendar', tone: 'var(--ink-2)',
      sub: nowPeriod.kind === 'month' && nowEnd ? `сейчас ${dayMonth(nowPeriod.from, state.today)} – ${dayMonth(nowEnd, state.today)}` : 'с 1-го по последнее число' },
    ...periodStreams.map((x) => {
      const next = nextIncomeDate([x], state.today);
      const r = rhythmOf(x);
      return { id: x.id, answer: `От прихода «${x.name}»`, title: `${r.kind === 'dates' ? r.days[0] : ''}-го числа`, icon: 'arrow-down', tone: 'var(--ink-2)',
        sub: next ? `ближайший ${dayMonth(next, state.today)}` : 'от прихода до прихода' };
    }),
  ];
  const planTitle = partTerm && nowEnd
    // «План на ноябрь» — только когда период и правда весь месяц: обрезанный сменой источника — датами.
    ? (calendarPeriod && nowPeriod.from.endsWith('-01') && addDays(nowEnd, 1).endsWith('-01') ? `План на ${MONTH_NOM[periodMonth]}` : `План ${periodShort(nowPeriod.from)} – ${periodShort(nowEnd)}`)
    : null;
  const credit = result.credit.find((c) => !c.graceSecured);
  // Платёж по карте, наступающий уже после ближайшей зарплаты, из «Можно
  // потратить» не вычитается: за него платит будущий доход. Но сказать об
  // этом надо явно, иначе исчезнувшая из вычетов сумма выглядит потерянной.
  const creditAfterPay = credit
    ? result.upcoming.some((u) => u.kind === 'credit' && u.id === credit.accountId && u.afterHorizon)
    : false;
  const buffer = state.goals.find((g) => g.kind === 'emergency_fund');
  const bv = buffer ? bufferView(buffer.currentAmount, plan.needFloor) : null;
  const missingFacts = missingFactList(state);
  // Откладывать просим в день прихода, а не в конце месяца: остатка в конце
  // месяца обычно нет, и цель на нём не наполняется никогда.
  const nudge = savingsNudge({
    incomeFacts: incomeFacts(state.transactions),
    today: state.today, incomeStreams: state.incomeStreams, goals: state.goals,
  });
  // Порядок задач уже зашит в распределение дохода, но человеку он нигде не
  // произносился: числа на экране есть, а что из них сейчас важнее — нет.
  const stack = prioritize({
    today: state.today,
    incomeStreams: state.incomeStreams,
    obligations: state.obligations,
    needBaseline: state.needBaseline,
    goals: state.goals,
    loans: state.loans,
    available: result,
    accounts: state.accounts,
    reserves: state.reserves,
    needBaselineAmount: state.baselines.need,
    wantBaselineAmount: state.baselines.want,
    /*
      Ручной выбор главной задачи больше не действует.

      Он не влиял ни на план, ни на распределение дохода — только на надпись
      на карточке, — но выглядел как настройка приоритетов и спорил с
      важностью целей, которую человек задаёт в самих целях. Поле в снимке
      остаётся: стирать чужие данные ради уборки нельзя, а прочитанное
      значение просто не используется.
    */
  });
  const focus = stack.primary;
  // Запас хода нужен, когда дохода не видно: обещать «до зарплаты» нечем.
  // Платёж по кредитке съедает его так же, как ипотека.
  const runway = computeRunway({
    today: state.today,
    accounts: state.accounts,
    reserves: state.reserves,
    obligations: state.obligations,
    needBaseline: state.baselines.need,
    wantBaseline: state.baselines.want,
    creditTerms: state.creditTerms,
    ...(state.trackingStartedAt ? { trackingStartedAt: state.trackingStartedAt } : {}),
  });
  const noIncome = state.incomeStreams.filter((s) => s.isActive).length === 0;

  /*
    Прогноз покрытия: три состояния денег порознь.

    Деньги, которые есть и закреплены, — это одно; деньги, назначенные из
    ожидаемого прихода, — совсем другое. Складывать их в одну цифру значит
    показывать обеспеченным то, что держится на обещании работодателя.
  */
  /*
    С начала учёта, а не с сегодня: конверт расходуется на платежи по порядку
    сроков, и отложенное под неотмеченную связь 23-го не должно «покрыть»
    связь 23 октября. Список на экране всё равно показывает только будущее.
  */
  const since = state.trackingStartedAt
    && isAfter(state.trackingStartedAt, shiftDays(state.today, -62))
    && isAfter(state.today, state.trackingStartedAt)
    ? state.trackingStartedAt : state.today;
  const instances = paymentInstances({
    obligations: state.obligations, reserves: backed,
    today: state.today, from: since, to: shiftDays(state.today, 62),
  });
  const expected = expectedIncomes({
    streams: state.incomeStreams, today: state.today,
    received: receivedIncomes(state.transactions),
    // Одна модель ожиданий на весь экран: и записанные человеком приходы, и
    // наступления по ритму. Иначе «Ждём приход» и покрытие платежей считают
    // по разным спискам и расходятся на одном и том же гонораре.
    expectations: state.expectedIncomes,
    facts: incomeFacts(state.transactions),
  });
  const forecast = forecastCoverage({
    instances,
    allocations: state.plannedAllocations,
    // Назначать будущие деньги можно только от источника: назначение
    // привязано к нему. Разовый приход в прогнозе виден, но целью назначения
    // пока быть не может — это следующий шаг, не этот.
    expected: expected.filter(
      (e): e is typeof e & { incomeStreamId: string } => e.incomeStreamId !== undefined,
    ),
    labelOf: ({ target }) => allocationLabel(state, target),
  });
  /**
   * Ближайшие платежи — по календарю, а не по списку задач.
   *
   * Оплаченные не показываем: они уже позади. Обеспеченные — показываем, с
   * пометкой: «через шесть дней ипотека, деньги отложены» — это ответ на
   * вопрос, с которым человек и открывает приложение.
   */
  const coverageOf = new Map(forecast.coverage.map((c) => [c.instanceId, c]));
  const instanceOf = new Map(instances.map((i) => [i.id, i]));

  /*
    Наблюдения: поводы, которые приложение замечает само.

    Считаются арифметикой, без модели, поэтому одинаковы на любом тарифе:
    предупреждение о нехватке денег не может быть платным. Разговор о поводе
    начинается только по нажатию человека.
  */
  const insights = visibleInsights(
    computeInsightCandidates({
      today: state.today,
      available: result,
      goals: state.goals,
      incomes: state.transactions
        .filter((t) => t.type === 'income' && t.balanceApplied !== false)
        .map((t) => ({ date: t.occurredDate, amount: t.amount })),
    }),
    state.insightLog,
  );

  // Платежи за горизонтом цикла — те самые, ради которых будущий доход и
  // нужен. «Ближайшие платежи» показывают только текущий цикл: платёж 9-го
  // при зарплате 5-го в него не попадает, и без отдельного блока человеку
  // негде было бы распорядиться деньгами, которые как раз на него и пойдут.
  const inCycle = new Set(result.upcoming
    .filter((u) => u.kind === 'obligation')
    .map((u) => `${u.id}:${u.date}`));
  const beyond = forecast.coverage
    .filter((c) => !inCycle.has(c.instanceId) && !instanceOf.get(c.instanceId)?.settled)
    .filter((c) => c.gap > ZERO || c.plannedInTime > ZERO || c.plannedLate > ZERO)
    .slice(0, 4);

  // Назначения, чей срок прихода наступил. Наступление даты — не получение
  // денег: подтверждает его человек, и до подтверждения это всё ещё план.
  const awaiting = state.plannedAllocations.filter(
    (a) => (a.status ?? 'planned') === 'planned' && a.expectedDate <= state.today,
  );
  const factFor = (incomeStreamId: string): Tx | undefined => state.transactions.find(
    (t) => t.type === 'income' && t.link?.kind === 'income'
      && t.link.incomeStreamId === incomeStreamId
      && monthOf(t.occurredDate) === monthOf(state.today),
  );

  /*
    Темп — от того же числа, что крупно.

    Прежде «хватит на 7 дней» считалось от «Не закреплено» (12 172 ₽ у
    владельца), а крупно стояло 8 188 ₽ в корзинах: два ответа на один
    вопрос спорили в одном блоке. Нехватку при обычном темпе можно разобрать
    тут же — тем же листом, что и нехватку из «Сейчас главное».
  */
  const rawEnvelope = (id: string): Money => add(...state.reserves
    .filter((r) => r.forKind === 'obligation' && r.forId === id).map((r) => r.amount));
  /*
    Платежи до прихода, под которые конверт заведён, но денег на его счёте
    меньше. Платёж без конверта сюда не попадает: его «Можно тратить» уже
    вычло из свободных денег, и нехватку по нему называет строка «не хватает».
  */
  const shortSoon = instances.filter((i) => !i.settled && i.remaining > ZERO
    && !isBefore(i.scheduledDate, state.today)
    && (!horizonDate || !isAfter(i.scheduledDate, horizonDate))
    && rawEnvelope(i.obligationId) > i.secured);
  const usualPerDay = p.usualPerDay;
  const paceDays = usualPerDay > ZERO ? Math.floor(byPlan / usualPerDay) : daysToHorizon;
  const paceGap = horizonDate && usualPerDay > ZERO && paceDays < daysToHorizon
    ? clampPos(sub(share(usualPerDay, daysToHorizon), byPlan))
    : ZERO;

  /*
    Проверь сегодня — одна карточка вместо четырёх.

    На данных владельца подряд стояли «Возможно, не отмечен платёж»,
    «Раскладку нужно обновить», «Пока не распределено» и «Счета разошлись»:
    четыре красных блока с четырьмя кнопками, три из которых вели в одно
    место. Здесь каждое дело — строка с одним действием, по порядку срочности:
    прошедшие платежи, сегодняшние, нехватка под ближайшие, зависшие
    конверты, раскладка.
  */
  /*
    Карточка дела — до двух ответов. «Оплачено» у прошедшего платежа сначала
    показывает похожий записанный расход («Это оплата»), если он есть: иначе
    оплата, уже записанная расходом, записалась бы второй раз.
  */
  const todos: HomeTodo[] = [];
  // Свободно на счёте: остаток минус всё, что на нём закреплено.
  const freeOn = (accountId: string): Money => {
    const a = state.accounts.find((x) => x.id === accountId);
    if (!a) return ZERO;
    return clampPos(sub(a.balance, add(...state.reserves.filter((r) => r.accountId === accountId).map((r) => r.amount))));
  };
  const spendable = state.accounts.filter((a) => a.isLiquid && a.kind !== 'credit_card' && !a.archived)
    .map((a) => ({ id: a.id, name: a.name, free: freeOn(a.id) }));
  const [reserveFor, setReserveFor] = useState<ReserveTarget | null>(null);
  /** Лист платежа из ленты: тот раз, по которому нажали, и вторая кнопка — как раньше по тапу. */
  const [payOpen, setPayOpen] = useState<{ id: string; date: PlainDate; second: { label: string; run: () => void } | null | undefined } | null>(null);
  /*
    С какого счёта откладывать под платёж: там, где уже лежит его конверт, —
    человек сам выбрал, откуда платит няне, — а без конверта со счёта платежа.
  */
  const homeAccountOf = (obligationId: string, fallback: string): { id: string; hint: string; envelope: boolean } => {
    const held = state.reserves.filter((r) => r.forKind === 'obligation' && r.forId === obligationId && r.amount > ZERO)
      .sort((a, b) => b.amount - a.amount)[0];
    return held ? { id: held.accountId, hint: 'здесь уже конверт', envelope: true } : { id: fallback, hint: 'счёт платежа', envelope: false };
  };
  const [afterOpen, setAfterOpen] = useState(false);
  /** Лист «Период плана» (макет 02.11.2026). */
  const [periodOpen, setPeriodOpen] = useState(false);
  for (const inst of overdue) {
    const now = instanceOf.get(inst.id);
    const settle = () => (paymentTxs(state, inst).length > 0
      ? setDecide(inst)
      : props.onSettle({ id: inst.obligationId, due: inst.scheduledDate, amount: inst.unpaid }));
    const other = { label: 'Другое', run: () => setDecide(inst) };
    if (inst.scheduledDate === state.today) {
      const short = now ? now.remaining : ZERO;
      todos.push({
        key: inst.id, pill: short > ZERO ? `сегодня · не хватает ${formatMoney(short)}` : 'сегодня',
        tone: short > ZERO ? 'bad' : 'ok', title: inst.label, amount: inst.unpaid,
        note: short > ZERO ? 'денег под платёж меньше, чем нужно' : 'деньги отложены',
        main: { label: 'Оплачено', run: settle }, alt: other,
      });
    } else {
      todos.push({
        key: inst.id, pill: `срок был ${formatDate(inst.scheduledDate)}`, tone: 'warn',
        title: inst.label, amount: inst.unpaid, note: 'пока считаю эти деньги занятыми',
        main: { label: 'Оплачено', run: settle }, alt: other,
      });
    }
  }
  /*
    Редкие вопросы — тоже карточками: прежде у каждого был свой тревожный
    блок, и на главной их могло стоять пять подряд.
  */
  // Назначение на приход, которого не будет: источник отменён или уже пришёл.
  for (const o of forecast.orphaned) {
    todos.push({
      key: `orphan:${o.allocation.id}`, pill: 'не придут', tone: 'bad', title: o.label, amount: o.amount,
      note: 'назначено, а источник отменён или уже пришёл — закрепи деньги с текущего счёта или назначь другой приход',
      main: { label: 'Снять назначение', run: () => props.onCancelPlan(o.allocation.id) },
    });
  }
  // Один приход обещан дважды: выбрать за человека нельзя, только показать.
  for (const o of forecast.overcommitted) {
    const stream = state.incomeStreams.find((x) => x.id === o.incomeStreamId);
    todos.push({
      key: `twice:${o.incomeStreamId}:${o.expectedDate}`, pill: 'обещан дважды', tone: 'bad',
      title: `${stream?.name ?? 'Приход'} · ${formatDate(o.expectedDate)}`, amount: o.promised,
      note: `ожидается ${formatMoney(o.expected)}, а назначено больше: ${o.targets.map((t) => `${t.label} ${formatMoney(t.amount)}`).join(', ')}. Что-то придётся сократить`,
    });
  }
  // Деньги ждали больше месяца: решить за человека, что с ними, нельзя.
  for (const e of stale) {
    todos.push({
      key: `stale:${e.id}`, pill: 'ждали больше месяца', tone: 'bad', title: e.name, amount: expectedRest(e),
      note: `ждали ${formatDate(e.expectedDate)}: пока не скажешь, что с ними, эти деньги висят`,
      main: { label: 'Пришло', run: () => props.onArrived(e.id) },
      alt: { label: 'Не пришло', run: () => props.onCloseArrival(e.id) },
      extra: { label: 'Отменить ожидание', run: () => props.onDropArrival(e.id) },
    });
  }
  // Ожидаемый приход: срок наступил или пришла часть — спрашиваем прямо.
  for (const e of awaitedMoney) {
    const due = !isAfter(e.expectedDate, state.today);
    const part = e.status === 'partial';
    if (!due && !part) continue;
    todos.push({
      key: `await:${e.id}`, pill: part ? 'пришла часть' : `ждали ${formatDate(e.expectedDate)}`, tone: 'warn',
      title: e.name, amount: expectedRest(e),
      note: part ? `пришло ${formatMoney(e.receivedAmount ?? ZERO)} из ${formatMoney(e.amount)}` : 'в «Можно тратить» этих денег нет, пока не придут',
      ...(due ? { main: { label: part ? 'Пришёл остаток' : 'Пришли', run: () => props.onArrived(e.id) } } : {}),
      alt: part
        ? { label: 'Больше не будет', run: () => props.onCloseArrival(e.id) }
        : { label: 'Не пришли', run: () => props.onDropArrival(e.id) },
    });
  }
  // Назначенные деньги, чей приход наступил: закрепить или сначала записать приход.
  for (const a of awaiting) {
    const stream = state.incomeStreams.find((x) => x.id === a.incomeStreamId);
    const fact = factFor(a.incomeStreamId);
    const { target } = a;
    const where = allocationLabel(state, target);
    todos.push({
      key: `planned:${a.id}`, pill: fact ? 'приход записан' : 'приход ещё не записан', tone: fact ? 'ok' : 'warn',
      title: where, amount: a.amount, note: `назначено из прихода «${stream?.name ?? 'Приход'}», ждали ${formatDate(a.expectedDate)}`,
      main: fact
        ? { label: 'Закрепить', run: () => props.onFulfil({ incomeStreamId: a.incomeStreamId, date: fact.occurredDate, accountId: fact.accountId, arrived: fact.amount }) }
        : { label: 'Записать приход', run: () => props.onDistribute(a.incomeStreamId) },
      alt: { label: 'Отменить', run: () => props.onCancelPlan(a.id) },
    });
  }
  /*
    Под платёж до прихода отложено меньше нужного: конверт «Няня» на 9 000 ₽
    держит оплату 16-го, а под 23-е и 30-е ничего нет. Денег при этом
    хватает — «Можно тратить» их уже вычло, — и решение не «найти деньги», а
    отложить их, чтобы не потратить: со счёта платежа одним нажатием, если там
    хватает, или с другого счёта.
  */
  for (const i of shortSoon) {
    if (!isAfter(i.scheduledDate, state.today)) continue;
    const c = coverageOf.get(i.id);
    const home = homeAccountOf(i.obligationId, i.accountId);
    const acc = state.accounts.find((a) => a.id === home.id);
    const own = freeOn(home.id);
    const target: ReserveTarget = { obligationId: i.obligationId, label: i.label, date: i.scheduledDate, need: i.remaining, accountId: home.id, accountHint: home.hint };
    const others = spendable.some((a) => a.id !== home.id && a.free > ZERO);
    todos.push({
      key: `short:${i.id}`, pill: 'не отложено', tone: 'warn',
      title: `${i.label} · ${formatDate(i.scheduledDate)}`, amount: i.remaining,
      note: acc ? `${home.envelope ? 'конверт на' : 'платёж с'} «${acc.name}» · там свободно ${formatMoney(own)}` : 'под этот платёж ничего не отложено',
      main: !paymentsSecured && c
        ? { label: 'Разобрать', run: () => props.onShort(c) }
        : own >= i.remaining
          ? { label: `Отложить ${formatMoney(i.remaining)}`, run: () => { void topUpReserve({ accountId: home.id, forKind: 'obligation', forId: i.obligationId, amount: i.remaining }).then(props.onChanged); } }
          : { label: 'Отложить', run: () => setReserveFor(target) },
      ...(paymentsSecured && others ? { alt: { label: 'С другого счёта', run: () => setReserveFor({ ...target, accountId: '' }) } } : {}),
    });
  }
  // Платёж сделан, а конверт под него всё держит: деньги ушли со счёта и
  // при этом продолжают считаться отложенными.
  for (const u of result.upcoming) {
    if (u.kind !== 'obligation' || !u.settled) continue;
    for (const r of staleHolds(state, u.id)) {
      todos.push({
        key: `hold:${r.id}`, pill: 'платёж сделан', tone: 'info', title: u.label, amount: r.amount,
        note: `а на «${state.accounts.find((a) => a.id === r.accountId)?.name ?? 'счёте'}» под него всё ещё отложено`,
        main: { label: 'Снять', run: () => props.onRelease(r.id) },
      });
    }
  }
  // Пришла зарплата — пора отложить в цели: копится то, что убрано до трат.
  if (nudge.incomeArrived && nudge.pending > ZERO) {
    for (const a of nudge.asks.filter((x) => !x.funded)) {
      todos.push({
        key: `fund:${a.goalId}`, pill: `зарплата пришла ${formatDate(nudge.cycleStart!)}`, tone: 'ok',
        title: a.name, amount: a.amount, note: a.why,
        main: { label: 'Отложено', run: () => props.onFund(a.goalId) },
      });
    }
  }

  /*
    После прихода: придёт, уйдёт, останется — «две чаши весов».

    Кредитка владельца на 151 975 ₽ 9 октября, пять платежей за первую
    неделю после зарплаты — и ни одного места, где было бы видно, сколько
    от неё останется. У стабильного бюджета свой ответ, здесь его нет.

    Считается общим прогнозом — тем же, что проверка покупки и советник:
    доход по нижней оценке, жизнь по дням, обе части долга по карте.
  */
  const outlook = stable ? null : incomeOutlook({
    today: state.today, horizon: horizonDate,
    accounts: state.accounts, creditTerms: state.creditTerms,
    incomeStreams: state.incomeStreams, obligations: state.obligations,
    reserves: state.reserves,
    needBaseline: state.baselines.need, wantBaseline: state.baselines.want,
    ...(state.trackingStartedAt ? { trackingStartedAt: state.trackingStartedAt } : {}),
    incomeFacts: incomeFacts(state.transactions),
  });

  /*
    Платежи — одним списком.

    Прежде о них рассказывали шесть мест: «Ближайший платёж», «Дальше» в
    «Сейчас главное», «Дальше по календарю», «Ближайшие платежи», «Подтверди
    платежи» и карточка кредитки. «Кредит авто» стоял на экране четыре раза,
    а сентябрьские оплаченные открывали «Ближайшие». Здесь — только
    предстоящее; прошедшее и сегодняшнее живут в «Проверь сегодня».
  */
  type PayRow = PayRowView;
  const payRows: PayRow[] = [];
  // Список — до конца отрезка «После прихода»: дальше он превращается в
  // календарь на два месяца, из которого ближайшее не найти.
  const payUntil = outlook?.to ?? shiftDays(state.today, 62);
  for (const i of instances) {
    if (i.settled || !isAfter(i.scheduledDate, state.today) || isAfter(i.scheduledDate, payUntil)) continue;
    const after = !!horizonDate && isAfter(i.scheduledDate, horizonDate);
    const c = coverageOf.get(i.id);
    const planned = c ? c.plannedInTime : ZERO;
    const status = i.remaining === ZERO ? 'отложено'
      : !after ? `${paymentsSecured ? 'не отложено' : 'не хватает'} ${formatMoney(i.remaining)}`
      : planned > ZERO ? `назначено из прихода ${formatMoney(planned)}`
      : 'из прихода';
    const tone = i.remaining === ZERO ? 'ok' : !after ? 'bad' : 'dim';
    /*
      Тап — лист платежа с «Оплатить» (16.10.2026). То, что прежде открывалось
      по тапу сразу, — его вторая кнопка: «Отложить» (лист сам), «что делать»
      при нехватке, «Назначить из прихода».
    */
    const second = i.remaining > ZERO && c
      ? !after
        ? paymentsSecured
          ? undefined
          : { label: `Не хватает ${formatMoney(i.remaining)} — что делать`, run: () => props.onShort(c) }
        : expected.length > 0 && planned === ZERO
          ? { label: 'Назначить из прихода', run: () => props.onPlanIncome({
            instanceId: i.id, obligationId: i.obligationId,
            label: i.label, dueDate: i.scheduledDate, need: c.gap > ZERO ? c.gap : i.remaining,
          }) }
          : null
      : null;
    const run = () => setPayOpen({ id: i.obligationId, date: i.scheduledDate, second });
    // Досрочное по долгу — часть его платежа: видно, из чего сумма.
    const extra = state.loans.find((l) => l.obligationId === i.obligationId)?.extraMonthly ?? ZERO;
    payRows.push({
      key: i.id, label: i.label, date: i.scheduledDate, amount: i.unpaid,
      status: extra > ZERO ? `вкл. ${formatMoney(extra)} досрочно · ${status}` : status, tone, after,
      // Часть уже внесена — переплатой прошлого раза или заранее.
      ...(i.paid > ZERO ? { paid: `${formatMoney(i.paid)} внесено заранее` } : {}),
      short: after ? ZERO : i.remaining,
      run,
    });
  }
  for (const c of result.credit) {
    if (c.settled || c.needForGrace <= ZERO || !isAfter(c.paymentDate, state.today)
      || isAfter(c.paymentDate, payUntil)) continue;
    const after = !!horizonDate && isAfter(c.paymentDate, horizonDate);
    // Сумма — то, что осталось внести, как у остальных платежей: внесённое на
    // карту уже не платёж. Прежде здесь стояла вся выписка, а внесённое
    // называлось «отложено» — после погашения строка выглядела нетронутой.
    const left = clampPos(sub(c.needForGrace, c.reserved));
    // Отложено — только конверты: деньги ещё на счёте, но под этот платёж.
    const held = clampPos(sub(left, c.missing));
    payRows.push({
      key: `card:${c.accountId}`, label: c.name, date: c.paymentDate, amount: left,
      status: c.missing === ZERO ? 'отложено'
        : `${held > ZERO ? `отложено ${formatMoney(held)} · ` : ''}${after ? 'остальное из прихода' : `не хватает ${formatMoney(c.missing)}`}`,
      tone: c.missing === ZERO ? 'ok' : after ? 'dim' : 'bad',
      after, short: after ? ZERO : c.missing,
      run: () => props.onEditCard(c.accountId),
    });
  }
  /*
    Покупки после выписки — второй платёж по карте, из прогноза.

    Выписку человек вводит руками, и следующая сама не появляется. Без этой
    строки «После прихода» считал бы платёж, которого нет в списке ниже, а
    сам человек узнавал бы о нём из банка.
  */
  for (const e of cardPayments({
    today: state.today, accounts: state.accounts, creditTerms: state.creditTerms, reserves: backed,
  })) {
    if (e.card?.part !== 'after_statement' || !isAfter(e.date, state.today)
      || isAfter(e.date, payUntil)) continue;
    const after = !!horizonDate && isAfter(e.date, horizonDate);
    const left = notCovered(e);
    payRows.push({
      key: `card-after:${e.ref}`, label: e.label, date: e.date, amount: e.amount,
      status: left === ZERO ? 'отложено' : after ? 'из прихода' : `не хватает ${formatMoney(left)}`,
      tone: left === ZERO ? 'ok' : after ? 'dim' : 'bad',
      after, short: after ? ZERO : left,
      run: () => props.onEditCard(e.ref),
    });
  }
  payRows.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const [paysOpen, setPaysOpen] = useState(false);
  const incomeLabel = (() => {
    const l = outlook?.incomeLines.find((x) => x.date === horizonDate) ?? outlook?.incomeLines[0];
    return l ? `Приход · ${l.label}` : 'Приход';
  })();

  /*
    Шапка: одна строка о платежах и, если есть, одна — о темпе или дате
    прихода. Строка о платежах ведёт в «Все платежи», о темпе — в варианты.
  */
  const strips: HeroStrip[] = [];
  if (!noIncome) {
    if (!paymentsSecured) {
      strips.push({ tone: 'bad', text: `Не хватает ${formatMoney(paymentsMissing)} на ближайшие платежи`, onClick: () => setPaysOpen(true) });
    } else if (shortSoon.length > 0) {
      strips.push({
        tone: 'warn', onClick: () => setPaysOpen(true),
        text: `Под ${shortSoon.length === 1 ? `«${shortSoon[0]!.label}»` : plural(shortSoon.length, 'платёж', 'платежа', 'платежей')} `
          + `отложено меньше нужного — доложи ${formatMoney(add(...shortSoon.map((i) => i.remaining)))}`,
      });
    } else {
      strips.push({ tone: 'ok', text: 'Платежи до следующего прихода обеспечены' });
    }
  }
  if (paceGap > ZERO && paymentsSecured) {
    strips.push({
      tone: 'warn', onClick: () => props.onFocus(paceGap),
      text: `Обычно уходит ${formatMoney(usualPerDay)} в день — хватит на ${plural(paceDays, 'день', 'дня', 'дней')} из ${daysToHorizon}, не хватит ≈ ${formatMoney(paceGap)}`,
    });
  }
  if (!horizonDate) strips.push({ tone: 'info', text: 'Дата следующего прихода неизвестна — назови её в доходах, и я посчитаю дневной темп.' });
  if (estimatedHorizon) strips.push({ tone: 'info', text: 'Дата прихода оценена по истории — подтверди её в доходах.' });

  // Наблюдение о цели стоит в строке цели, остальные — отдельным блоком.
  const shownInsights = view ? insights.filter((i) => i.kind !== 'income_unallocated') : insights;
  const goalInsight = (goalId: string) => shownInsights.find((i) => i.action?.target === `goal:${goalId}`);
  const goalIds = new Set(state.goals.map((g) => g.id));
  const otherInsights = shownInsights.filter((i) => !(i.action?.target?.startsWith('goal:') && goalIds.has(i.action.target.slice(5))));

  return (
    <>
      <header className="brandbar">
        <Wordmark {...(props.pro ? { pro: true } : {})} />
        {/* Профиль — единственные вопросы человека про себя, а не про деньги:
            кто вошёл, за что платит, что с его данными и как выйти. */}
        <div className="hright">
          <NetBadge state={props.net} account={props.account} />
          {props.onAskHead && <AskHeadButton onClick={props.onAskHead} />}
          <ProfileCapsule name={state.profileName} onProfile={props.onProfile} onMenu={props.onMenu}
            bell={props.bell} onBell={props.onBell} />
        </div>
      </header>

      {/* На компьютере — две колонки: слева ответ и «Коротко», справа платежи и задачи. На телефоне обёрток не видно. */}
      <div className="homegrid"><div className="homecol">
      {/*
        Ответ — сколько можно тратить до ближайшего прихода.

        Раньше крупно стояло «Не закреплено»: все деньги без конверта, вместе
        с нераспределёнными и появившимися мимо приложения. У владельца это
        12 172 ₽ при 8 188 ₽ в корзинах — крупная цифра обещала больше, чем
        разрешает план. Первый внешний пользователь сказал то же своими
        словами: вместо суммы «на сегодня» — сумму до числа с учётом
        следующего дохода. «Не отложено» осталось рядом, в трёх числах, —
        как состав, а не как ответ.

        До первой раскладки корзин нет, и крупно стоит то, что не отложено:
        других честных чисел в этот момент нет.
      */}
      <HeroCard
        today={state.today} horizonDate={horizonDate} daysToHorizon={daysToHorizon} amount={byPlan}
        perDay={horizonDate && byPlan > ZERO ? perDayByPlan : null}
        sub={horizonDate ? null : runway.known
          ? `При обычном темпе хватит примерно на ${plural(runway.days, 'день', 'дня', 'дней')}`
          : 'Чтобы сказать, на сколько хватит, нужна оценка обычных трат'}
        strips={strips}
        onAccounts={onAccounts} reserved={reservedNow} free={available}
        onSpend={props.onSpend} />

      {/*
        Аккаунта нет — тихая строка: данные живут только на этом телефоне.
        Регистрацию предлагают один раз, после плана; закрыл лист — и больше
        ничто об этом не напоминало, кроме профиля (15.10.2026).
      */}
      {props.guest && (
        <button className="guestline" onClick={props.onSignup}>
          <Icon name="lock" size={14} />
          <span>Данные только на этом телефоне</span>
          <b>Создать аккаунт</b>
          <Icon name="chevron-right" size={12} />
        </button>
      )}

      {/*
        «Могу купить?» считает ядро на устройстве: это бесплатно всем,
        включая гостя, и работает без сети. «Спроси SKOKA» отсюда ушёл в
        кнопку в шапке (22.10.2026; до того — над «+», 14.10.2026).

        «Записать» рядом (15.10.2026, docs/design/menu-2026-10-15): «+» в
        меню не заметили — человек спросил, как добавить операцию. Кнопка
        словами открывает то же окно, что «+»: расход, приход, перевод, в
        цель, долг.
      */}
      <section className="acts two">
        <button className="act" onClick={props.onCheck}>
          <CartIcon />
          <span>Могу купить?</span>
          <b className="chev"><Icon name="chevron-right" size={13} /></b>
        </button>
        <button className="act pri" onClick={props.onAdd}>
          <Icon name="plus" size={18} />
          <span>Записать</span>
        </button>
      </section>

      {/*
        Пробный кончается через 2 дня — одна строка под главной цифрой (§7):
        когда и что будет, без давления. Карту не просили, списывать нечего.
      */}
      {access.tier === 'trial' && access.endsAt && daysLeft(access.endsAt) <= 2 && (
        <button className="laycall pro-trial-note" onClick={() => openPro('general')}>
          <div><b>Пробный Pro до {dayLabel(access.endsAt.slice(0, 10))}</b>
            <span>Потом — бесплатная версия, записи останутся. Ничего не спишется.</span></div>
          <span className="btn inline pri">{access.billing ? 'Остаться в Pro' : 'Что даёт Pro'}</span>
        </button>
      )}
      <TodoRail todos={todos} />

      {/*
        «Коротко»: две части плана и что останется после прихода.

        «Обычно в месяц» — по истории трат (usual.ts), подпись говорит, откуда
        число: по скольким месяцам, по оценке или закреплено.
      */}
      {(state.needBaseline > ZERO || outlook) && <div className="gsec"><h2>{planTitle ?? 'Коротко'}</h2>{planTitle && PERIOD_CHOICE && <button type="button" className="gsec-pick gsec-ico" aria-label="Период плана" onClick={() => setPeriodOpen(true)}><Icon name="calendar" size={15} /></button>}</div>}
      {periodOpen && <ChoiceSheet title="Период плана" value={periodValue} options={periodOptions}
        lead="С какого дня считать план?"
        tip={<>От этого зависит, за какой срок считаются «Повседневные нужды», «Свободные траты» и «Аналитика». «Можно тратить» не меняется.{periodValue === 'month' && periodStreams.length > 1 ? ' Сейчас стоит месяц: доходов несколько, и приложение выбрало само.' : ''} Идущий период пересчитается сразу.</>}
        onPick={(id) => {
          const x = periodStreams.find((p) => p.id === id);
          if (id === periodValue) return;
          void setPeriodChoice(id === 'month' ? { kind: 'month' } : { kind: 'salary', sourceId: id }).then(async () => {
            await props.onChanged();
            flashMessage(id === 'month' ? 'План — с 1-го числа' : `План — от прихода «${x?.name ?? ''}»`);
          });
        }}
        onClose={() => setPeriodOpen(false)} />}
      {(state.needBaseline > ZERO || outlook) && (
        <section className="duo">
          {state.needBaseline > ZERO && (
            <>
              {/*
                Блок — ход плана, а не второе «можно тратить» (02.11.2026):
                наверху — деньги до прихода, здесь — сколько из плана части
                потрачено и успевается ли к концу периода. Строки во всю
                ширину: название целиком, сумма справа.
              */}
              <div className="planrows">
                <PlanRow title="Повседневные нужды" icon={<NeedIcon />} tone="m"
                  plan={partTerm ? partTerm.need.plan : state.needBaseline}
                  spent={partTerm ? partTerm.need.spent : livingThisMonth.need}
                  gone={partTerm ? partTerm.gone : monthGone}
                  onOpen={() => props.onPart('need')} />
                <PlanRow title="Свободные траты" icon={<WantIcon />} tone="h"
                  plan={partTerm ? partTerm.want.plan : state.wantBaseline}
                  spent={partTerm ? partTerm.want.spent : livingThisMonth.want}
                  gone={partTerm ? partTerm.gone : monthGone}
                  onOpen={() => props.onPart('want')} />
              </div>
            </>
          )}
          {outlook && (
            <button className="gafter" onClick={() => setAfterOpen(true)}>
              <span className="gafter-top"><span>После прихода · {formatDate(outlook.from)} – {formatDate(outlook.to)}</span>
                <Icon name="chevron-right" size={12} /></span>
              <span className="gafter-sum">
                <b className={`num${outlook.left !== null && outlook.left < ZERO ? ' bad' : ''}`}>{outlook.left === null
                  ? 'назови обычные траты'
                  : outlook.left < ZERO ? `не хватит ${formatMoney(outlookGap(outlook))}` : `≈ ${formatMoney(outlook.left)}`}</b>
                <span>{outlook.left !== null && outlook.left >= ZERO ? 'останется' : ''} из {formatMoney(outlook.income)}{outlook.variable ? ' по нижней оценке' : ''}</span>
              </span>
              <span className="gafter-bar" aria-hidden="true">
                <i className="pay" style={{ flexGrow: outlook.payments }} />
                {outlook.living !== null && <i className="life" style={{ flexGrow: outlook.living }} />}
                {outlook.left !== null && outlook.left > ZERO && <i className="left" style={{ flexGrow: outlook.left }} />}
              </span>
              <span className="gafter-legend">
                <span><i className="pay" />платежи {formatMoney(outlook.payments)}</span>
                {outlook.living !== null && <span><i className="life" />жизнь {formatMoney(outlook.living)}</span>}
              </span>
            </button>
          )}
        </section>
      )}

      </div><div className="homecol">
      <PayTimeline rows={payRows} horizonDate={horizonDate} incomeLabel={incomeLabel} onAll={() => setPaysOpen(true)} />
      {afterOpen && outlook && <AfterIncomeSheet outlook={outlook} onClose={() => setAfterOpen(false)} />}
      {payOpen && (
        <PaymentSheet state={state} obligationId={payOpen.id} date={payOpen.date}
          {...(payOpen.second !== undefined ? { second: payOpen.second } : {})}
          onClose={() => setPayOpen(null)}
          onPay={(t) => { setPayOpen(null); props.onSettle(t); }}
          onEdit={() => { const id = payOpen.id; setPayOpen(null); props.onEditObligation(id); }}
          onChanged={props.onChanged} />
      )}
      {reserveFor && (
        <ReserveSheet target={reserveFor} accounts={spendable} AmountField={AmountInput}
          onClose={() => setReserveFor(null)}
          onSave={async (accountId, amount) => {
            await topUpReserve({ accountId, forKind: 'obligation', forId: reserveFor.obligationId, amount });
            setReserveFor(null);
            props.onChanged();
          }} />
      )}
      {paysOpen && (
        <PaymentsSheet rows={payRows} today={state.today} horizonDate={horizonDate} incomeLabel={incomeLabel}
          onClose={() => setPaysOpen(false)} />
      )}



      {/*
        Разрыв: обещанные деньги не придут.

        Назначение осталось в истории — это прежнее решение человека, — но
        покрытием оно больше не считается, и платёж снова гол. Карточка висит,
        пока разрыв не закрыт: молча забыть его значит обещать обеспеченность
        того, что ничем не обеспечено.
      */}

      {/*
        Стабильный бюджет: дата следующей выплаты себе.

        Стоит выше «Не распределено» намеренно: сначала человек узнаёт, когда
        придут следующие деньги, и только потом — что делать с сегодняшними.
      */}
      {stable && (
        <section className={`blk${
          stable.outlook.warning === 'none' && !needsDecision(stable.outlook.due) ? '' : ' alarm'
        }`}>
          <div className="blkhead"><b>Стабильный бюджет
            <Tip>Это твои деньги из буфера, а не ожидаемый внешний доход.
              {stable.plan.status === 'paused'
                && ' Возобновишь — ближайшей станет первая дата начиная с этого дня.'}</Tip></b>
            <span className="tag soft">в запасе {formatMoney(stable.buffer)}</span>
            {stable.plan.status === 'paused' && <span className="tag">приостановлен</span>}
          </div>
          {stable.plan.status === 'paused' ? (
            <div className="note" style={{ marginTop: 0 }}>Новые выплаты не наступают.</div>
          ) : stable.outlook.gap ? (
            /*
              Долгое отсутствие: на главной один вопрос, а не тринадцать.
              Срок при этом не называется — что было всё это время, никто
              не знает.
            */
            <>
              <div className="li"><div className="nm">
                <b className="mega-s num">
                  Режим не сверялся с {formatDate(stable.outlook.gap.from)}
                </b>
                <span>{plural(stable.outlook.gap.count, 'выплата', 'выплаты', 'выплат')}
                  {' '}без ответа · срок следующей не считается</span>
              </div></div>
              <button className="btn pri" onClick={props.onStable}>Разобрать</button>
            </>
          ) : needsDecision(stable.outlook.due) ? (
            /*
              Решение о сегодняшней или просроченной выплате — единственное,
              что сейчас имеет значение. Следующий срок при этом **не
              называется**: перескочить через невзятые деньги значило бы
              обещать спокойный месяц тому, кто их не получил.
            */
            <>
              <div className="li"><div className="nm">
                <b className="mega-s num">
                  {stable.outlook.due!.state === 'due_today'
                    ? `Сегодня выплата себе ${formatMoney(stable.outlook.due!.amount)}`
                    : `Выплата себе за ${formatDate(stable.outlook.due!.scheduledDate)} не проведена`}
                </b>
                <span>пока не решишь, следующий срок не наступает</span>
              </div></div>
              <button className="btn pri" onClick={props.onStable}>
                Выплатить себе {formatMoney(stable.outlook.due!.amount)}
              </button>
              <button className="btn" onClick={props.onStable}>Пропустить или изменить</button>
            </>
          ) : stable.outlook.due ? (
            <div className="li"><div className="nm">
              <b className="mega-s num">
                {stable.outlook.due.state === 'unfunded'
                  ? 'Следующая выплата не обеспечена'
                  : `До следующей выплаты себе — ${plural(
                    diffDays(state.today, stable.outlook.due.scheduledDate), 'день', 'дня', 'дней')}`}
              </b>
              <span>{formatMoney(stable.outlook.due.amount)}
                {' '}· {formatDate(stable.outlook.due.scheduledDate)}</span>
            </div></div>
          ) : (
            <div className="note" style={{ marginTop: 0 }}>Выплат впереди не назначено.</div>
          )}
          <div className="note" style={{ marginTop: 0 }}>{stable.outlook.why}.</div>
          {stable.outlook.warning === 'next_unfunded' && (
            <div className="note">
              Сама выплата не состоится и не уменьшится: реши, пополнить запас,
              уменьшить выплату или приостановить режим.
            </div>
          )}
          <button className="btn" onClick={props.onStable}>Настроить</button>
        </section>
      )}

      {/*
        Предложение режима. Только там, где он правда решает задачу: доход
        рывками, твёрдого срока нет, и денег хватает на два месяца. Молча
        режим не включается никогда.
      */}
      {stableOffer && (
        <section className="blk">
          <div className="blkhead"><b>Доход приходит нерегулярно?
            <Tip>Можно сделать бюджет стабильнее: отложить часть денег в запас и
              платить себе из него по расписанию — как зарплату.</Tip></b></div>
          <button className="btn pri" onClick={props.onStable}>Посмотреть, как это</button>
        </section>
      )}

      {/*
        Перенос объясняет, почему часть денег трогать нельзя. Без объяснения
        это выглядит как пропавшие деньги.
      */}
      {view && view.carry > ZERO && (
        <div className="note">
          {formatMoney(view.carry)} отложены до ближайшего прихода.
          <Tip>Его не хватит на следующий отрезок, и эти деньги понадобятся уже после него.</Tip>
        </div>
      )}

      {/*
        Перечень фактов вместо процента точности (PRD §4.11).

        Процент был оценкой, которой не существует: доля от чего? Человек не
        мог ни проверить его, ни понять, что делать, чтобы стало больше.
        Список того, что осталось уточнить, отвечает на второй вопрос сразу,
        а первый снимает вовсе.
      */}
      {/*
        Каждый пункт — строка, которая сразу открывает свою форму. Прежде это
        была одна плашка со списком через точку, и тап вёл в общее меню —
        человек с пустыми данными не находил, куда что вписать (15.10.2026).
      */}
      {missingFacts.length > 0 && (
        <section className="blk missing">
          <div className="blkhead"><b>Осталось уточнить
            <Tip>Без этого часть ответов приблизительна, а часть — неизвестна: SKOKA не выдумывает то, чего не знает.</Tip></b></div>
          {missingFacts.map((f) => (
            <button key={f.key} className="li w" onClick={() => props.onFill(f.open)}>
              <span className="nm"><b>{f.label}</b></span>
              <Icon name="chevron-right" size={13} />
            </button>
          ))}
        </section>
      )}

      {/*
        Нехватку до прихода уже называет главная цифра — строкой темпа с
        кнопкой «Что сделать». Карточка с той же задачей второй раз ниже
        повторяла её другими числами, а кнопка «Поставить цель» под списком
        повседневных трат смешивала выживание с мечтами. Поэтому при принятой
        раскладке карточка показывается только для задач другого рода: долг,
        подушка, цель.
      */}
      {!state.isEmpty && !(view && MONEY_SHORT.includes(focus.kind)) && (
        <section className={`blk focus ${focusTone(focus.kind)}`}>
          <div className="blkhead">
            <span className="fico" aria-hidden="true"><FocusIcon kind={focus.kind} /></span>
            <b>Сейчас главное</b>
            <button className="icobtn sm" onClick={props.onWhy} aria-label="Почему это главное">?</button>
          </div>
          {/*
            Заголовок, число и объяснение — три разных голоса, и различать их
            надо размером, а не только цветом. Пока всё это лежало строкой
            списка и двумя примечаниями, сумма задачи выглядела подписью, а
            «Дальше» — самым крупным текстом карточки.
          */}
          <div className="ftitle">{focus.title}</div>
          {focus.amount > ZERO && <div className="fsum num">{formatMoney(focus.amount)}</div>}
          <div className="fwhy">{focus.why}</div>
          <div className="fact">{focus.action}</div>
          {/* Подпись кнопки зависит от того, что за задача: «Посмотреть
              варианты» одинаково подходит и к дыре в бюджете, и к цели — а
              значит не говорит ни о чём. */}
          {/*
            Нехватку называет сама задача, а не разность на счетах. «Дожить до
            прихода» считает, сколько не хватает **на повседневные нужды** до следующего
            дохода: свободные деньги при этом бывают положительными — 3 000 ₽
            на двадцать дней это не профицит.
          */}
          <button className="btn fbtn"
            onClick={() => (focus.kind === 'unknown_need' ? props.onUsual()
              : props.onFocus(MONEY_SHORT.includes(focus.kind) ? focus.amount : ZERO))}>
            {focusAction(focus.kind)}<span className="chev" aria-hidden="true"><Icon name="chevron-right" size={13} /></span>
          </button>

        </section>
      )}

      {/* Наблюдения стоят под главной задачей и над входом советника: сначала
          то, что приложение заметило само, потом место для вопроса. */}
      {/*
        «Пришло N, а платежам не хватает» при принятой раскладке уже сказано
        в «Проверь сегодня» — делом с кнопкой. Третий раз ту же сумму на
        одном экране человек читает как три разные беды.
      */}
      {!state.isEmpty && (
        <InsightCards insights={otherInsights}
          onAct={(insight) => {
            /*
              «Не хватает на платёж» — в лист нехватки этого платежа, как
              «Не хватает … — что делать» в «Ближайших платежах». Прежде
              кнопка просто переключала на «Деньги», без объяснения
              (02.11.2026). Ключ наблюдения: payment:<вид>:<id>:<дата>.
            */
            if (insight.action?.target === 'payment') {
              const [, kind, id, date] = insight.key.split(':');
              const inst = kind === 'obligation' ? instances.find((i) => i.obligationId === id && i.scheduledDate === date) : undefined;
              const c = inst ? coverageOf.get(inst.id) : undefined;
              if (c) { props.onShort(c); return; }
              // Наступления нет в окне «Ближайших платежей» (просрочка давнее окна) — тот же лист из чисел «Можно тратить»:
              // прежде кнопка молча уводила в «Деньги» (03.11.2026).
              const due = kind === 'obligation' ? result.upcoming.find((x) => x.kind === 'obligation' && x.id === id && x.date === date) : undefined;
              if (due) {
                props.onShort({
                  instanceId: `${due.id}:${due.date}`, label: due.label, dueDate: due.date,
                  amount: due.amount, secured: due.covered, plannedInTime: ZERO, plannedLate: ZERO, expectedBy: null,
                  gap: due.remaining, state: due.remaining > ZERO ? 'gap' : 'covered',
                });
                return;
              }
              /*
                Кредитка: тот же лист, числа — из «Можно тратить»: нужно
                внести для беспроцентного, внесено и отложено в конвертах
                (в том числе на других счетах), не хватает (02.11.2026).
              */
              const u = kind === 'credit' ? result.upcoming.find((x) => x.kind === 'credit' && x.id === id) : undefined;
              if (u) {
                // Назначенное из будущих приходов на эту карту (03.11.2026) — как у обычных платежей.
                const mine = state.plannedAllocations.filter((a) => isPlanned(a)
                  && a.target.kind === 'card' && a.target.accountId === u.id);
                const inTime = mine.filter((a) => !isAfter(a.expectedDate, u.date));
                const plannedInTime = add(...inTime.map((a) => a.amount));
                const plannedLate = add(...mine.filter((a) => isAfter(a.expectedDate, u.date)).map((a) => a.amount));
                const gap = clampPos(sub(u.remaining, plannedInTime));
                props.onShort({
                  instanceId: `card:${u.id}`, label: `${u.label} · для беспроцентного периода`, dueDate: u.date,
                  amount: u.amount, secured: u.covered, plannedInTime, plannedLate,
                  expectedBy: inTime.map((a) => a.expectedDate).sort().at(-1) ?? null,
                  gap,
                  state: u.remaining === ZERO ? 'covered' : gap > ZERO ? (plannedLate > ZERO ? 'late' : 'gap') : 'forecast',
                });
                return;
              }
            }
            props.onInsight(insight);
          }}
          onDismissed={props.onChanged} />
      )}

      {/*
        Разбор — отдельная секция, а не третий вход в советника.

        Прежде здесь стоял блок с полем вопроса, кнопкой разбора и рассказом
        про Pro сразу. Вместе с кнопкой в hero это давало три двери в одну
        комнату; теперь дверей две: вопрос — наверху, разбор — здесь.
      */}
      {/* У Pro разбор — в кнопке «Спроси SKOKA» в шапке; здесь остаётся карточка для
          тех, у кого её нет: витрина PRO или первый бесплатный разбор. */}
      {!state.isEmpty && !paidAdvisor && (
        <AdvisorSection access={access} {...(lastReview ? { lastReview } : {})}
          onReview={() => props.onAsk({ mode: 'review' })}
          onPlans={() => openPro('general')}
          onSignup={() => navigate('signup')} />
      )}


      {/*
        Деньги, которых ждут, — пока их срок не наступил: вернут долг, премия,
        продажа. К доступному не прибавляются. Наступил срок, пришла часть или
        ждали больше месяца — спрашивает карточка «Проверь сегодня».
      */}

      {awaitedMoney.some((e) => isAfter(e.expectedDate, state.today) && e.status !== 'partial') && (
        <section className="blk">
          <div className="blkhead"><b>Ждём приход
            <Tip>В «Доступно» этих денег нет: считается только то, что пришло.
              Частичный приход ожидание не закрывает — остаток всё ещё ждут.</Tip></b></div>
          {awaitedMoney.filter((e) => isAfter(e.expectedDate, state.today) && e.status !== 'partial').map((e) => {
            const due = !isAfter(e.expectedDate, state.today);
            const rest = expectedRest(e);
            const part = e.status === 'partial';
            return (
              <div className="li fund" key={e.id}>
                <div className="nm"><b>{e.name}</b>
                  <span>
                    {part
                      ? `пришло ${formatMoney(e.receivedAmount ?? ZERO)} из ${formatMoney(e.amount)}`
                      : due ? `ждали ${formatDate(e.expectedDate)}` : formatDate(e.expectedDate)}
                  </span>
                </div>
                <div className="fundside">
                  <div className="am num">{formatMoney(rest)}</div>
                  {due && (
                    <button className="btn inline pri" onClick={() => props.onArrived(e.id)}>
                      {part ? 'Пришёл остаток' : 'Пришли'}
                    </button>
                  )}
                  {/*
                    «Больше не будет» — решение человека, а не догадка.
                    Пока он его не принял, ожидание живёт и горизонт стоит на
                    месте: приложение не вправе решить за него, что остатка
                    не случится.
                  */}
                  {part
                    ? (
                      <button className="btn inline" onClick={() => props.onCloseArrival(e.id)}>
                        Больше не будет
                      </button>
                    )
                    : (
                      <button className="btn inline" onClick={() => props.onDropArrival(e.id)}>
                        {due ? 'Не пришли' : 'Отменить'}
                      </button>
                    )}
                </div>
              </div>
            );
          })}
        </section>
      )}




      <section className="blk">
        {/* Вместо «будущему себе» — сколько отложено на все цели: цели бывают
            и не для себя (подарок, помощь родителям), а итог нужен всем. */}
        <div className="blkhead"><b>Коплю</b>{(() => {
          const shown = state.goals.filter((g) => g.status === 'active' || g.status === 'paused');
          return shown.length > 0
            ? <span className="goaltotal num">{formatMoney(add(...shown.map((g) => g.currentAmount)))} из {formatMoney(add(...shown.map((g) => g.targetAmount)))}</span>
            : null;
        })()}
          <button className="icobtn sm addbtn" onClick={props.onAddGoal} aria-label="Новая цель"><Icon name="plus" size={15} /></button></div>
        {state.goals.filter((g) => g.status === 'active' || g.status === 'paused').length === 0 ? (
          <div className="li"><div className="nm">
            <b>Целей пока нет</b>
            <span>Подушка, закрыть долг, крупная покупка — с них видно, куда идут деньги</span>
          </div></div>
        ) : state.goals.filter((g) => g.status === 'active' || g.status === 'paused').map((g) => {
          // Цель открывается на правку: сменить важность, срок, взнос или
          // поставить на паузу. Прежде отсюда не было входа вовсе.
          return (
            <Fragment key={g.id}>
            <GoalRow state={state} goal={g} needFloor={plan.needFloor} onOpen={() => props.onEditGoal(g.id)} />
            {goalInsight(g.id) && (
              <GoalHint insight={goalInsight(g.id)!} goalName={g.name} onAct={props.onInsight} onDismissed={props.onChanged} />
            )}
            </Fragment>
          );
        })}
      </section>

      {/*
        Что сделать с неотмеченным платежом.

        Каждый ответ здесь означает разное для денег, и лист даёт место это
        сказать: «уже оплачено» списывает со счёта, «ещё нужно оплатить»
        оставляет деньги связанными, «платежа не было» закрывает одну дату, а
        не обязательство. В строке на главной для таких объяснений места нет.
      */}
      {decide && (() => {
        /*
          Ответ — выбором, как часть плана, и одной кнопкой (03.11.2026): пять
          кнопок подряд, три из них зелёные, читались как «нажми любую».
          Найденные расходы — первыми: деньги со счёта уже ушли, второй расход
          записывать не надо.
        */
        const inst = decide;
        const txs = paymentTxs(state, inst).slice(0, 2);
        const options: ChoiceOption[] = [
          ...txs.map((t) => ({
            id: `tx:${t.id}`, icon: 'link', tone: 'var(--ok)',
            answer: `Это оплата: −${formatMoney(t.amount)}`,
            title: `${t.note || state.categories.find((c) => c.id === t.categoryId)?.name || 'расход'} · ${formatDate(t.occurredDate)}`,
          })),
          { id: 'paid', icon: 'circle-check', tone: 'var(--ok)', answer: 'Оплачено, но ещё не записано',
            title: 'запишу расход со счёта — целиком или частью' },
          { id: 'unpaid', icon: 'hourglass', tone: 'var(--warn)', answer: 'Ещё нужно оплатить',
            title: 'деньги останутся отложенными, вопрос уйдёт с главной' },
          { id: 'skip', icon: 'forward', tone: 'var(--ink-2)', answer: 'В этот раз платить не нужно',
            title: 'закрывает только эту дату, платёж остаётся' },
        ];
        return (
          <ChoiceSheet title="Что с платежом?" value="" options={options}
            lead={<>{inst.label} · {formatMoney(inst.unpaid)}<small>срок был {formatDate(inst.scheduledDate)}</small></>}
            tip={<>Пока ответа нет, эти деньги считаются отложенными на платёж.</>}
            confirm={(id) => (id === 'paid' ? 'Дальше' : 'Готово')}
            footer={
              <button type="button" className="lnk wide choice-foot"
                onClick={() => { setDecide(null); props.onEditObligation(inst.obligationId); }}>
                Дата или сумма изменились насовсем — изменить
              </button>
            }
            onClose={() => setDecide(null)}
            onPick={async (id) => {
              setDecide(null);
              if (id.startsWith('tx:')) {
                await linkPaymentTx(id.slice(3), inst.obligationId, inst.scheduledDate);
                props.onChanged();
              } else if (id === 'paid') {
                props.onSettle({ id: inst.obligationId, due: inst.scheduledDate, amount: inst.unpaid });
              } else {
                props.onDecide(inst.obligationId, inst.scheduledDate, id === 'unpaid' ? 'unpaid' : 'not_applicable', inst.amount);
              }
            }} />
        );
      })()}
      </div></div>
    </>
  );
}

/**
 * Часть плана: сколько осталось, а не сколько было запланировано.
 *
 * Плановая сумма сама по себе ничего не говорит: человек и так помнит, что
 * договаривался о восьмидесяти тысячах. Вопрос, на который он смотрит с экрана,
 * другой — сколько ещё можно. Поэтому главная цифра здесь «осталось», а план
 * ушёл в подпись (SCREENS §15, PRD §2.1).
 *
 * Перерасход не прячем и не рисуем отрицательным остатком: «осталось −5 000»
 * читается как загадка, «потратил на 5 000 больше» — как факт.
 */
/**
 * Часть плана на месяц: «Повседневные нужды» и «Свободные траты».
 *
 * Крупное число — то, что человек действительно может потратить, а не остаток
 * строки бюджета. Для «Повседневные нужды» это значит вычесть ещё не прошедшие
 * обязательные платежи месяца: пока они сидели внутри, карточка показывала
 * 77 248 ₽ там, где на еду оставалось 19 467, — и выглядела как разрешение
 * тратить.
 *
 * Полоса сравнивает не с планом, а с календарём: в середине месяца «потрачено
 * 48%» ничего не значит, пока не сказано, что месяца прошло 60%.
 */
/** Выбор периода плана — значок календаря у заголовка (02.11.2026). */
const PERIOD_CHOICE = true;

/** «12 окт» — даты периода над карточками. */
const periodShort = (d: PlainDate): string => `${Number(d.slice(8, 10))} ${['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'][Number(d.slice(5, 7)) - 1]}`;

/** Сумма до целого рубля. */
const toRub = (m: Money): Money => (Math.round(m / 100) * 100) as Money;

/**
 * Строка плана части (02.11.2026): потрачено из плана, полоска с отметкой
 * «сегодня» — доля прошедшего периода, под ней остаток и темп. Жирные —
 * только суммы.
 */
/** Сумма в строке плана — до рубля: копейки в обзоре только мешают. */
const rub = (m: Money): string => formatMoney(toRub(m));

const PlanRow = (p: { title: string; icon: JSX.Element; tone: 'm' | 'h'; plan: Money; spent: Money; gone: number; onOpen: () => void }) => {
  const noPlan = p.plan <= ZERO;
  const over = !noPlan && p.spent > p.plan;
  const pct = noPlan ? 100 : Math.min(100, Math.round((p.spent / p.plan) * 100));
  const today = Math.min(100, Math.max(0, Math.round(p.gone * 100)));
  const fast = !noPlan && !over && pct > today + 5;
  return (
    <button type="button" className={`planrow pt-${p.tone}${over ? ' over' : ''}`} onClick={p.onOpen}
      aria-label={`${p.title}: потрачено ${formatMoney(p.spent)} из ${formatMoney(p.plan)}`}>
      <span className="pr-top">
        <span className="picon">{p.icon}</span>
        <span className="pr-name">{p.title}</span>
        {!noPlan && <span className={`pr-pace${over ? ' bad' : fast ? ' warn' : ''}`}>{over ? 'перерасход' : fast ? 'быстрее плана' : 'в темпе'}</span>}
      </span>
      <span className="pr-bar"><i style={{ width: `${pct}%` }} />{!noPlan && <s style={{ left: `${today}%` }} aria-hidden="true" />}</span>
      <span className="pr-bot">
        <span><b className="num">{rub(p.spent)}</b>{noPlan ? ' — в плане на это ничего нет' : <> из <b className="num pr-of">{rub(p.plan)}</b></>}</span>
        {!noPlan && <span>{over ? <>сверх на <b className="num">{rub(sub(p.spent, p.plan))}</b></> : <>осталось <b className="num pr-left">{rub(sub(p.plan, p.spent))}</b></>}</span>}
      </span>
    </button>
  );
};

/** «на ноябрь». */
const MONTH_NOM = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];


/**
 * Секция советника: разбор либо предложение Pro.
 *
 * Три состояния, и путать их нельзя. Человеку, у которого первый разбор ещё
 * не потрачен, замок показывать нельзя вовсе: мы обещали его бесплатно на
 * стартовом экране и в тарифах, и увидеть вместо обещанного витрину — худшее
 * первое впечатление из возможных. Заплативший, у которого кончился пакет, не
 * должен видеть «Попробовать PRO»: он уже попробовал и заплатил.
 */
function AdvisorSection({ access, lastReview, onReview, onPlans, onSignup }: {
  access: AccessState;
  lastReview?: string;
  onReview: () => void;
  onPlans: () => void;
  onSignup: () => void;
}) {
  const gate = advisorGate(access);
  const paid = access.tier === 'pro' || access.tier === 'trial';
  // Пока доступ неизвестен (нет сети, ответ не пришёл), витрину не показываем:
  // предлагать купить вслепую — то же, что предлагать купить уже купленное.
  const unknown = access.offline;

  if (!paid && !unknown && gate.kind !== 'open' && gate.kind !== 'first') {
    // Гостю — аккаунт: пробный и Pro живут на нём.
    if (gate.kind === 'signup') {
      return (
        <section className="blk advisor">
          <div className="prohead"><SparkIcon size={26} /><div><b>Финансовый советник</b></div></div>
          <div className="note" style={{ marginTop: 0 }}>{gate.note}</div>
          <button className="btn pri" onClick={onSignup}>Создать аккаунт</button>
        </section>
      );
    }
    /*
      Карточка Pro (21.10.2026, docs/design/pro-2026-10-21): тёмная среди
      светлых блоков — заметна, но не кричит. Внутри живой пример того, что
      откроется, а не перечень «больше возможностей».
    */
    return (
      <section className="pro-card">
        <ProLockup />
        <h3>Говори — SKOKA запишет</h3>
        <div className="demo dmini">
          <div className="demo-say"><span className="demo-mic"><Icon name="microphone" size={13} /></span>
            <span className="demo-text">кофе 250 с карты</span></div>
          <div className="demo-op"><span className="catico c-amber"><Icon name="mug-hot" size={19} /></span>
            <div className="nm"><b>Кофе</b><span>Карта · сейчас</span></div><b className="am num">−250 ₽</b></div>
        </div>
        <p>Ещё чеки по фото, выписки из банка и вопросы SKOKA про твои деньги.</p>
        <button className="pro-btn" onClick={onPlans}>
          {access.billing
            ? access.earlyYear ? `Подключить Pro — ${access.earlyYear.toLocaleString("ru-RU")} ₽ за год` : 'Подключить Pro — от 249 ₽ в месяц'
            : access.trial.available ? `Попробовать ${access.trial.days} дней бесплатно` : 'Что даёт Pro'}
        </button>
        <span className="pro-fine">
          {access.billing && access.earlyYear ? 'новым пользователям: первый год за полцены'
            : access.trial.available ? 'Без карты · само не продлится' : 'Учёт и «Можно тратить» бесплатны всегда'}
        </span>
      </section>
    );
  }

  return (
    <section className="blk advisor">
      <div className="prohead"><SparkIcon size={26} />
        <div><b>Финансовая картина</b></div>
        {paid && <span className="tag soft">PRO</span>}
      </div>
      <div className="note" style={{ marginTop: 0 }}>
        SKOKA посмотрит на твою ситуацию целиком: найдёт главное, риски
        и подскажет, что делать.
      </div>
      <button className="btn pri" onClick={onReview}>Посмотреть разбор</button>
      <div className="note after">
        {lastReview
          ? `Последний разбор · ${whenReview(lastReview)}`
          : gate.kind === 'first'
            ? 'Первый разбор — бесплатно, без карты'
            : 'Разборов ещё не было'}
      </div>
    </section>
  );
}

/** «сегодня, 18:42» либо «17 сентября, 18:42». */
function whenReview(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const day = new Date(iso).toDateString() === new Date().toDateString()
    ? 'сегодня'
    : `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return `${day}, ${time}`;
}

/**
 * Что написано на кнопке задачи.
 *
 * «Посмотреть варианты» одинаково подходит и к дыре в бюджете, и к цели — а
 * значит не говорит человеку ничего. Подпись называет род задачи: у проблемы
 * спрашивают, как её решить, у долга — что делать, у цели смотрят план.
 */
/**
 * Цвет задачи.
 *
 * Не украшение: по тону человек узнаёт род беды раньше, чем прочтёт заголовок.
 * Нехватка денег — предупреждающий жёлтый, долг и проценты — фирменный
 * розовый, цель и порядок — зелёный. Тон задаётся одной переменной, и на неё
 * же опираются рамка, заливка и сумма.
 */
function focusTone(kind: PriorityKind): string {
  if (kind === 'survive_to_income' || kind === 'income_gap' || kind === 'cover_obligations') {
    return 't-short';
  }
  if (kind === 'avoid_interest_or_overdue' || kind === 'expensive_debt') return 't-debt';
  if (kind === 'nothing_yet') return 't-ok';
  return 't-goal';
}

/** Значок задачи: тот же смысл, что у тона, но виден и в чёрно-белом. */
const FocusIcon = ({ kind }: { kind: PriorityKind }) => {
  const tone = focusTone(kind);
  // Неизвестные траты — вопрос, а не цель: значок говорит «нужно узнать».
  const name = kind === 'unknown_need' ? 'circle-question'
    : tone === 't-short' ? 'exclamation' : tone === 't-debt' ? 'credit-card'
      : tone === 't-ok' ? 'check' : 'bullseye';
  return <Icon name={name} size={16} />;
};

function focusAction(kind: PriorityKind): string {
  if (kind === 'survive_to_income' || kind === 'income_gap' || kind === 'cover_obligations') {
    return 'Как решить';
  }
  if (kind === 'avoid_interest_or_overdue' || kind === 'expensive_debt') return 'Что делать';
  if (kind === 'nothing_yet') return 'Поставить цель';
  if (kind === 'unknown_need') return 'Указать траты';
  return 'Посмотреть план';
}

/**
 * Наблюдение о цели — в строке самой цели («Коплю»), а не отдельным блоком:
 * проблема стоит там, к чему относится. «Не сейчас» — как у наблюдений.
 */
function GoalHint({ insight, goalName, onAct, onDismissed }: {
  insight: InsightCandidate; goalName: string;
  onAct: (i: InsightCandidate) => void; onDismissed: () => void;
}) {
  useEffect(() => { void markInsightShown(insight); }, [insight.key, insight.signature]);
  // «Шкаф в прихожую: взнос не задан…» — имя цели уже стоит строкой выше.
  const text = insight.text.startsWith(`${goalName}: `) ? insight.text.slice(goalName.length + 2) : insight.text;
  return (
    <div className="ghint">
      <div className="ghint-text"><Icon name="lightbulb" size={15} /><span>{text.charAt(0).toUpperCase() + text.slice(1)}</span></div>
      <div className="ghint-acts">
        {insight.action && <button className="btn inline pri" onClick={() => onAct(insight)}>{insight.action.label}</button>}
        <button className="btn inline" onClick={async () => { await dismissInsight(insight); onDismissed(); }}>Не сейчас</button>
      </div>
    </div>
  );
}

/** Значки действий и секции Pro. */
const CartIcon = () => <Icon name="cart-shopping" size={19} />;
const SparkIcon = ({ size = 20 }: { size?: number }) => <Icon name="sparkles" size={Math.round(size * 0.9)} />;
const LockIcon = () => <Icon name="lock" size={16} />;
const ChatIcon = () => <Icon name="message-lines" size={18} />;
const EyeIcon = () => <Icon name="eye" size={18} />;

/** Значок части плана: дом — жизнь, сердце — себе. */
/** Значки частей плана — свои, как в выборе части (02.11.2026). */
const NeedIcon = () => <Icon name="basket-shopping-check" size={18} />;
const WantIcon = () => <Icon name="bag-shopping-heart" size={18} />;

/**
 * Часть плана: сколько в месяц, сколько потрачено, сколько осталось.
 *
 * Сумма в месяц правится здесь же (28.10.2026): раньше «Изменить оценку» вела
 * в отдельный лист «Обычные траты» с обеими частями и галочкой «Не менять по
 * истории». Порядок — как считается: в месяц, потрачено, осталось; остаток
 * назван один раз. Назвал свою сумму — подстройка под траты выключается сама:
 * иначе история тихо перекрыла бы названное число.
 */
function PartSheet({ part, state, planned, spent, onSaved, onClose }: {
  part: 'need' | 'want';
  state: AppState;
  /** «Обычно в месяц» — по истории трат или по оценке. */
  planned: Money;
  spent: { need: Money; want: Money };
  onSaved: () => void | Promise<void>;
  onClose: () => void;
}) {
  const isLife = part === 'need';
  const title = isLife ? 'Повседневные нужды' : 'Свободные траты';
  const used = isLife ? spent.need : spent.want;
  const usual = isLife ? state.usual.need : state.usual.want;
  const start = planned > ZERO ? formatAmountInput(String(planned / 100)) : '';
  const [amount, setAmount] = useState(start);
  const [adjust, setAdjust] = useState(usual.source !== 'pinned');
  const [busy, setBusy] = useState(false);
  const typed = amount === start ? planned : parseAmountInput(amount);
  const left = sub(typed, used);
  const over = left < ZERO;
  const source = amount !== start ? 'твоя сумма' : usualSource(usual);
  const changed = amount !== start || adjust !== (usual.source !== 'pinned');
  const [y, m] = state.today.split('-').map(Number) as [number, number];
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const dayNo = Number(state.today.slice(8, 10));
  const month = MONTHS[m - 1];
  /*
    Прогноз — тот же темп до конца месяца, до сотни рублей. В первые дни
    месяца одна покупка задирала бы его вдвое, поэтому — с пятого дня.
  */
  const projected = dayNo >= 5 && used > ZERO
    ? (Math.round((used / dayNo) * days / 10_000) * 10_000) as Money : null;
  const save = async () => {
    setBusy(true);
    try {
      // Своё число — в оценку этой части; другая часть остаётся как была.
      if (amount !== start || !adjust) {
        const mine = confirmed(typed);
        await setBaselineAmounts(isLife ? mine : state.estimates.need, isLife ? state.estimates.want : mine);
      }
      await setUsualPinned(part, !adjust);
      await onSaved();
    } finally { setBusy(false); }
  };
  return (
    <SheetWrap title={title} onClose={onClose}
      tip="«В месяц» считается по твоим тратам: серединный месяц из последних полных, а пока их нет — по твоей оценке. Сколько можно тратить до прихода — число на главной.">
      <div className="partsheet">
        <div className="ps-plan">
          <span className="ps-label"><b>В месяц</b>{source && <small>{source}</small>}</span>
          <span className="ps-amount">
            <AmountInput value={amount} label="Сколько в месяц" placeholder="0"
              onChange={(v) => { setAmount(v); if (v !== start) setAdjust(false); }} />
            <span aria-hidden="true">₽</span>
          </span>
        </div>
        <label className="ps-toggle">
          <span className="ps-label"><b>Подстраивать под мои траты</b>
            <small>раз в месяц — по последним месяцам. Выключи, и сумма останется твоей</small></span>
          <input type="checkbox" role="switch" className="rcswitch" checked={adjust} aria-label="Подстраивать под мои траты"
            onChange={(e) => setAdjust(e.target.checked)} />
        </label>
        <div className="ps-sum">
          <div className="ps-line"><span>Потрачено с 1 {month}</span><b className="num">{formatMoney(used)}</b></div>
          <div className={`ps-line ps-left${over ? ' bad' : ''}`}>
            <span>{over ? 'Больше обычного' : 'Осталось'}</span>
            <b className="num">{formatMoney(over ? kopAbs(left) : left)}</b>
          </div>
        </div>
        {projected !== null && (
          <div className="note">
            Если тратить так же — к {days} {month} уйдёт ≈ {formatMoney(projected)}
            {projected > typed ? `, на ${formatMoney(sub(projected, typed))} больше обычного` : ''}.
          </div>
        )}
        {changed && (
          <button className="btn pri" disabled={busy || typed <= ZERO} onClick={() => void save()}>Сохранить</button>
        )}
      </div>
    </SheetWrap>
  );
}

// ─────────────────────────────────────────────────────── транзакции

function Transactions({ state, ask, onAdd, onChanged, onAdjust }: {
  state: AppState; ask: ReactNode; onAdd: () => void; onChanged: () => void; onAdjust: (accountId: string) => void;
}) {
  return (
    <TxEditLayer state={state} onChanged={onChanged}>
      {(open, remove) => <Operations state={state} ask={ask} onEdit={open} onDelete={remove} onAdd={onAdd} onChanged={onChanged} onAdjust={onAdjust} />}
    </TxEditLayer>
  );
}

/**
 * Правка и удаление операции с «Вернуть» — общие для «Операций» и листа
 * категории в «Аналитике»: одна операция правится одинаково, откуда ни открой.
 *
 * Правка — тот же лист, что запись (15.10.2026): вторая форма для тех же
 * полей выглядела и вела себя иначе, и человек учил две.
 */
function TxEditLayer({ state, onChanged, children }: {
  state: AppState; onChanged: () => void;
  children: (open: (id: string) => void, remove: (id: string) => void) => ReactNode;
}) {
  const [edit, setEdit] = useState<string | null>(null);
  /*
    Только что удалённая операция — пока видна плашка «Вернуть».

    Вместо вопроса «Удалить?» перед удалением: подтверждение спрашивает о том,
    в чём человек уверен, и не спасает от промаха пальцем, а «Вернуть» спасает.
  */
  const [undo, setUndo] = useState<DeletedTx | null>(null);
  const [restoring, setRestoring] = useState(false);
  useEffect(() => {
    if (!undo) return;
    const t = setTimeout(() => setUndo(null), UNDO_MS);
    return () => clearTimeout(t);
  }, [undo]);
  /** Удалить свайпом: сразу, с «Вернуть» — как из листа правки. */
  const remove = async (id: string) => {
    const deleted = await deleteTransaction(id);
    track('op_deleted', { via: 'swipe' });
    if (deleted) setUndo(deleted);
    onChanged();
  };
  return (
    <>
      {children(setEdit, (id) => void remove(id))}
      {edit && (() => {
        const tx = state.transactions.find((t) => t.id === edit);
        if (!tx) return null;
        const done = { onClose: () => { setEdit(null); onChanged(); }, onSaved: () => { setEdit(null); onChanged(); },
          onDeleted: (deleted: DeletedTx) => { setEdit(null); setUndo(deleted); onChanged(); } };
        // Корректировка — свой лист: у неё названный остаток, а не сумма и категория.
        return tx.type === 'adjustment'
          ? <AdjustmentSheet state={state} tx={tx} {...done} />
          : <AddOperation state={state} edit={tx} onPlans={() => undefined} {...done} />;
      })()}
      {/* Плашка — поверх листов: свайпом удаляют и в листе категории «Аналитики». */}
      {undo && createPortal(
        <div className="undo-toast" role="status">
          <span>Операция удалена</span>
          <button className="lnk" disabled={restoring} onClick={async () => {
            setRestoring(true);
            try { await restoreTransaction(undo); setUndo(null); onChanged(); }
            finally { setRestoring(false); }
          }}>Вернуть</button>
        </div>, document.body)}
    </>
  );
}

/** Сколько видна плашка «Вернуть» после удаления операции. */
const UNDO_MS = 5000;

// ─────────────────────────────────────────────────────────── счета

/**
 * Экран счёта (концепт «Деньги» 26.10.2026, выбор владельца 27.10.2026):
 * открыл счёт — сразу видно, что с ним сделать, что с него платится и что
 * по нему было.
 *
 * История — последние пять операций прямо здесь, а «Все операции счёта»
 * ведут в «Операции» с выбранным счётом: там поиск, периоды и свайпы, и
 * второй экран истории держать не нужно.
 */
function AccountScreen({ state, accountId, needFloor, onBack, onRecord, onTransfer, onAdjust, onSettings, onPayment, onGoal, onAllOps, onChanged }: {
  state: AppState; accountId: string; needFloor: Money;
  onBack: () => void; onRecord: () => void; onTransfer: () => void; onAdjust: () => void; onSettings: () => void;
  onPayment: (id: string) => void; onGoal: (id: string) => void; onAllOps: () => void; onChanged: () => void;
}) {
  const [allPays, setAllPays] = useState(false);
  const a = state.accounts.find((x) => x.id === accountId);
  if (!a) return null;
  const checked = checkedText(state, a.id);
  const bank = a.icon?.startsWith('bank:') ? bankName(a.icon.slice(5)) : null;
  const sub = [bank, a.kind === 'cash' ? null : KIND_NAME[a.kind as AccKind]?.toLowerCase(), a.isLiquid ? 'для трат' : 'не для трат']
    .filter(Boolean).join(' · ');
  const pays = state.obligations.filter((o) => o.isActive && o.accountId === a.id)
    .map((o) => ({ o, next: nextPaymentInstance(state, o.id) }))
    .sort((x, y) => (x.next?.scheduledDate ?? '9999').localeCompare(y.next?.scheduledDate ?? '9999'));
  const monthly = add(ZERO, ...pays.map(({ o }) => o.amount));
  const goals = goalsOnAccount(state, a.id);
  const txs = state.transactions.filter((t) => t.accountId === a.id || t.counterAccountId === a.id)
    .sort((x, y) => (y.occurredAt ?? y.occurredDate).localeCompare(x.occurredAt ?? x.occurredDate));
  const acts: [string, string, () => void, boolean?][] = [
    ['plus', 'Записать', onRecord, true], ['arrow-right-arrow-left', 'Перевести', onTransfer],
    ['pen-to-square', 'Настроить', onSettings],
  ];
  return (
    <>
      <div className="obj-top">
        <button className="obj-back" onClick={onBack}><Icon name="chevron-left" size={14} />Деньги</button>
        {/* Карандаш — только у суммы: «Скорректировать остаток». Лист счёта — кнопкой «Настроить» (27.10.2026). */}
        <div className="obj-head"><AccountGlyph account={a} size="s" /><b>{a.name}</b></div>
        <div className="obj-sumrow"><span className={`obj-sum num${a.balance < ZERO ? ' bad' : ''}`}>{formatMoney(a.balance)}</span>
          <button className="penbtn" onClick={onAdjust} aria-label="Скорректировать остаток"><Icon name="pen" size={14} /></button></div>
        <div className="obj-sub">{sub}{checked ? <> · <span className={checked.warn ? 'warn' : ''}>{checked.text}</span></> : null}</div>
        <div className="obj-acts">
          {acts.map(([icon, label, go, main]) => (
            <button key={label} className={main ? 'main' : ''} onClick={go}><span><Icon name={icon} size={19} /></span>{label}</button>
          ))}
        </div>
      </div>
      {goals.length > 0 && (
        <section className="blk">
          <div className="blkhead"><b>Копится здесь</b>
            <span className="grpsum num">{formatMoney(add(ZERO, ...goals.map((g) => g.currentAmount)))}</span></div>
          {goals.map((g) => <GoalRow key={g.id} state={state} goal={g} needFloor={needFloor} onOpen={() => onGoal(g.id)} />)}
        </section>
      )}
      {pays.length > 0 && (
        <section className="blk">
          <div className="blkhead"><b>Платится отсюда</b><span className="grpsum num">{formatMoney(monthly)} в месяц</span></div>
          {(allPays ? pays : pays.slice(0, 3)).map(({ o, next }) => (
            <button className="li w" key={o.id} onClick={() => onPayment(o.id)}>
              <div className="nm"><b>{o.name}</b><span>{next ? formatDate(next.scheduledDate) : 'Платежей по расписанию больше нет'}</span></div>
              <div className="am num">{next ? formatMoney(next.unpaid) : '—'}</div>
            </button>
          ))}
          {pays.length > 3 && !allPays && (
            <button className="li w obj-more" onClick={() => setAllPays(true)}>
              <div className="nm"><span>ещё {plural(pays.length - 3, 'платёж', 'платежа', 'платежей')} · {formatMoney(add(ZERO, ...pays.slice(3).map(({ o }) => o.amount)))}</span></div>
              <span className="ch"><Icon name="chevron-down" size={13} /></span>
            </button>
          )}
        </section>
      )}
      {/* Операции — как на экране «Операций»: днями, без рамки вокруг; «Все» — туда же с этим счётом. */}
      <div className="obj-h"><b>Операции</b>
        {txs.length > 0 && <button onClick={onAllOps}>Все<Icon name="chevron-right" size={12} /></button>}</div>
      {txs.length === 0
        ? <p className="obj-none">По этому счёту операций пока нет.</p>
        : (
          <TxEditLayer state={state} onChanged={onChanged}>
            {(open, remove) => <OpDays state={state} days={byDay(txs.slice(0, 5))} accountId={a.id} onOpen={open} onDelete={remove} />}
          </TxEditLayer>
        )}
    </>
  );
}

/** Архив счетов: вернуть счёт в работу (из прежних «Счетов», 27.10.2026). */
function AccountArchive({ state, onClose, onChanged }: { state: AppState; onClose: () => void; onChanged: () => void }) {
  const archived = state.accounts.filter((a) => a.archived);
  useEffect(() => { if (archived.length === 0) onClose(); }, [archived.length]);
  return (
    <SheetWrap title="Архив счетов" onClose={onClose} className="ops-sheet">
      <p className="ops-context">В капитал и «Можно тратить» не входят. Операции по ним — на месте, в «Операциях» и «Аналитике».</p>
      {archived.map((a) => (
        <div className="li" key={a.id}>
          <AccountGlyph account={a} />
          <div className="nm"><b>{a.name}</b>
            <span>{a.archivedAt ? `в архиве с ${dayMonth(a.archivedAt, state.today)}` : 'в архиве'}</span></div>
          <div className="am num">{formatMoney(a.balance)}</div>
          <button className="textact inline" onClick={async () => { await restoreAccount(a.id); onChanged(); }}>Вернуть</button>
        </div>
      ))}
    </SheetWrap>
  );
}

/**
 * Порядок счетов: тянут за полоски.
 *
 * Полоски у названия группы двигают всю группу, у счёта — счёт внутри его
 * группы. Порядок сохраняется сразу, как палец отпущен: «Готово» только
 * закрывает экран, и забыть его нажать — не значит потерять перестановку.
 */
function AccountOrder({ state, onDone }: { state: AppState; onDone: () => void }) {
  const live = state.accounts.filter((a) => !a.archived);
  const [groups, setGroups] = useState(() => accountGroups(live, state.accountGroups).map((g) => ({ key: g.key, title: g.title, ids: g.accounts.map((a) => a.id) })));
  const [drag, setDrag] = useState<{ list: string; id: string } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const latest = useRef(groups);
  latest.current = groups;
  const byId = (id: string) => live.find((a) => a.id === id);

  const save = (next: typeof groups) => void saveAccountOrder(next.map((g) => g.key), next.flatMap((g) => g.ids));

  const grip = (list: string, id: string, label: string) => ({
    className: 'grip',
    'aria-label': label,
    onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      setDrag({ list, id });
    },
    onPointerMove: (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!drag || drag.id !== id || !box.current) return;
      // Место — по середине соседей: сколько их выше пальца, таков и номер.
      const others = [...box.current.querySelectorAll<HTMLElement>(`[data-list="${list}"]`)]
        .filter((el) => el.dataset.id !== id);
      const at = others.filter((el) => { const r = el.getBoundingClientRect(); return r.top + r.height / 2 < e.clientY; }).length;
      setGroups((prev) => {
        if (list === 'groups') {
          const keys = prev.map((g) => g.key);
          if (keys.indexOf(id as typeof keys[number]) === at) return prev;
          const moving = prev.find((g) => g.key === id)!;
          const rest = prev.filter((g) => g.key !== id);
          return [...rest.slice(0, at), moving, ...rest.slice(at)];
        }
        return prev.map((g) => {
          if (g.key !== list || g.ids.indexOf(id) === at) return g;
          const rest = g.ids.filter((x) => x !== id);
          return { ...g, ids: [...rest.slice(0, at), id, ...rest.slice(at)] };
        });
      });
    },
    onPointerUp: () => { setDrag(null); save(latest.current); },
    onPointerCancel: () => { setDrag(null); save(latest.current); },
  });

  return (
    <div className="reordering" ref={box}>
      <header className="pgtitle pghead"><h1>Порядок счетов</h1>
        <button className="reorder-done" onClick={onDone}>Готово</button></header>
      <p className="reorder-hint">Потяни за полоски справа — выше или ниже. Полоски у названия группы двигают всю группу.
        В таком порядке счета будут везде в приложении.</p>
      {groups.map((g) => (
        <section className={`blk${drag?.id === g.key ? ' dragging' : ''}`} key={g.key} data-list="groups" data-id={g.key}>
          <div className="blkhead"><b>{g.title}</b>
            <button {...grip('groups', g.key, `Передвинуть группу «${g.title}»`)}><Icon name="grip-lines" size={16} /></button></div>
          {g.ids.map((id) => {
            const a = byId(id);
            if (!a) return null;
            return (
              <div className={`li${drag?.id === id ? ' dragging' : ''}`} key={id} data-list={g.key} data-id={id}>
                <AccountGlyph account={a} />
                <div className="nm"><b>{a.name}</b>{!a.isLiquid && a.kind !== 'credit_card' && <span>не для трат</span>}</div>
                {g.ids.length > 1 && <button {...grip(g.key, id, `Передвинуть «${a.name}»`)}><Icon name="grip-lines" size={16} /></button>}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

/**
 * Долги: сколько осталось, что гасить первым и почему.
 *
 * Справа у долга — остаток, как баланс у счетов. Прежде там стояли проценты
 * за год, и «28 197 ₽ в год» у автокредита рядом с «341 272 ₽» у ипотеки
 * читалось как ошибка очереди. Проценты за год — одной строкой вверху.
 *
 * Кредитка здесь, пока на ней минус. Пока держится беспроцентный период,
 * процентов нет, и она в конце очереди. Ставка — из её кредита, если названа.
 *
 * Объяснения у каждого долга собираются из его цифр (`debtWhy`): главная
 * путаница — «процентов больше у ипотеки, значит гасить её» — снимается
 * прямо там, где возникает.
 */
function Debts({ state, result, onPrepay, onAdd, onEdit, onEditCard }: {
  state: AppState; result: AvailableResult;
  onPrepay: (loanId: string) => void;
  onAdd: () => void;
  onEdit: (loanId: string) => void;
  onEditCard: (accountId: string) => void;
}) {
  const [method, setMethod] = useState<DebtMethod>('rate');
  const loans = state.loans.filter((l) => l.kind !== 'credit_card' && l.principal > ZERO);
  const items: Array<DebtItem & { loan?: AppState['loans'][number]; accountId?: string }> = [
    ...loans.map((l) => {
      const payment = add(l.payment, l.extraMonthly ?? ZERO);
      const monthsLeft = amortize({
        principal: l.principal, ratePct: l.ratePct, payment,
        kind: l.schedule ?? 'annuity',
        // Убывающему графику нужно число оставшихся платежей: без него расчёт
        // честно отвечает «не знаю», а не считает по равному платежу.
        ...(l.termMonths ? { termMonths: l.termMonths } : {}),
      }).months;
      return {
        id: l.id, name: l.name, principal: l.principal, ratePct: l.ratePct, payment,
        cost: annualCost(l), monthsLeft, loan: l,
      };
    }),
    ...state.accounts
      .filter((a) => a.kind === 'credit_card' && !a.archived && a.balance < ZERO)
      .map((a) => {
        const loan = state.loans.find((l) => l.kind === 'credit_card' && l.card?.accountId === a.id);
        const st = result.credit.find((c) => c.accountId === a.id);
        return {
          id: `card:${a.id}`, name: a.name, principal: kop(-a.balance), ratePct: loan?.ratePct ?? 0,
          payment: ZERO, cost: ZERO, monthsLeft: null, accountId: a.id,
          card: { missing: st?.missing ?? ZERO, paymentDate: st?.paymentDate ?? state.today },
        };
      }),
  ];
  const head = (
    <div className="blkhead"><b>Долги{items.length > 0 && <Tip>{DEBTS_RULE}</Tip>}</b>
      <button className="icobtn sm addbtn" onClick={onAdd} aria-label="Добавить кредит"><Icon name="plus" size={15} /></button></div>
  );
  // Без долгов блок остаётся: кредит, взятый после настройки, иначе было
  // бы некуда добавить.
  if (items.length === 0) {
    return (
      <section className="blk">
        {head}
        <div className="note">Ипотека, кредит, рассрочка или личный долг — добавь, и платёж войдёт в расчёт.</div>
      </section>
    );
  }

  const queue = debtQueue(items, method);
  /*
    Долг на сегодня — основной долг и проценты, набежавшие с последнего
    платежа: банк считает их каждый день, и в его приложении долг между
    платежами растёт. Очередь и объяснения считаются по основному долгу.
  */
  const owed = (d: (typeof items)[number]) => add(d.principal, d.loan?.accrued ?? ZERO);
  // Проценты набегают копейками каждый день — в списке они целыми рублями: иначе строка не помещается.
  const rubR = (m: Money): string => formatMoney(kop(Math.round(m / 100) * 100));
  const total = add(...items.map(owed));
  const yearly = add(...items.map((d) => d.cost));
  const perDay = add(...loans.map((l) => l.accruedPerDay ?? ZERO));

  return (
    <section className="blk">
      {head}
      <div className="li"><div className="nm"><b className="mega-s num bad">{rubR(total)}</b>
        <span>{yearly > ZERO
          ? `≈ ${formatMoney(yearly)} процентов в год${perDay > ZERO ? ` · +${rubR(perDay)} в день` : ''}`
          : 'процентов не начисляется'}</span></div></div>

      {queue.length > 1 && (
        <div className="prio" role="group" aria-label="Приоритет">
          <span className="prio-k">Приоритет</span>
          <span className="prio-opt"><button className={`chip${method === 'rate' ? ' on' : ''}`}
            onClick={() => setMethod('rate')}>По ставке</button>
            <Tip label="Пояснение: по ставке">{METHOD_RULE.rate}</Tip></span>
          <span className="prio-opt"><button className={`chip${method === 'size' ? ' on' : ''}`}
            onClick={() => setMethod('size')}>По сумме</button>
            <Tip label="Пояснение: по сумме">{METHOD_RULE.size}</Tip></span>
        </div>
      )}
      {queue.map((d, i) => {
        const item = d as (typeof items)[number];
        const loan = item.loan;
        // Пройденный путь: по остатку не видно, свежий это долг или почти
        // выплаченный, а для человека это разные истории.
        const passed = loan ? progressLabel(loanProgress({ loan, today: state.today, monthsLeft: d.monthsLeft })) : null;
        const extra = loan?.extraMonthly ?? ZERO;
        const open = () => (item.accountId ? onEditCard(item.accountId) : onPrepay(d.id));
        return (
          <div className="row w" key={d.id}>
            <button className="rl plain" onClick={open}>
              {i + 1}. {d.name}{d.card && <span className="tag">кредитка</span>}
              <Tip label={`Почему «${d.name}» на этом месте`}>{debtWhy(d, i, queue, method)}</Tip>
              <span className="rn">{extra > ZERO ? `досрочно по ${formatMoney(extra)} в месяц · ` : ''}{debtReason(d, i, method)}</span>
              {passed && <span className="rn">{passed}</span>}
              {loan?.accrued && loan.accruedSince && (
                <span className="rn">из них проценты {rubR(loan.accrued)}
                  <Tip label={`Проценты по «${d.name}»`}>
                    Основной долг {formatMoney(d.principal)} и проценты, набежавшие с {longDate(loan.accruedSince)},
                    — {formatMoney(loan.accrued)}. Каждый день добавляется ≈ {rubR(loan.accruedPerDay ?? ZERO)};
                    {' '}платёж по графику их закроет.
                  </Tip></span>
              )}
            </button>
            <div className="rv num">{rubR(owed(item))}</div>
            <button className="icobtn sm" onClick={() => (item.accountId ? onEditCard(item.accountId) : onEdit(d.id))}
              aria-label={`Изменить условия: ${d.name}`}><PencilIcon /></button>
          </div>
        );
      })}
    </section>
  );
}

// ──────────────────────────────────────────────────── первичная настройка

function Setup({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('Основная карта');
  const [balance, setBalance] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <Shell>
      <header className="brandbar"><Wordmark /></header>
      <section className="lead">
        <h1>Сколько можно<br />потратить сегодня?
          <Tip>Начнём с одного счёта — этого хватит, чтобы показать первую цифру.
            Обязательные платежи и доход добавим следующим шагом, и цифра станет настоящей.</Tip></h1>
      </section>
      <section className="blk form">
        <label>Название счёта
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Основная карта" /></label>
        <label>Сколько на нём сейчас
          <input
            value={balance} onChange={(e) => setBalance(e.target.value)}
            inputMode="decimal" placeholder="0" autoFocus /></label>
        <button
          className="btn pri" disabled={busy || parseAmountInput(balance) === ZERO}
          onClick={async () => {
            setBusy(true);
            await addAccount({ name: name || 'Основная карта', kind: 'debit', balance: parseAmountInput(balance) });
            onDone();
          }}
        >Продолжить</button>
      </section>
    </Shell>
  );
}

// ─────────────────────────────────────────────────────────── шторки

/**
 * Обёртка обработчика: любая ошибка становится видимой.
 *
 * Раньше отказ внутри onClick превращался в необработанное отклонение обещания —
 * кнопка молча ничего не делала. Для приложения с деньгами это худший вид
 * отказа: пользователь не знает, сохранилось или нет.
 */
export function useAction() {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setError(null); setBusy(true);
    try { await fn(); } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось');
    } finally { setBusy(false); }
  };
  return { run, error, busy };
}

/**
 * Добавление операции: расход, поступление или перевод.
 *
 * Три разных типа, а не «трата с минусом»: перевод между своими счетами
 * капитал не меняет и расходом не является ни в одну сторону (И2), а
 * поступление не бывает отрицательным расходом. Знак задаёт тип операции,
 * сумма всегда положительная.
 */
/**
 * Ввод операции (§5.2).
 *
 * Экран построен вокруг одного действия: набрать сумму и сохранить. Всё
 * остальное — вид операции, категория, счёт, дата — подставлено так, что в
 * обычном случае его не трогают.
 *
 * Клавиатура своя, а не системная. Системная занимает половину экрана,
 * показывает буквы и знаки, которых здесь быть не может, и прячет кнопку
 * сохранения. Своя же умеет то, ради чего её и делали: посчитать. Человек у
 * кассы не знает итога — он знает слагаемые, и без счёта в поле он считает в
 * голове, а ошибка на этом шаге попадает в деньги молча.
 *
 * Вид операции и счёт запоминаются между вызовами: кто ведёт расходы, тот
 * почти всегда вводит расход с той же карты, и переставлять это каждый раз
 * означало бы два лишних касания на каждую запись.
 */
/**
 * Ручная запись сразу. Другие способы заполняют форму и не записывают без подтверждения.
 *
 * Тот же лист правит сохранённую операцию (`edit`, 15.10.2026): отдельная
 * форма правки выглядела иначе и умела меньше. Вкладки работают и при правке:
 * пополнение, записанное расходом, переделывается в приход, перевод — во
 * взнос в цель или погашение долга.
 */
export interface GoalOperationDraft {
  expr: string; accountId: string; toId: string; goalId: string; note: string;
  date: PlainDate; time: string; requestId: string; reopenGoalPicker?: boolean;
}

function AddOperation({
  state, onClose, onSaved, onPlans, prefill, openPhrase, startIncome, edit, onDeleted, startOn, startGoal, draft, onViewGoal,
}: {
  /** С экрана счёта: «Записать» — расход с этого счёта, «Перевести» — перевод с него. */
  startOn?: { accountId: string; type: 'expense' | 'transfer' };
  startGoal?: string;
  draft?: GoalOperationDraft | undefined;
  onViewGoal?: (id: string, draft: GoalOperationDraft) => void;
  state: AppState; onClose: () => void;
  /**
   * Записано.
   *
   * Что именно — важно: после внешнего прихода при включённом стабильном
   * бюджете человеку надо показать, куда эти деньги разложить. Пустой вызов
   * оставил бы его один на один с выросшим «не распределено».
   */
  onSaved: (saved?: { income?: Money }) => void;
  /** Экран Pro — с примером того, что человек хотел сделать. */
  onPlans: (entry?: ProEntry) => void;
  /**
   * Проверенная покупка, перенесённая с экрана проверки.
   *
   * Заполнение, а не запись: «Записать покупку» открывает форму, сохраняет человек.
   * Открывать пустую форму после того, как он уже назвал сумму и дату, —
   * заставлять вводить всё заново.
   */
  prefill?: { amount: Money; date: PlainDate; accountId?: string };
  /** Открыть сразу разбор фразы: способ выбран на предыдущем экране. */
  openPhrase?: boolean;
  /** Записать приход этой зарплаты: сразу «Приход», ожидаемый приход выбран. */
  startIncome?: string;
  /** Сохранённая операция — лист открыт для правки. */
  edit?: Tx;
  /** Операцию удалили из листа правки: экран покажет «Вернуть». */
  onDeleted?: (deleted: DeletedTx) => void;
}) {
  /** С какой вкладки и с каким выбором начинается правка — как было при записи. */
  const [start] = useState(() => (edit ? editStart(state, edit) : null));
  const [categories, setCategories] = useState(state.categories);
  const cats = categories.filter(c => !c.archived && c.kind !== 'income');
  const [incomeCatId, setIncomeCatId] = useState(edit?.type === 'income' && !start?.refund ? edit.categoryId ?? '' : '');
  const [note, setNote] = useState(draft?.note ?? edit?.note ?? '');
  const keyboard = useOperationKeyboard();
  const padKeys = usePadKeys(keyboard.amountRef);
  const [attachment, setAttachment] = useState<'photo' | 'doc' | null>(null);
  /** Файл, выбранный кнопкой «Файл», — лист документа начинает с него. */
  const [docFile, setDocFile] = useState<File | null>(null);
  /** Снимок чека сохранённой траты и открыт ли лист «Чек». */
  const [photo, setPhoto] = useState<string | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const receiptId = edit?.receiptId;
  useEffect(() => {
    if (!receiptId) { setPhoto(null); return; }
    void getReceiptPhoto(receiptId).then((p) => setPhoto(p?.dataUrl ?? null)).catch(() => setPhoto(null));
  }, [receiptId]);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const spendable = state.accounts.filter((a) => !a.archived);
  const today = todayPd();

  // Покупка из советника или проверки — всегда расход, что бы ни записывали
  // в прошлый раз.
  const initialGoal = draft?.goalId ?? startGoal ?? (start?.goalId || savingsNudge({
    incomeFacts: incomeFacts(state.transactions), today: state.today, incomeStreams: state.incomeStreams, goals: state.goals,
  }).asks.find((a) => state.goals.some((g) => g.id === a.goalId && g.status === 'active'))?.goalId
    || state.goals.find((g) => g.status === 'active')?.id || '');
  const [goalRequestId] = useState(() => draft?.requestId ?? newRequestId());
  const [type, setType] = useState<OpType>(() => (draft || startGoal ? 'goal' : start ? start.type : startOn ? startOn.type : prefill ? 'expense' : startIncome !== undefined ? 'income' : recallType()));
  const [expr, setExpr] = useState(() => {
    if (draft) return draft.expr;
    if (edit) return String(edit.amount / 100).replace('.', ',');
    if (prefill) return String(prefill.amount / 100);
    if (startGoal) {
      const nudge = savingsNudge({ incomeFacts: incomeFacts(state.transactions), today: state.today, incomeStreams: state.incomeStreams, goals: state.goals });
      const amount = nudge.asks.find((a) => a.goalId === startGoal)?.amount
        ?? state.goals.find((g) => g.id === startGoal)?.monthlyContribution ?? ZERO;
      return amount > ZERO ? String(amount / 100).replace('.', ',') : '';
    }
    return '';
  });
  const [catId, setCatId] = useState(() => (edit ? (edit.type === 'expense' || start?.refund ? edit.categoryId ?? '' : '')
    : cats.find(c => c.id === recall('skoka.cat', ''))?.id ?? ''));
  const [accId, setAccId] = useState(() => {
    if (draft) return draft.accountId;
    if (edit) return edit.accountId;
    const options = type === 'goal' ? spendable.filter((a) => a.kind !== 'credit_card') : spendable;
    return options.find(a => a.id === (startOn?.accountId ?? prefill?.accountId))?.id
      ?? options.find(a => a.id === recall('skoka.acc', ''))?.id ?? options.find(a => a.isLiquid)?.id ?? options[0]?.id ?? '';
  });
  const [toId, setToId] = useState(() => {
    if (draft) return draft.toId;
    if (edit) return edit.counterAccountId ?? spendable.find((a) => a.id !== accId)?.id ?? '';
    if (type === 'goal') {
      const id = initialGoal;
      const goal = state.goals.find((g) => g.id === id);
      return goal ? goalAccountId(state, goal) ?? '' : '';
    }
    return spendable.find((a) => a.id !== accId)?.id ?? '';
  });
  const [date, setDate] = useState<PlainDate>(draft?.date ?? edit?.occurredDate ?? prefill?.date ?? today);
  const [time, setTime] = useState(() => draft?.time ?? (edit ? edit.occurredAt.slice(11, 16) || '12:00' : new Date().toTimeString().slice(0, 5)));
  /** Календарь под «Когда» раскрыт: клавиатура SKOKA на это время уходит. */
  const [whenOpen, setWhenOpen] = useState(false);
  const [part, setPart] = useState<PlanPart>(edit?.part ?? 'need');
  // У сохранённой операции часть плана уже выбрана — категория её не перебивает.
  const [partExplicit, setPartExplicit] = useState(!!edit?.part);
  /*
    У категории своей части нет («Прочее») — её выбирают сами. Прежде
    оставалась прошлая, обычно «нужды», и такие траты незаметно уходили не
    туда (15.10.2026).
  */
  const [partUnset, setPartUnset] = useState(false);
  const [source, setSource] = useState(start?.source ?? startIncome ?? '');
  /**
   * Поступление — возврат за покупку.
   *
   * Тогда это не доход, а отмена траты: категория — расходная («за что
   * вернули»), и возврат уменьшает траты в ней, а не добавляется к доходам.
   */
  const [refund, setRefund] = useState(start?.refund ?? false);
  const isRefund = type === 'income' && refund;
  /**
   * Ждёт ли человек остаток по частичному приходу.
   *
   * `null` — он ещё не отвечал. Это не то же самое, что «не ждёт»: молча
   * закрыть ожидание значило бы вычеркнуть деньги, на которые он рассчитывает,
   * а молча оставить целое ожидание — посчитать пришедшее дважды.
   */
  const [waitRest, setWaitRest] = useState<boolean | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  // Способ выбран на предыдущем экране — открываем разбор сразу.
  const [phrase, setPhrase] = useState(openPhrase === true);
  const [hint, setHint] = useState<string | null>(null);
  /*
    Категория, созданная по фразе сама. Запись не сохранили или выбрали
    другую — она никому не нужна: убираем, чтобы в справочнике не копились
    следы брошенных записей (как у чеков, где новая создаётся при «Записать»).
  */
  const autoCat = useRef<string | null>(null);
  useEffect(() => () => {
    const id = autoCat.current;
    if (id) void categoryDeletable(id).then((free) => (free ? deleteCategory(id) : undefined)).catch(() => {});
  }, []);
  // Отчёт владельцу: открыл запись — записал (как) или бросил.
  // Время — активное, с открытия листа: по нему видно, где люди «тупят».
  const entry = useRef<{ suspended?: boolean; saved: boolean; method: 'manual' | 'voice' | 'phrase'; from: number }>({ saved: false, method: 'manual', from: activeTime() });
  useEffect(() => {
    // Правка — не новая запись: в отчёте её открытие и уход не считаются.
    if (edit) return;
    track('op_open');
    return () => { if (!entry.current.saved && !entry.current.suspended) track('op_abandoned', { ms: Math.round(activeTime() - entry.current.from) }); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const recorded = (kind: string, method: string = entry.current.method): void => {
    entry.current.saved = true;
    track('op_saved', { kind, method, ms: Math.round(activeTime() - entry.current.from) });
  };
  /*
    «В цель» — взнос: тот же лист, что у перевода, и строка «Цель». Сумма —
    из плана цели, «Куда» — накопительный счёт, если он есть; тот же счёт —
    деньги закрепляются под цель на нём.
  */
  const goals = state.goals.filter((g) => g.status === 'active');
  /** На паузе — в выборе отдельным свёрнутым разделом: отложить в них тоже можно. */
  const pausedGoals = state.goals.filter((g) => g.status === 'paused');
  const goalOptions = [...goals, ...pausedGoals];
  const nudge = useMemo(() => savingsNudge({
    incomeFacts: incomeFacts(state.transactions), today: state.today, incomeStreams: state.incomeStreams, goals: state.goals,
  }), [state]);
  const [goalId, setGoalId] = useState(initialGoal);
  const goalAccounts = spendable.filter((a) => a.kind !== 'credit_card');
  const goalPlanned = (id: string): Money => {
    const g = goals.find((x) => x.id === id);
    return nudge.asks.find((a) => a.goalId === id)?.amount ?? g?.monthlyContribution ?? ZERO;
  };
  // Без известного счёта цели выбор остаётся за человеком.
  const goalTarget = (id: string): string => {
    const g = state.goals.find((x) => x.id === id);
    return g ? goalAccountId(state, g) ?? '' : '';
  };
  /*
    «Долг» — погасить кредит или кредитку (макет 14.10.2026): тот же лист, что
    у перевода, «Куда» — долг. Сумма — платёж по графику, у карты — её долг.
  */
  const debts = useMemo(() => debtOptions(state, date), [state, date]);
  const [debtId, setDebtId] = useState(start?.debtId ?? '');
  const debt = debts.find((o) => o.id === debtId) ?? null;
  /*
    Правка без смены сути — та же операция: меняются сумма, счёт, категория,
    дата, заметка, а состав чека и связи с платежом, зарплатой и целью
    остаются. Сменили тип, цель, долг или ожидаемый приход — вместо прежней
    записывается новая, как при записи.
  */
  const refundPatchable = edit?.type === 'income' && edit.link?.kind === 'income' && !edit.link.incomeStreamId;
  const patching = !!start && type === start.type
    && (type !== 'goal' || (goalId === start.goalId && toId !== accId))
    && (type !== 'debt' || debtId === start.debtId)
    && (type !== 'income' || ((isRefund === start.refund || refundPatchable) && (isRefund || source === start.source)));
  const pickDebt = (id: string) => {
    const next = debts.find((o) => o.id === id);
    setDebtId(id);
    if (next && next.suggested > ZERO && (!expr || value === debt?.suggested)) setExpr(String(next.suggested / 100).replace('.', ','));
  };
  // Цель создали прямо из выбора — её счёт появляется в состоянии на шаг позже.
  // У сохранённого взноса «Куда» уже есть — его не переставляем.
  useEffect(() => {
    if (type === 'goal' && goalId && !patching) setToId((current) => current || goalTarget(goalId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, goalId, state.goals]);
  const pickType = (k: OpType) => {
    setType(k); setSuggestion(null);
    if (k === 'debt') {
      // Платят со своего счёта, не с кредитки.
      const from = goalAccounts.find((a) => a.id === accId) ?? goalAccounts.find((a) => a.isLiquid) ?? goalAccounts[0];
      if (from && from.id !== accId) setAccId(from.id);
      const first = debt ?? debts[0];
      if (first) {
        setDebtId(first.id);
        if (!expr && first.suggested > ZERO) setExpr(String(first.suggested / 100).replace('.', ','));
      }
      return;
    }
    if (k !== 'goal') return;
    // Взнос — со счёта для трат.
    const from = goalAccounts.find((a) => a.id === accId) ?? goalAccounts.find((a) => a.isLiquid) ?? goalAccounts[0];
    if (from && from.id !== accId) setAccId(from.id);
    setToId(from ? goalTarget(goalId) : '');
    if (!expr && goalId && goalPlanned(goalId) > ZERO) setExpr(String(goalPlanned(goalId) / 100).replace('.', ','));
  };
  // Тип операции листается свайпом по листу — как разделы «Денег».
  useSwipeTabs(OP_TABS.map(([k]) => k), type, pickType, { within: '.operation-sheet', coach: 'optabs' });
  const [aiOn, setAiOn] = useState(false);
  const [access, setAccess] = useState<AccessState>(UNKNOWN_ACCESS);
  /**
   * Способ ввода, до которого человек дотянулся, но который ему не открыт.
   *
   * Замок не гасит кнопку: погашенная кнопка ничего не объясняет, и человек
   * второй раз приходит к ней с тем же вопросом. Нажатие называет причину и
   * ведёт к тарифу — но только когда дело в тарифе.
   */
  const [locked, setLocked] = useState<FeatureView | null>(null);

  useEffect(() => { void aiConfigured().then(setAiOn); }, []);
  useEffect(() => { void fetchAccess().then(setAccess); }, []);
  // Включили пробный или оплатили — метки «Pro» снимаются сразу, без перезагрузки.
  useEffect(() => onAccessChanged(() => { void fetchAccess().then(setAccess); }), []);

  // Ручной ввод бесплатен всегда и здесь не спрашивается: разбирает его не
  // модель, а само приложение. Платными остаются три способа, где за человека
  // читает модель, — снимок чека, файл и запись словами.
  const scanView = featureView('receipt_photo', access);
  const docView = featureView('receipt_import', access);
  const phraseView = featureView('phrase_input', access);
  /** Спрашиваем сервер заново — идёт проверка, вместо «Нужна сеть». */
  const [checking, setChecking] = useState(false);
  const tryMode = async (view: FeatureView, go: () => void): Promise<void> => {
    /*
      «Нет сети» здесь значит лишь, что ответа о доступе нет: он ещё не
      пришёл или запрос сорвался. Владелец нажал «Файл» при живой сети и
      получил «Нужна сеть» (15.10.2026). Прежде чем отказать — спросить ещё
      раз; не ответил за 8 секунд — тогда уже «Нужна сеть».
    */
    if (!view.allowed && view.reason === 'offline' && view.feature) {
      const feature = view.feature;
      setLocked(null);
      setChecking(true);
      const fresh = await Promise.race([
        fetchAccess(),
        new Promise<AccessState>((done) => setTimeout(() => done(UNKNOWN_ACCESS), 8000)),
      ]);
      setChecking(false);
      setAccess(fresh);
      view = featureView(feature, fresh);
    }
    if (view.allowed) { setLocked(null); go(); return; }
    track('pro_wall', { feature: view.feature ?? 'unknown', reason: view.reason ?? 'tier' });
    // Закрыто тарифом — сразу экран Pro с примером того, что человек хотел
    // сделать. Заметка остаётся для других причин: кончился объём, нет сети,
    // нужен аккаунт.
    if (view.offerUpgrade) {
      setLocked(null);
      onPlans(entryOf(view));
      return;
    }
    setLocked(view);
  };

  useEffect(() => {
    if (partExplicit) return;
    const c = categories.find((x) => x.id === catId);
    if (c?.defaultPart) { setPart(c.defaultPart); setPartUnset(false); } else setPartUnset(!!c);
  }, [catId, categories, partExplicit]);

  const calc = useMemo(() => evalAmount(expr), [expr]);
  const value = calc.value !== null && calc.value > 0 ? (calc.value as Money) : ZERO;
  const toAccount = state.accounts.find((a) => a.id === toId);
  const isCardPayment = type === 'transfer' && toAccount?.kind === 'credit_card';
  /** Названный источник — это один из доходов человека или разовые деньги. */
  const stream = state.incomeStreams.find((x) => x.isActive && x.id === source);
  // По сроку уже пришла часть и ждут остаток — «часть» и «Жду …» от него,
  // а не от всей зарплаты: вторая часть 20 000 из ожидаемых 50 000 — «Жду 30 000».
  const pending = type === 'income' && stream ? pendingRest(state, stream.id, date) : null;
  const partial = pending !== null ? value > ZERO && value < pending : partialIncome(stream, value);
  // Расход похож на оплату неоплаченного платежа («ЖКХ» рядом по сроку) —
  // засчитываем его оплатой, если человек не выключит строку под суммой.
  // Сумма любая: отдали больше — лишнее в счёт следующего, меньше — часть.
  // При правке не предлагаем: связь могли выключить при записи, и тихо
  // засчитать трату оплатой из-за правки заметки было бы нечестно.
  const payFor = type === 'expense' && catId && !patching ? paymentFor(state, { date, categoryId: catId, note, amount: value }, { anyAmount: true }) : null;
  const paySplit = payFor ? splitPayment(state, payFor.obligationId, payFor.scheduledDate, value) : null;
  const [asPayment, setAsPayment] = useState(true);
  const account = state.accounts.find((a) => a.id === accId);
  // Приход датой раньше сверки уже сидит в названном остатке. У сохранённой
  // операции это уже решено при записи — об этом своя строка ниже.
  const inBalance = type === 'income' && !patching && account?.balanceAsOf !== undefined
    && date < account.balanceAsOf;

  const tap = (k: string) => {
    setErr(null); setFlash(false);
    if (k === '⌫') { setExpr((p) => p.slice(0, -1)); return; }
    if (k === ',' && /[,.][^+\-*/]*$/.test(expr)) return; // вторая запятая в числе
    setExpr((p) => p + k);
  };

  /**
   * Записать новую операцию — при правке на место прежней.
   *
   * Прежняя удаляется, новая пишется тем же путём, что при записи: так
   * взнос в цель, платёж по долгу и приход зарплаты получают свои связи, а
   * прежние — снимаются. Не вышло записать — прежняя возвращается как была.
   */
  const replace = async (work: () => Promise<void>): Promise<void> => {
    if (!edit) { await work(); return; }
    const removed = await deleteTransaction(edit.id);
    try { await work(); } catch (e) { if (removed) await restoreTransaction(removed); throw e; }
  };
  /** Записано: отчёт владельцу и закрытие листа. */
  const done = (kind: string, saved?: { income?: Money }) => {
    if (edit) { track('op_edited', { retyped: true }); onSaved(); return; }
    recorded(kind);
    onSaved(saved);
  };
  const fail = (e: unknown) => { setErr(e instanceof Error ? e.message : 'Не удалось сохранить'); setBusy(false); };

  /**
   * Правка без смены сути: та же операция с новыми полями.
   *
   * Хранилище откатывает прежнее действие и применяет новое, а не вычисляет
   * разницу. Счёт и категория, ушедшие в архив после записи, остаются
   * допустимыми: правка заметки не должна требовать сменить их.
   */
  const patch = async (saved: Tx) => {
    const own = (id: string) => spendable.some((a) => a.id === id) || id === saved.accountId || id === saved.counterAccountId;
    if (!own(accId) || ((type === 'transfer' || type === 'goal') && (!own(toId) || toId === accId))) { setErr('Выбери действующие счета'); return; }
    const categoryId = type === 'income' && !isRefund ? incomeCatId : catId;
    const kind = isRefund ? 'expense' : type;
    if ((type === 'expense' || type === 'income') && categoryId !== (saved.categoryId ?? '')
      && !categories.some(c => c.id === categoryId && !c.archived && (c.kind ?? 'expense') === kind)) { setErr(isRefund ? 'Выбери, за что вернули' : 'Выбери категорию'); return; }
    setErr(null); setBusy(true);
    try {
      await updateTransaction(saved.id, {
        amount: value, accountId: accId, date, time, note: note.trim(),
        ...((type === 'expense' || type === 'income') && categoryId ? { categoryId } : {}),
        ...(type === 'expense' || isRefund ? { part } : {}),
        ...(type === 'income' && refundPatchable ? { refund } : {}),
        ...((type === 'transfer' || type === 'goal') && toId ? { counterAccountId: toId } : {}),
      });
      track('op_edited');
      onSaved();
    } catch (e) { fail(e); }
  };

  const save = async () => {
    if (busy) return;
    // Подсветка суммы одна ничего не говорит: человек жмёт ещё раз.
    if (value === ZERO) { setFlash(true); setErr('Введи сумму'); return; }
    if ((type === 'expense' || isRefund) && partUnset) { setErr('Выбери часть плана: повседневные нужды или свободные траты'); return; }
    if (patching && edit) { await patch(edit); return; }
    if (type === 'goal') {
      if (!goalOptions.some((g) => g.id === goalId)) { setErr('Выбери цель'); return; }
      if (!goalAccounts.some((a) => a.id === accId) || !goalAccounts.some((a) => a.id === toId)) { setErr('Выбери действующие счета'); return; }
      setErr(null); setBusy(true);
      try {
        await replace(async () => {
          // Тот же взнос, что «Отложил»: перевод с привязкой к цели, а на том же
          // счёте — закреплённые под цель деньги.
          await fundGoal({
            goalId, amount: value, cycleStart: nudge.cycleStart ?? today, requestId: goalRequestId,
            fromAccountId: accId, toAccountId: toId, date, time,
            ...(note.trim() ? { note: note.trim() } : {}),
          });
        });
        track('goal_funded');
        done('goal');
      } catch (e) { fail(e); }
      return;
    }
    if (type === 'debt') {
      if (!debt) { setErr('Выбери долг'); return; }
      if (!goalAccounts.some((a) => a.id === accId)) { setErr('Выбери действующий счёт'); return; }
      const split = splitDebt(debt, value, date, today);
      if (split.blocked) { setErr(split.blocked); return; }
      setErr(null); setBusy(true);
      try {
        await replace(async () => {
          const said = note.trim();
          if (debt.kind === 'card') {
            // Как «Записать погашение»: перевод на карту, долг по ней уменьшается.
            await addTransfer({ fromId: accId, toId: debt.account!.id, amount: value, date, time, ...(said ? { note: said } : {}) });
            return;
          }
          // Платёж по графику — как «Оплачено» в «Платежах»; сверх него — досрочно.
          if (split.payment > ZERO && debt.due) {
            await settleObligation({
              obligationId: debt.due.obligationId, dueDate: debt.due.scheduledDate, amount: split.payment,
              paidDate: date, accountId: accId, time, requestId: newRequestId(), ...(said ? { note: said } : {}),
            });
          }
          if (split.extra > ZERO) {
            // Что сделал банк — как в прошлый раз в «Досрочно»; не выбирали — сократил срок.
            await addLoanPrepayment({ loanId: debt.loan!.id, accountId: accId, amount: split.extra, date, time, ...(said ? { note: said } : {}),
              ...(debt.loan!.prepayMode ? { mode: debt.loan!.prepayMode } : {}) });
          }
        });
        done('debt');
      } catch (e) { fail(e); }
      return;
    }
    if (type === 'income' && partial && waitRest === null) return;
    if (suggestion) { setErr('Подтверди новую категорию или выбери существующую'); return; }
    if (!spendable.some(a => a.id === accId) || (type === 'transfer' && (!spendable.some(a => a.id === toId) || toId === accId))) { setErr('Выбери действующие счета'); return; }
    const categoryId = type === 'income' && !isRefund ? incomeCatId : catId;
    const kind = isRefund ? 'expense' : type;
    if ((type === 'expense' || type === 'income') && !categories.some(c => c.id === categoryId && !c.archived && (c.kind ?? 'expense') === kind)) { setErr(isRefund ? 'Выбери, за что вернули' : 'Выбери категорию'); return; }
    if (type === 'expense' && payFor && asPayment && paySplit?.loan && paySplit.prepay > ZERO && !paySplit.loan.allowsEarly) {
      setErr(`По договору досрочно гасить нельзя — впиши не больше ${formatMoney(paySplit.first.take)} или выключи «В счёт платежа».`); return;
    }
    setErr(null); setBusy(true);
    try {
      await replace(async () => {
        const when = { date, time, ...(note.trim() ? { note: note.trim() } : {}) };
        if (type === 'expense') {
          // Платёж по кредиту и сверх него: лишнее — досрочно, отдельной
          // операцией, как во вкладке «Долг» (21.10.2026).
          const early = payFor && asPayment && paySplit?.loan && paySplit.prepay > ZERO ? paySplit : null;
          const tx = await addExpense({ accountId: accId, amount: early ? early.first.take : value, categoryId: catId, part, ...when });
          if (payFor && asPayment) await linkPaymentTx(tx.id, payFor.obligationId, payFor.scheduledDate);
          if (early && payFor) {
            await addLoanPrepayment({
              loanId: early.loan!.id, accountId: accId, amount: early.prepay, date, time, envelopeOf: payFor.obligationId,
              ...(note.trim() ? { note: note.trim() } : {}),
              ...(early.loan!.prepayMode ? { mode: early.loan!.prepayMode } : {}),
            });
          }
        } else if (isRefund) {
          await addIncomeTx({
            accountId: accId, amount: value, source: 'Возврат', categoryId: catId, part, incomeKind: 'refund', ...when,
          });
        } else if (type === 'income') {
          await addIncomeTx({
            accountId: accId, amount: value, source: stream?.name ?? categories.find(c => c.id === incomeCatId)?.name ?? 'Поступление', categoryId: incomeCatId, ...when,
            // Связь ставится, только если человек назвал именно свой доход.
            // Подарок и продажа ничей прогноз не закрывают и регулярными не
            // становятся: приложение не вправе решать за него, что это зарплата.
            ...(stream ? { incomeStreamId: stream.id } : {}),
            // Остаток ждут — ожидание остаётся, но уменьшенное. Не ждут — ноль,
            // и это ответ, а не отсутствие ответа. Часть покрыла ожидаемый
            // остаток — тоже ноль: больше по сроку не ждут.
            ...(() => {
              const rest = stream ? restToRecord(stream, value, pending, waitRest) : undefined;
              return rest !== undefined ? { expectedRest: rest } : {};
            })(),
          });
        } else {
          await addTransfer({ fromId: accId, toId, amount: value, ...when });
        }
      });
      // Правка — не привычка ввода: запоминаем только новые записи.
      if (!edit) remember(type, catId, accId);
      if (type === 'expense' && payFor && asPayment) track('payment_settled', { via: 'expense' });
      // Возврат раскладывать не нужно: это не новые деньги, а вернувшиеся с траты.
      done(isRefund ? 'refund' : type, type === 'income' && !isRefund ? { income: value } : undefined);
    } catch (e) { fail(e); }
  };

  /** Удалить из листа правки — сразу, с «Вернуть» на экране. */
  const removeSaved = async () => {
    if (!edit || busy) return;
    setBusy(true);
    try {
      const deleted = await deleteTransaction(edit.id);
      track('op_deleted', { via: 'sheet' });
      if (deleted && onDeleted) onDeleted(deleted); else onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Не удалось удалить'); setBusy(false); }
  };

  /** Знак перед итогом: у перевода его нет — деньги не убыли и не прибыли. */
  const sign = type === 'expense' ? '− ' : type === 'income' ? '+ ' : '';
  const sameGoalAccount = type === 'goal' && !!toId && toId === accId;
  // Сохранённый платёж уже сидит в графике долга: расклад «платёж + досрочно»
  // от него посчитался бы неверно.
  const debtSplit = type === 'debt' && debt && !patching ? splitDebt(debt, value, date, today) : null;
  /** Два счёта рядом: перевод, взнос в цель и погашение долга. */
  const paired = type === 'transfer' || type === 'goal' || type === 'debt';
  /** Состав чека есть только у расхода: сменили тип — он не сохранится. */
  const items = edit?.items ?? [];

  padKeys.current = tap;

  return (
    <SheetWrap title={edit ? 'Правка операции' : 'Новая операция'} onClose={onClose} bare operationHeight={keyboard.height} keyboardInset={keyboard.keyboardInset} typing={keyboard.typing}>
      {!edit && coachWanted(state.dismissedHints, SWIPE_OPTABS_HINT) && (
        <SwipeCoach kind="optabs" title="Листай пальцем"
          text="По листу — вид операции: Расход, Приход, Перевод, В цель, Долг. По карточке счёта — другой счёт."
          onDone={() => void coachSeen(SWIPE_OPTABS_HINT)} />
      )}
      <div className="opbar">
        <button className="opclose" onClick={onClose} aria-label="Закрыть"><CloseX /></button>
        {/* Вид — значками в овале, как нижнее меню; выбранный — со словом (макет 27.10.2026). */}
        <OpTypeSwitch types={OP_TABS} value={type} onPick={pickType} />
      </div>

      <div className="opcontent">
        {/*
          Сначала — откуда деньги, как у перевода в банке: счёт карточкой,
          листается пальцем, тап — прежний список счетов. У перевода, «В цель»
          и «Долга» под ним — вторая карточка: счёт, цель или долг.
        */}
        <div className={`opcards${paired ? ' pair' : ''}`}>
          <AccountCard caption={type === 'income' ? 'Зачислить на' : paired ? 'Со счёта' : 'Списать с'}
            pickerTitle={type === 'income' ? 'Зачислить на счёт' : type === 'transfer' ? 'Перевести со счёта' : type === 'goal' ? 'Отложить со счёта' : type === 'debt' ? 'Платить со счёта' : 'Списать со счёта'}
            accounts={type === 'goal' || type === 'debt' ? goalAccounts : spendable} value={accId}
            onChange={v => { setAccId(v); if (v === toId && type !== 'debt' && type !== 'goal') setToId(spendable.find(x => x.id !== v)?.id ?? ''); }} />
          {type === 'transfer' && <>
            <button className="opswap" aria-label="Поменять счета местами" onClick={() => { setAccId(toId); setToId(accId); }}><Icon name="arrow-up-arrow-down" size={12} /></button>
            <AccountCard caption="На счёт" pickerTitle="Перевести на счёт"
              accounts={spendable.filter(a => a.id !== accId)} value={toId} onChange={setToId} />
          </>}
          {type === 'debt' && <DebtField options={debts} value={debtId} onChange={pickDebt} />}
          {/* «В цель»: вторая карточка — цель; счёт берётся из цели («Где копим»). */}
          {type === 'goal' && <>
          <span className="opswap goal-direction" aria-hidden="true"><Icon name="arrow-down" size={16} /></span>
          <GoalField card goals={goals} paused={pausedGoals} value={goalId}
            reopen={draft?.reopenGoalPicker}
            onView={onViewGoal ? (id) => { entry.current.suspended = true; onViewGoal(id, { expr, accountId: accId, toId, goalId, note, date, time, requestId: goalRequestId }); } : undefined}
            onResume={(id) => updateGoal(id, { status: 'active' })}
            onChange={(id) => {
              setGoalId(id);
              if (id !== goalId) setToId(goalTarget(id));
              if (goalPlanned(id) > ZERO && (!expr || value === goalPlanned(goalId))) setExpr(String(goalPlanned(id) / 100).replace('.', ','));
            }} /></>}
        </div>
        {type === 'goal' && <AccountField label="Где отложил" pickerTitle="Где отложил" accounts={goalAccounts} value={toId} onChange={setToId} />}
        {sameGoalAccount && (
            <div className="note">Деньги останутся на «{toAccount?.name}» и будут отложены на цель. В «Можно тратить» они не попадут.</div>
          )}
          {type === 'goal' && !sameGoalAccount && toAccount && goalOptions.some((g) => g.id === goalId) && (
            <div className="note">Учтём перевод на «{toAccount.name}» как взнос в цель.</div>
          )}
          {debtSplit && (debtSplit.blocked
            ? <div className="note warn">{debtSplit.blocked}</div>
            : <div className="note">{debtSplit.note}</div>)}
        {/*
          Сумма — по центру, с воздухом (макет 27.10.2026): итог крупно, под
          ним мелко — что набрано, с курсором, как в банке (28.10.2026). Тап
          возвращает ввод к сумме, как раньше.
        */}
        <button ref={keyboard.amountRef} type="button" aria-label="Сумма операции" className={`opsum center${flash ? ' flash' : ''}`} onClick={() => { setFlash(false); keyboard.returnToAmount(); }}>
          <div className={`optotal num${type === 'income' ? ' inc' : ''}`}>
            {calc.error === 'divzero' ? 'на ноль не делится'
              : calc.error === 'overflow' ? 'слишком много'
              : `${sign}${formatMoney(value)}`}
          </div>
          <div className="opexpr">
            {expr ? groupExpr(expr) : '\u00a0'}
            {!keyboard.editingNote && <i className="opcaret" />}
          </div>
        </button>
        {type === 'goal' && goalOptions.find((g) => g.id === goalId) && (
          <p className="unified-plan">Ежемесячный взнос — {formatMoney(goalOptions.find((g) => g.id === goalId)!.monthlyContribution)}.</p>
        )}

        {/*
          Связь с платежом — сразу под суммой, к которой она относится, а не под
          клавиатурой. И не пропадает, когда сумма не та: пишет, как разойдутся
          деньги (16.10.2026).
        */}
        {payFor && paySplit && (
          <label className={`payln li sw${asPayment ? '' : ' off'}`}>
            <span className="payln-ico" aria-hidden="true"><Icon name="calendar-check" size={16} /></span>
            <span className="nm"><b>В счёт платежа «{payFor.label}»</b>
              <span>{asPayment ? splitLine(paySplit, today, value) : 'выключено — обычный расход, платёж ждёт оплаты'}</span></span>
            <input type="checkbox" role="switch" checked={asPayment} onChange={(e) => setAsPayment(e.target.checked)} />
            <span className="swbox" aria-hidden="true" />
          </label>
        )}
        {/* Категория — частыми плитками, «Все» — прежний выбор с поиском и деревом. */}
        {(type === 'expense' || type === 'income') && <QuickCategories facts={state.transactions} categories={categories} kind={type === 'income' && !isRefund ? 'income' : 'expense'}
            value={type === 'income' && !isRefund ? incomeCatId : catId} label={isRefund ? 'За что вернули' : 'Категория'}
            isNew={!!autoCat.current && autoCat.current === (type === 'income' && !isRefund ? incomeCatId : catId)}
            onUpdated={setCategories} onChange={id => { if (type === 'income' && !isRefund) setIncomeCatId(id); else { setCatId(id); setPartExplicit(false); } setSuggestion(null); }} />}
        {/*
          Платёж по кредиту, записанный тратой, съедает «Можно тратить» и не
          уменьшает долг (28.10.2026): категория с «кредит» в названии — повод
          подсказать вкладку «Долг».
        */}
        {type === 'expense' && !isRefund && !edit && /кредит|ипотек|рассрочк|займ/i.test(categories.find((c) => c.id === catId)?.name ?? '') && (
          <div className="note opdebt-hint">
            <span>Похоже на платёж по кредиту. Во вкладке «Долг» он уменьшит долг и не посчитается тратой.</span>
            <button type="button" className="lnk" onClick={() => pickType('debt')}>Записать как долг</button>
          </div>
        )}
        <div className="oprows opv2">
          {/* Когда и часть плана (у прихода — ожидаемый приход) — в один ряд во всю ширину. */}
          <div className={`opchips${type === 'expense' || type === 'income' ? '' : ' single'}`}>
            <WhenField open={whenOpen} onOpen={setWhenOpen} label="Дата и время операции"
            text={`${date === today ? 'Сегодня' : dayTitle(date)}, ${time}`}
            date={date} time={time} max={today} onDate={(d) => setDate(pd(d))} onTime={setTime} />
            {(type === 'expense' || isRefund) && <PickRow label="Часть плана" tip={PART_TIP} warn={partUnset} empty="Выбрать"
            value={partUnset ? '' : part} onChange={v => { setPart(v as PlanPart); setPartExplicit(true); setPartUnset(false); if (partUnset) setErr(null); }}
            options={[...PART_OPTIONS]} lead={PART_QUESTION} sheetTip={PART_TIP_SHEET} />}
            {type === 'income' && !isRefund && <PickRow label="Ожидаемый приход" value={source} onChange={v => { setSource(v); setWaitRest(null); }} options={incomeStreamOptions(state)} />}
          </div>
          {/* Категория «при записи»: часть не подставлена — сказать, что выбрать
              и почему; в плашке на это нет места (02.11.2026). */}
          {(type === 'expense' || isRefund) && partUnset && (
            <p className="note opparthint">«{categories.find((c) => c.id === catId)?.name ?? 'Эта категория'}» бывает по-разному: выбери, нужда это или свободная трата.</p>
          )}
          {type === 'income' && (
            <label className="oprow checkrow">
              <span>Это возврат за покупку</span>
              <input type="checkbox" checked={refund}
                onChange={(e) => { setRefund(e.target.checked); setSource(''); setWaitRest(null); setErr(null); }} />
            </label>
          )}
          {type === 'income' && !isRefund && !patching && <IncomeLink stream={stream} value={value} waitRest={waitRest} onWaitRest={setWaitRest} pending={pending} />}
          {inBalance && (
            <div className="note">
              Остаток счёта назван {formatDate(account!.balanceAsOf!)} — эти деньги в нём уже
              учтены, поэтому баланс не изменится.
              <Tip>Запись нужна, чтобы доход был виден в истории и в прогнозе.</Tip>
            </div>
          )}
          {/*
            Заметка — поле во всю ширину строки, и подпись ему не нужна: слово
            «Заметка» стоит плейсхолдером внутри и исчезает, как только человек
            начал писать.
          */}
          <NoteField value={note} onChange={setNote} inputRef={keyboard.noteRef} onFocus={keyboard.focusNote} onBlur={keyboard.blurNote} onEnter={keyboard.returnToAmount} />
          {/* Чек сохранённой траты — строкой формы: состав и снимок открываются листом. */}
          {patching && type === 'expense' && (items.length > 0 || photo) && (
            <button className="oprow categoryfield" onClick={() => setReceiptOpen(true)}>
              <span>Чек</span>
              <span className="category-value">
                <span className="catvalue-name">{items.length > 0 ? plural(items.length, 'позиция', 'позиции', 'позиций') : 'Снимок'}</span>
                <span className="catvalue-chevron" aria-hidden="true"><Icon name="chevron-right" size={13} /></span>
              </span>
            </button>
          )}
        </div>

        {patching && edit?.balanceApplied === false && (
          <div className="note">Эта операция не меняла остаток: он уже учитывал её, когда была названа сумма на счёте. Правка суммы остаток тоже не тронет.</div>
        )}
        {edit && !patching && type !== 'expense' && items.length > 0 && (
          <div className="note">Состав чека не сохранится — он бывает только у расхода.</div>
        )}

        {isCardPayment && (
          <div className="note">
            Погашение кредитки — перевод, а не новая трата: расход был в момент покупки.
          </div>
        )}
        {suggestion && <div className="note warn"><b>Создать категорию «{suggestion}»?</b>
          <div className="categorytools"><button className="lnk" disabled={busy} onClick={async () => {
            setBusy(true); try { const c = await saveCategory({ name: suggestion, kind: type === 'income' ? 'income' : 'expense', defaultPart: part });
              setCategories(prev => [...prev, c]); if (type === 'income') setIncomeCatId(c.id); else setCatId(c.id); setSuggestion(null);
            } catch(e) { setErr(e instanceof Error ? e.message : 'Не удалось создать'); } finally { setBusy(false); }
          }}>Создать и выбрать</button><button className="lnk" onClick={() => { setSuggestion(null); setHint('Выбери существующую категорию'); }}>Выбрать существующую</button></div>
        </div>}
        {hint && <div className="note warn">{hint}</div>}
        {err && <div className="note warn" role="alert">{err}</div>}

      </div>

      {/*
        Низ формы — своей панелью (28.10.2026): способы ввода, клавиатура и
        кнопка записи видны всегда, а листается только то, что над ними.
        Раньше листалась вся форма, и на невысоком экране кнопка закрывала
        нижние ряды клавиатуры.
      */}
      <div className="optray">
        {checking && <div className="note" role="status">Проверяю связь…</div>}
        {locked && (
          <div className="note">
            <b>{locked.message}</b>
            {/* Про ручной ввод говорим только тогда, когда закрыт тариф. При
                кончившейся квоте это уже сказано в самом сообщении, и повтор
                звучал бы как отговорка. */}
            {locked.offerUpgrade && (
              <> Снимок, файл и фразу за человека читает модель — это платная
                часть. Ручная запись открыта всегда: сумма, категория и дата уже
                на экране.</>
            )}
            {locked.offerUpgrade && (
              <div className="notefoot">
                <button className="lnk" onClick={() => onPlans(entryOf(locked))}>Что даёт Pro</button>
              </div>
            )}
            {/* Гостю нужен аккаунт, а не деньги: пробный и Pro живут на аккаунте. */}
            {locked.offerSignup && (
              <div className="notefoot">
                <button className="lnk" onClick={() => navigate('signup')}>Создать аккаунт — бесплатно</button>
              </div>
            )}
          </div>
        )}
        {/* Способы ввода (28.10.2026): три овальные кнопки — «Вручную» нет,
            лист и есть ручной ввод. Чек, файл и голос открываются поверх и
            возвращают сюда. При правке их нет: чек или фраза записали бы
            новую операцию, а не поправили эту. */}
        {!edit && <Collapse open={!whenOpen} className="modesbox"><div className="opmodes pills">
          <button className="opmode" onClick={() => { void tryMode(scanView, () => setAttachment('photo')); }} aria-label="Сканировать чек">
            <ScanIcon /><span>Чек</span>
            {scanView.badge && <b className="lockmark">{scanView.badge}</b>}
          </button>
          {/* «Файл» сразу открывает системный выбор — фото, камера, файлы:
              без промежуточного листа с одной кнопкой «Выбрать файл». */}
          {docView.allowed ? (
            <label className="opmode file" aria-label="Загрузить файл">
              <DocIcon /><span>Файл</span>
              <input type="file" accept={DOC_ACCEPT} onChange={(e) => {
                const picked = e.target.files?.[0];
                // Тот же файл можно выбрать ещё раз — например, после отмены.
                e.target.value = '';
                if (!picked) return;
                setLocked(null); setDocFile(picked); setAttachment('doc');
              }} />
            </label>
          ) : (
            <button className="opmode" onClick={() => { void tryMode(docView, () => setAttachment('doc')); }} aria-label="Загрузить файл">
              <DocIcon /><span>Файл</span>
              {docView.badge && <b className="lockmark">{docView.badge}</b>}
            </button>
          )}
          <button className="opmode" onClick={() => { void tryMode(phraseView, () => setPhrase(true)); }}
            disabled={!aiOn} aria-label="Записать голосом">
            <VoiceIcon /><span>Голосом</span>
            {phraseView.badge && <b className="lockmark">{phraseView.badge}</b>}
          </button>
        </div></Collapse>}

        {/* Пока раскрыт календарь «Когда», клавиатура плавно уходит и даёт ему место. */}
        {!keyboard.editingNote && <Collapse open={!whenOpen} className={edit ? 'padbox bottom' : 'padbox'}><div className="pad">
          {['7', '8', '9', '÷', '4', '5', '6', '×', '1', '2', '3', '−', ',', '0', '⌫', '+'].map((k) => (
            <button key={k} className={/[÷×−+]/.test(k) ? 'padop' : k === '⌫' ? 'paddel' : ''}
              aria-label={k === '⌫' ? 'Стереть' : k} onClick={() => tap(k === '÷' ? '/' : k === '×' ? '*' : k === '−' ? '-' : k)}>{/[÷×−+]/.test(k) ? <ArithmeticIcon symbol={k} /> : k === '⌫' ? <Icon name="delete-left" size={20} /> : k}</button>
          ))}
        </div></Collapse>}

        {/* Частичный приход без ответа не записывается: «ждёт» и «не ждёт»
            дают разный прогноз, и выбрать за человека нельзя. */}
        {(() => {
          const asking = type === 'income' && partial && waitRest === null && !patching;
          const button = (
        <button className="btn pri" data-pad-enter disabled={busy || !!suggestion || asking || !!debtSplit?.blocked || (type === 'goal' && (!goalOptions.some((g) => g.id === goalId) || !goalAccounts.some((a) => a.id === accId) || !goalAccounts.some((a) => a.id === toId)))}
          onClick={() => void save()}>
          {busy ? 'Сохраняю…'
            : asking ? 'Ответь про остаток'
            : edit ? 'Сохранить'
            : type === 'expense' ? 'Записать расход'
            : isRefund ? 'Записать возврат'
            : type === 'income' ? 'Записать приход'
            : type === 'goal' ? (sameGoalAccount ? 'Закрепить под цель' : 'Отложить в цель')
            : type === 'debt' ? (debt?.kind === 'card' ? 'Погасить' : 'Записать платёж')
            : isCardPayment ? 'Записать погашение' : 'Записать перевод'}
        </button>
          );
          // Удалить — рядом с «Сохранить»: под пальцем, а не в шапке, где
          // теснятся пять вкладок. Удаляет сразу, на экране — «Вернуть».
          return edit ? (
            <div className="opfoot">
              <button className="opdel" disabled={busy} onClick={() => void removeSaved()} aria-label="Удалить операцию">
                <Icon name="trash-can" size={19} />
              </button>
              {button}
            </div>
          ) : button;
        })()}
      </div>

      {receiptOpen && edit && (
        <SheetWrap title="Чек" onClose={() => setReceiptOpen(false)}>
          {items.length > 0 && (
            <>
              <div className="blkhead">
                <b>Состав
                  <Tip>Состав остаётся как есть: правка суммы его не пересчитывает. Поправить
                    позицию внутри уже сохранённой траты пока нельзя.</Tip></b>
                <span className="tag">{items.length}</span>
              </div>
              <div className="blk">
                {items.map((i, n) => (
                  <ImportRow key={`${i.name}-${n}`} title={i.name} amount={formatMoney(i.sum)}
                    {...(i.qty !== 1 ? { sub: `${String(i.qty).replace('.', ',')} × ${formatMoney(i.price)}` } : {})} />
                ))}
              </div>
            </>
          )}
          {photo && (
            <>
              <div className="blkhead" style={{ marginTop: 8 }}><b>Снимок чека
                <Tip>Снимок лежит только на этом устройстве и на сервер не уходит: один чек
                  весит сотни килобайт, и возить их в каждом сохранении значило бы
                  сломать синхронизацию ради архива.</Tip></b></div>
              <a className="shot" href={photo} target="_blank" rel="noreferrer">
                <img src={photo} alt="Снимок чека" loading="lazy" />
              </a>
            </>
          )}
        </SheetWrap>
      )}
      {attachment === 'photo' && <ScanReceipt initialAccountId={accId} state={{ ...state, categories }} onClose={() => { setAttachment(null); void loadState().then(s => setCategories(s.categories)); }} onSaved={() => { recorded('expense', 'scan'); onSaved(); }} onFile={(selected) => { setAccId(selected); setAttachment('doc'); }} />}
      {attachment === 'doc' && <ReceiptDoc initialAccountId={accId} state={{ ...state, categories }} {...(docFile ? { file: docFile } : {})} onClose={() => { setAttachment(null); setDocFile(null); void loadState().then(s => setCategories(s.categories)); }} onManual={() => { setAttachment(null); setDocFile(null); void loadState().then(s => setCategories(s.categories)); }} onPlans={() => onPlans('file')} onSaved={() => { recorded('expense', 'file'); onSaved(); }} />}
      {phrase && (
        <VoiceSheet categories={categories} accounts={spendable} today={today} example={PHRASE_EXAMPLE[type]} onClose={() => setPhrase(false)}
          onParsed={(r, h, said, byVoice) => {
            setPhrase(false);
            setHint(h);
            // «Проверь и запиши» — плашкой: строкой в форме она мешала и после прочтения.
            if (!h) flashMessage(`Заполнено по ${byVoice ? 'голосу' : 'фразе'} — проверь и запиши`);
            entry.current.method = byVoice ? 'voice' : 'phrase';
            if (r.type !== 'unknown') setType(r.type);
            if (r.amount !== null) setExpr(String(r.amount / 100).replace('.', ','));
            /*
              Счёт — тот, что назван во фразе: «кофе 300 с Т-Банка»
              (22.10.2026). Не назван — остаётся последний, как при ручной
              записи. У перевода — и откуда, и куда.
            */
            const from = spendable.find((a) => a.id === r.account);
            if (from) setAccId(from.id);
            const fromId = from?.id ?? accId;
            if (r.type === 'transfer') {
              const to = spendable.find((a) => a.id === r.toAccount && a.id !== fromId);
              if (to) setToId(to.id);
              else if (toId === fromId) setToId(spendable.find((a) => a.id !== fromId)?.id ?? '');
            }
            const found = categories.find(c => c.id === r.category && !c.archived && (c.kind ?? 'expense') === r.type);
            if (r.type === 'income') setIncomeCatId(found?.id ?? '');
            if (r.type === 'expense') setCatId(found?.id ?? '');
            /*
              Подходящей категории нет — по той же настройке, что у чеков и
              выписок: «Создавать автоматически» — создаём и выбираем, с
              пометкой «новая»; «Класть в Прочее» — в «Прочее»; иначе
              спрашиваем, как раньше.
            */
            const name = !found && (r.type === 'expense' || r.type === 'income') ? r.categorySuggestion ?? null : null;
            const other = r.type === 'expense' ? categories.find((c) => c.id === 'other' && !c.archived) : undefined;
            if (name && state.newCategoryMode === 'auto') {
              setSuggestion(null);
              const kind = r.type === 'income' ? 'income' : 'expense';
              void saveCategory({ name, kind, defaultPart: kind === 'expense' ? r.part ?? 'need' : null }).then((c) => {
                autoCat.current = c.id;
                setCategories((prev) => [...prev, c]);
                if (kind === 'income') setIncomeCatId(c.id); else setCatId(c.id);
              }).catch(() => setSuggestion(name));
            } else if (name && state.newCategoryMode === 'other' && other) {
              setSuggestion(null);
              setCatId(other.id);
            } else setSuggestion(name);
            // Заметка — словами человека: по ней операцию узнают в списке.
            // Раньше в неё шло только название магазина, а без него она
            // оставалась пустой. Начатую заметку не трогаем.
            setNote(previous => previous || said || r.merchant || '');
            // Часть плана задаёт категория, как при ручной записи. Догадка
            // модели нужна только смешанной категории — или когда категории нет.
            if (r.part && !found?.defaultPart) { setPart(r.part); setPartExplicit(true); }
            if (r.date) setDate(r.date);
          }} />
      )}
    </SheetWrap>
  );
}

/** С чего начинается правка: вкладка и выбор в ней. */
interface EditStart { type: OpType; goalId: string; debtId: string; source: string; refund: boolean }

/**
 * С какой вкладки открыть правку сохранённой операции — той, с которой её
 * записали бы. Взнос в цель — «В цель», платёж и досрочный по кредиту —
 * «Долг», пока долг не погашен: погашенный не из чего выбрать, и такой
 * платёж правится как расход. Погашение кредитки — «Перевод»: так оно и
 * хранится.
 */
function editStart(state: AppState, tx: Tx): EditStart {
  const base = { goalId: '', debtId: '', source: '', refund: false };
  const link = tx.link;
  if (tx.type === 'transfer') return link?.kind === 'goal' ? { ...base, type: 'goal', goalId: link.goalId } : { ...base, type: 'transfer' };
  if (tx.type === 'income') {
    const income = link?.kind === 'income' ? link : undefined;
    return { ...base, type: 'income', source: income?.incomeStreamId ?? '', refund: income?.incomeKind === 'refund' };
  }
  const loanId = link?.kind === 'loan' ? link.loanId
    : link?.kind === 'obligation' ? state.loans.find((l) => l.obligationId === link.obligationId)?.id : undefined;
  const debtId = loanId ? `loan:${loanId}` : '';
  if (debtId && debtOptions(state, tx.occurredDate).some((o) => o.id === debtId)) return { ...base, type: 'debt', debtId };
  return { ...base, type: 'expense' };
}

/** Вкладки записи: тип, подпись, иконка (макет 14.10.2026). */
const OP_TABS: ReadonlyArray<readonly [OpType, string, string]> = [
  // Расход — вверх (ушло), приход — вниз (пришло): выбор владельца 27.10.2026.
  ['expense', 'Расход', 'arrow-up'],
  ['income', 'Приход', 'arrow-down'],
  ['transfer', 'Перевод', 'arrow-right-arrow-left'],
  ['goal', 'В цель', 'bullseye-arrow'],
  ['debt', 'Долг', 'hand-holding-dollar'],
];


/**
 * Описать операцию словами — отдельным листом.
 *
 * Здесь нужна системная клавиатура с буквами, а на основном экране её нет и
 * быть не должно: там своя, числовая. Два способа ввода на одном экране
 * дрались бы за низ экрана.
 */
/** Пример фразы — под тип операции: «аптека 260» над поступлением сбивал. */
const PHRASE_EXAMPLE: Record<OpType, string> = {
  expense: 'кофе 350 с карты',
  income: 'зарплата 85 000',
  transfer: 'на накопительный 5 000',
  goal: 'отложил 20 000 на отпуск',
  debt: 'заплатил ипотеку 36 000',
};

const CheckIcon = () => <Icon name="check" size={18} />;
const ScanIcon = () => <Icon name="camera-viewfinder" size={17} />;
/** Документ: электронный чек приходит файлом, а не снимком. */
const DocIcon = () => <Icon name="file-lines" size={17} />;
const VoiceIcon = () => <Icon name="microphone" size={17} />;

/** Значок цели: у подушки — зонт, у остальных — копилка. */
const glyphOf = (g: AppState['goals'][number]): string => (g.kind === 'emergency_fund' ? 'umbrella' : 'bullseye-arrow');
const colorOfGoal = (g: AppState['goals'][number]): CategoryColor => (g.kind === 'emergency_fund' ? 'blue' : 'green');

/** Выбор цели во взносе: карточка «Куда» или строка как у категории, лист — как у счёта, и «+ Новая цель». */
function GoalField({ goals, paused = [], value, onChange, onResume, card = false, onView, reopen }: {
  goals: AppState['goals']; value: string; onChange: (id: string) => void;
  onView?: ((id: string) => void) | undefined; reopen?: boolean | undefined;
  /** Цели на паузе — свёрнутым разделом «На паузе». */
  paused?: AppState['goals'];
  /** Возобновить цель с паузы — спрашиваем, когда её выбрали. */
  onResume?: (id: string) => Promise<void>;
  /** Карточкой «Куда» рядом с «Откуда» — в «В цель». */
  card?: boolean;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (reopen) setOpen(true); }, [reopen]);
  const [creating, setCreating] = useState(false);
  const [showPaused, setShowPaused] = useState(false);
  const [ask, setAsk] = useState<AppState['goals'][number] | null>(null);
  /** Возобновлённые здесь же: до перечитывания они ещё числятся на паузе. */
  const [resumed, setResumed] = useState<Set<string>>(new Set());
  const create = useContext(CreateGoalContext);
  const active = [...goals, ...paused.filter((g) => resumed.has(g.id))];
  const stillPaused = paused.filter((g) => !resumed.has(g.id));
  const current = [...active, ...stillPaused].find((g) => g.id === value);
  const pct = (g: AppState['goals'][number]) => (g.targetAmount > ZERO ? Math.min(100, Math.round((g.currentAmount / g.targetAmount) * 100)) : 0);
  const goalIndex = Math.max(0, active.findIndex((g) => g.id === value));
  return (
    <>
      {card ? (
        // Цель — карточкой, как счёт (27.10.2026): листается между целями, тап — список.
        <SwipeCard index={goalIndex} count={active.length}
          tone={(i) => { const g = i === goalIndex ? current : active[i]; return g ? colorOfGoal(g) : 'gray'; }}
          label={`В цель: ${current?.name ?? 'выбрать цель'}`} onTap={() => setOpen(true)}
          onStep={(d) => { const next = active[active.findIndex((g) => g.id === value) + d]; if (next) onChange(next.id); }}
          render={(i) => {
            const g = i === goalIndex ? current : active[i];
            return <CardRow glyph={g ? <CategoryGlyph icon={glyphOf(g)} color={colorOfGoal(g)} />
              : <span className="catico c-gray"><Icon name="bullseye-arrow" size={19} /></span>}
              caption="В цель" name={g?.name ?? 'Выбрать цель'}
              {...(g ? { sub: `${formatMoney(g.currentAmount)} из ${formatMoney(g.targetAmount)}` } : {})} />;
          }} />
      ) : (
      <button className="oprow categoryfield" onClick={() => setOpen(true)}>
        <span>Цель</span>
        <span className="category-value">
          {current ? <><CategoryGlyph icon={glyphOf(current)} color={colorOfGoal(current)} size="s" /><span className="catvalue-name">{current.name} · {pct(current)}%</span></> : 'Выбрать'}
          <span className="catvalue-chevron" aria-hidden="true"><Icon name="chevron-right" size={13} /></span>
        </span>
      </button>
      )}
      {open && (
        <SheetWrap title="В какую цель" onClose={() => setOpen(false)}>
          <div className="fieldlist account-list">
            {active.length === 0 && stillPaused.length > 0 && (
              <p className="note">Сейчас ни на что не копишь — все цели на паузе. Возобнови одну из них или заведи новую.</p>
            )}
            {active.map((g) => (
              <div className="goal-choice-row" key={g.id}>
                <button className="li w" aria-pressed={g.id === value} onClick={() => { onChange(g.id); setOpen(false); }}>
                  <CategoryGlyph icon={glyphOf(g)} color={colorOfGoal(g)} size="s" />
                  <span className="nm"><span className="account-name">{g.name}</span>
                    <span>{formatMoney(g.currentAmount)} из {formatMoney(g.targetAmount)} · {pct(g)}%</span></span>
                  <span>{g.id === value ? <Icon name="check" size={14} /> : null}</span>
                </button>
                {onView && <button className="goal-look" onClick={() => { setOpen(false); onView(g.id); }} aria-label={`О цели «${g.name}»`}>О цели <Icon name="chevron-right" size={13} /></button>}
              </div>
            ))}
            {active.length === 0 && stillPaused.length === 0 && <p className="note">Целей пока нет — заведи первую.</p>}
            {/* Нужной цели нет — заводим здесь же, не бросая взнос. Выше «На паузе»: это чаще нужно. */}
            {create && (
              <button className="li w newrow" onClick={() => { setOpen(false); setCreating(true); }}>
                <CategoryGlyph icon="plus" color="gray" size="s" />
                <span className="nm"><span className="account-name">Новая цель</span></span>
              </button>
            )}
            {/* На паузе — свёрнуто: не мешают выбору, но под рукой. */}
            {stillPaused.length > 0 && (
              <button className="li w pausedhead" onClick={() => setShowPaused((v) => !v)} aria-expanded={showPaused}>
                <span className="nm"><span className="account-name">На паузе · {stillPaused.length}</span></span>
                <span><Icon name={showPaused ? 'chevron-up' : 'chevron-down'} size={13} /></span>
              </button>
            )}
            {showPaused && stillPaused.map((g) => (
              <button className="li w" key={g.id} aria-pressed={g.id === value}
                onClick={() => { setOpen(false); if (onResume) setAsk(g); else onChange(g.id); }}>
                <CategoryGlyph icon="pause" color="gray" size="s" />
                <span className="nm"><span className="account-name">{g.name}</span>
                  <span>на паузе · {formatMoney(g.currentAmount)} из {formatMoney(g.targetAmount)}</span></span>
                <span>{g.id === value ? <Icon name="check" size={14} /> : null}</span>
              </button>
            ))}
          </div>
        </SheetWrap>
      )}
      {creating && create?.((id) => { setCreating(false); if (id) onChange(id); })}
      {/* Выбрали цель с паузы — спрашиваем, возобновить ли. Отложить можно и без этого. */}
      {ask && (
        <SheetWrap title="Цель на паузе" onClose={() => setAsk(null)}>
          <p className="asktext">«{ask.name}» сейчас на паузе. Возобновить? Тогда SKOKA снова будет просить взнос с каждого прихода.</p>
          <button className="btn pri" onClick={async () => {
            const g = ask; setAsk(null);
            await onResume?.(g.id);
            setResumed((p) => new Set(p).add(g.id));
            onChange(g.id);
          }}>Возобновить</button>
          <button className="btn" onClick={() => { const g = ask; setAsk(null); onChange(g.id); }}>Только отложить</button>
        </SheetWrap>
      )}
    </>
  );
}

/** Значок долга по виду: кредит банка, рассрочка, личный долг. У кредитки — значок её счёта. */
const DebtGlyph = ({ o, size = 'm' }: { o: DebtOption; size?: 'f' | 's' | 'm' }) => (o.account
  ? <AccountGlyph account={o.account} size={size} />
  : <CategoryGlyph size={size}
    icon={o.loan?.kind === 'installment' ? 'cart-shopping' : o.loan?.kind === 'private' ? 'user' : 'building-columns'}
    color={o.loan?.kind === 'installment' ? 'amber' : o.loan?.kind === 'private' ? 'gray' : 'blue'} />);

/**
 * «Куда» у «Долга»: карточка как у счёта в переводе, выбор — как у счёта,
 * группами «Кредитки» и «Кредиты», и «+ Новый долг» (макет 14.10.2026).
 */
function DebtField({ options, value, onChange }: {
  options: DebtOption[]; value: string; onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const create = useContext(CreateDebtContext);
  const current = options.find((o) => o.id === value);
  const debtIndex = Math.max(0, options.findIndex((o) => o.id === value));
  const groups = ([['card', 'Кредитки'], ['loan', 'Кредиты']] as const)
    .map(([kind, title]) => ({ kind, title, list: options.filter((o) => o.kind === kind) }))
    .filter((g) => g.list.length > 0);
  const owed = (o: DebtOption): Money => (o.kind === 'card' ? o.suggested : o.loan?.principal ?? ZERO);
  const row = (o: DebtOption) => (
    <button className="li w" key={o.id} aria-pressed={o.id === value} onClick={() => { onChange(o.id); setOpen(false); }}>
      <DebtGlyph o={o} size="s" />
      <span className="nm"><span className="account-name">{o.name}</span><span>{o.line}</span></span>
      <span>{o.id === value ? <Icon name="check" size={14} /> : null}</span>
    </button>
  );
  return (
    <>
      {/* Долг — карточкой, как счёт (27.10.2026): листается между долгами, тап — список. */}
      <SwipeCard index={debtIndex} count={options.length}
        tone={(i) => { const o = i === debtIndex ? current : options[i]; return o?.kind === 'card' && o.account ? accountColor(o.account) : o ? 'violet' : 'gray'; }}
        label={`В погашение: ${current?.name ?? 'выбрать долг'}`} onTap={() => setOpen(true)}
        onStep={(d) => { const next = options[options.findIndex((o) => o.id === value) + d]; if (next) onChange(next.id); }}
        render={(i) => {
          const o = i === debtIndex ? current : options[i];
          return <CardRow glyph={o ? <DebtGlyph o={o} /> : <span className="catico c-gray"><Icon name="hand-holding-dollar" size={19} /></span>}
            caption="В погашение" name={o?.name ?? 'Выбрать долг'} {...(o ? { sub: o.left } : {})} />;
        }} />
      {open && (
        <SheetWrap title="Какой долг" onClose={() => setOpen(false)}>
          <div className="fieldlist account-list">
            {groups.map((g) => (
              <div key={g.kind}>
                <div className="ops-group grp"><span>{g.title}</span>
                  <span className="num">{formatMoney(sub(ZERO, add(...g.list.map(owed))))}</span></div>
                {g.list.map(row)}
              </div>
            ))}
            {options.length === 0 && <p className="note">Долгов пока нет — заведи первый.</p>}
            {/* Нужного долга нет — заводим здесь же, не бросая запись. */}
            {create && (
              <button className="li w newrow" onClick={() => { setOpen(false); setCreating(true); }}>
                <CategoryGlyph icon="plus" color="gray" size="s" />
                <span className="nm"><span className="account-name">Новый долг</span></span>
              </button>
            )}
          </div>
        </SheetWrap>
      )}
      {creating && create?.((id) => { setCreating(false); if (id) onChange(`loan:${id}`); })}
    </>
  );
}

/**
 * Новый счёт — тот же лист, что в настройке (`account-form.tsx`): вид
 * переключателем, кредитка со всеми полями, «Тратим отсюда» у накопительного
 * выключен (прежде он включался сам, 22.10.2026).
 */
function AddAccount({ onClose, onSaved }: { onClose: () => void; onSaved: (account?: AppState['accounts'][number]) => void }) {
  return (
    <AccountSheet init={newAccount(KIND_NAME.debit, 'debit')} isNew onClose={onClose}
      onSave={async (v) => { const { account } = await createAccount(v); onSaved(account); }} />
  );
}


/**
 * Правка кредитки целиком: и счёт, и условия.
 *
 * Раньше условия задавались только при создании, а потом менять их было
 * негде — при том что лимит, дата платежа и сумма для грейса меняются
 * каждый месяц. Без правки вся логика беспроцентного периода устаревала
 * на следующий же цикл.
 */
function EditCard({ state, result, accountId, onClose, onSaved, onAdjust }: {
  state: AppState; result: AvailableResult; accountId: string; onClose: () => void; onSaved: () => void;
  /** Долг по карте — корректировкой, а не полем: так он виден в операциях. */
  onAdjust: (id: string) => void;
}) {
  const acc = state.accounts.find((a) => a.id === accountId);
  const terms = state.creditTerms.find((t) => t.accountId === accountId);
  const st = result.credit.find((c) => c.accountId === accountId);
  const cardLoan = state.loans.find((l) => l.kind === 'credit_card' && l.card?.accountId === accountId);
  // Ставка живёт в кредите карты; у старой карты его нет — поле пустое.
  const [rate, setRate] = useState(cardLoan && cardLoan.ratePct > 0 ? String(cardLoan.ratePct).replace('.', ',') : '');
  const [name, setName] = useState(acc?.name ?? '');
  const [icon, setIcon] = useState(acc ? accountIcon(acc) : 'credit-card-front');
  const [color, setColor] = useState<CategoryColor>(acc ? accountColor(acc) : 'violet');
  // Суммы — с разрядами: «134400» в поле читается хуже, чем «134 400».
  const [limit, setLimit] = useState(terms ? formatAmountInput(String(terms.limit / 100)) : '');
  const [needGrace, setNeedGrace] = useState(terms ? formatAmountInput(String(terms.statementDebt / 100)) : '');
  const [payDay, setPayDay] = useState(String(terms?.paymentDay ?? 10));
  const [stmtDay, setStmtDay] = useState(terms?.statementDay ? String(terms.statementDay) : '');
  const [grace, setGrace] = useState(String(terms?.graceDays ?? 55));
  const [paid, setPaid] = useState(terms ? formatAmountInput(String(terms.reserved / 100)) : '');
  const [minPct, setMinPct] = useState(
    terms?.minPaymentPct !== undefined ? String(terms.minPaymentPct * 100) : '',
  );
  const [minFloor, setMinFloor] = useState(
    terms?.minPaymentFloor !== undefined ? formatAmountInput(String(terms.minPaymentFloor / 100)) : '',
  );
  if (!acc) return null;

  const debtV = kopAbs(acc.balance);
  const needV = parseAmountInput(needGrace);
  const paidV = parseAmountInput(paid);

  return (
    <SheetWrap title={acc.name} onClose={onClose} className="rcpt">
      <div className="rcform">
        {/*
          Что с картой сегодня — сверху и цифрами с основанием. Это переехало
          сюда из развёрнутого блока карты на «Счетах»: там теперь одна строка.
        */}
        {((st && st.needForGrace > ZERO && !st.settled) || (st && st.minPayment !== null && st.minPayment > ZERO)) && (
          <div className="rccard rcsel">
            {st && st.needForGrace > ZERO && !st.settled && (
              <div className="rcselrow"><span className="rclabel">{st.missing > ZERO ? 'Не хватает' : 'Деньги на платёж отложены'}</span>
                <span className="rcvalue"><span className="rcvalcol">
                  <span className={`rcvaltext num${st.missing > ZERO ? ' bad' : ''}`}>
                    {st.missing > ZERO ? formatMoney(st.missing) : <Icon name="check" size={14} />}</span>
                  <small>{formatMoney(st.needForGrace)} по выписке · внесено {formatMoney(st.reserved)} · платёж {st.daysToPayment === 0
                    ? 'сегодня' : formatDate(st.paymentDate)}</small></span></span></div>
            )}
            {st && st.minPayment !== null && st.minPayment > ZERO && (
              <div className="rcselrow"><span className="rclabel">Минимальный платёж</span>
                <span className="rcvalue"><span className="rcvalcol">
                  <span className="rcvaltext num">{formatMoney(st.minPayment)}</span>
                  <small>если не внести всю сумму по выписке</small></span></span></div>
            )}
          </div>
        )}

        {/* Долг здесь не правится: он меняется корректировкой, с записью в
            операциях. Прежде поле долга перезаписывало остаток молча. */}
        <BigAmount label="Долг" readOnly={formatMoney(debtV)} right={<CheckedOn state={state} account={acc} />} />
        <button type="button" className="rcview more" onClick={() => onAdjust(acc.id)}>
          <Icon name="scale-balanced" size={13} /> Скорректировать</button>

        {/* Два разных вопроса — что с картой сейчас и какие у неё условия.
            Вперемешку они читались одной длинной анкетой. */}
        <CardHead>Сейчас по карте</CardHead>
        <div className="rccard rcsel">
          <CardMoney label="Внести для беспроцентного" value={needGrace} onChange={setNeedGrace}
            placeholder={formatAmountInput(String(Math.abs(acc.balance) / 100))}
            tip="Сумма из выписки. Пока выписки нет, ставь весь текущий долг." />
          <CardMoney label="Уже внесено после выписки" value={paid} onChange={setPaid}
            tip="Только то, что ушло со счёта на карту. Деньги, которые лежат под этот платёж на другом счёте, сюда не пиши." />
        </div>
        {/* Здесь было жёлтое «У накопительного счёта включено «Тратим отсюда»… посчитаны дважды»:
            оно горело при любой внесённой сумме и любом таком счёте, к этой карте отношения не имея.
            Деньги под карту на других счетах теперь конверты — они считаются один раз (03.11.2026). */}

        <CardHead>Условия карты</CardHead>
        <div className="rccard rcsel">
          <CardMoney label="Кредитный лимит" value={limit} onChange={setLimit} />
          <CardDays label="До какого числа платёж" value={[clampDay(Number(payDay))]} sub="каждый месяц"
            onChange={(ds) => setPayDay(String(ds[0] ?? 10))} />
          <CardDays label="Какого числа выписка" empty="если знаешь"
            value={stmtDay.trim() ? [clampDay(Number(stmtDay))] : []}
            onChange={(ds) => setStmtDay(ds[0] ? String(ds[0]) : '')}
            tip="Это другое число, не день платежа: у карты с платежом до 9-го выписка может приходить 16-го." />
          <GraceRows value={grace} onChange={setGrace} />
          <CardInput label="Ставка" value={rate} onChange={setRate} unit="% годовых" inputMode="decimal"
            placeholder="39" width={44}
            tip="Сколько банк возьмёт, если беспроцентный период сорвётся. По ней «Долги» покажут, во что обойдётся карта и где она в очереди гашения." />
          <CardInput label="Минимальный платёж" value={minPct} onChange={setMinPct} unit="% от долга"
            inputMode="decimal" placeholder="5" width={36}
            tip="Смотри в тарифе карты или в выписке. Пока не заполнено, минимальный платёж в план не входит." />
          <CardMoney label="но не меньше" value={minFloor} onChange={setMinFloor} placeholder="если есть" />
        </div>

        <div className="rccard rcsel">
          <CardInput label="Название" value={name} onChange={setName} width={170} />
          <CardLook icon={icon} color={color} onChange={(i, c) => { setIcon(i); setColor(c); }} />
        </div>

        <button className="btn pri"
          onClick={async () => {
            await updateAccount(accountId, {
              name: name.trim() || acc.name,
              icon: icon === accountIcon({ kind: 'credit_card' }) ? '' : icon,
              color: bankOf(icon) || color === accountColor({ kind: 'credit_card' }) ? '' : color,
            });
            // Пустое поле — это «не знаю», и таким оно и уезжает в хранилище:
            // подставленное значение стало бы условием, которого банк не ставил.
            const pct = minPct.trim() === '' ? null : Number(minPct.replace(',', '.'));
            const floor = minFloor.trim() === '' ? null : parseAmountInput(minFloor);
            await upsertCreditTerms(accountId, {
              limit: parseAmountInput(limit),
              statementDebt: needV > ZERO ? needV : debtV,
              reserved: paidV,
              paymentDay: clampDay(Number(payDay)),
              ...(stmtDay.trim() === '' ? {} : { statementDay: clampDay(Number(stmtDay)) }),
              graceDays: Math.max(0, Math.trunc(Number(grace) || 0)),
              ...(pct !== null && Number.isFinite(pct) && pct > 0 ? { minPaymentPct: pct / 100 } : {}),
              ...(floor !== null && floor > ZERO ? { minPaymentFloor: floor } : {}),
            });
            const r = parseRateInput(rate);
            if (r !== null && r !== (cardLoan?.ratePct ?? null)) await setCardRate(accountId, r);
            onSaved();
          }}>Сохранить</button>
        <button className="textact"
          onClick={async () => {
            if (confirm('Архивировать кредитку? Операции по ней останутся, вернуть её можно из «Архива».')) {
              await archiveAccount(accountId); onSaved();
            }
          }}>Архивировать кредитку</button>
      </div>
    </SheetWrap>
  );
}

/** Правка отложенного под платёж: без неё предупреждение висит вечно. */
function SetReserve({ state, accountId, onClose, onSaved }: {
  state: AppState; accountId: string; onClose: () => void; onSaved: () => void;
}) {
  const terms = state.creditTerms.find((t) => t.accountId === accountId);
  const acc = state.accounts.find((a) => a.id === accountId);
  const [val, setVal] = useState(terms ? String(terms.reserved / 100) : '');
  if (!terms || !acc) return null;
  const need = terms.statementDebt;
  const now = parseAmountInput(val);
  /*
    Конверты под эту карту на других счетах — тоже отложенное (02.11.2026):
    без них лист писал «не хватает 105 061 ₽», а главная — 55 950 ₽.
  */
  const enveloped = add(ZERO, ...backedReserves({ accounts: state.accounts, reserves: state.reserves, obligations: state.obligations, today: state.today })
    .filter((r) => r.forKind === 'credit' && r.forId === accountId).map((r) => r.amount));

  return (
    <SheetWrap title="Отложено на платёж" onClose={onClose}>
      <div className="form">
        <div className="note">
          Для сохранения беспроцентного периода нужно внести {formatMoney(need)}.
        </div>
        <label>Сколько уже внесено после выписки
          <Tip>Внесённое вычитается из недобора, а не из доступного повторно.</Tip>
          <input value={val} onChange={(e) => setVal(e.target.value)} inputMode="decimal" autoFocus /></label>
        <div className="note">
          {enveloped > ZERO && <>Ещё отложено в конвертах: {formatMoney(enveloped)}. </>}
          {add(now, enveloped) >= need
            ? 'Резерв собран — предупреждение уйдёт с главной.'
            : `Не хватает ${formatMoney(sub(need, add(now, enveloped)))}.`}
        </div>
        <button className="btn pri"
          onClick={async () => { await setCreditReserved(accountId, now); onSaved(); }}>
          Сохранить
        </button>
      </div>
    </SheetWrap>
  );
}


/**
 * Подтверждение взноса в цель.
 *
 * Приложение не распоряжается деньгами: перевод делает человек, здесь он
 * только записывает то, что уже сделал. Поэтому спрашивается и сумма (в этом
 * месяце могла случиться покупка), и куда деньги легли — от этого зависит,
 * ушли они из «Доступно» переводом или их надо удержать конвертом.
 */

/**
 * Выбор главной задачи.
 *
 * Приложение считает порядок само, но человек может знать про свою жизнь то,
 * чего нет в цифрах: что через месяц выходить из декрета или что долг родителям
 * важнее ставки. Поэтому выбор остаётся за ним — а причина рядом остаётся
 * посчитанной, а не подогнанной под выбор.
 */
/**
 * Почему это главное.
 *
 * Прежде здесь стоял список задач, из которого человек выбирал главную сам.
 * Выбор не влиял ни на план, ни на распределение дохода — только на надпись
 * на карточке, — но выглядел как настройка приоритетов и потому спорил с
 * важностью целей, которую человек уже задал в самих целях. Теперь порядок
 * считается всегда, а экран объясняет его и показывает, где им управляют.
 *
 * Важность цели не может поднять её выше безопасности: сначала то, без чего
 * месяц не сходится, и только потом то, чего человек хочет.
 */
function WhySheet({ state, onClose, onGoals }: {
  state: AppState; onClose: () => void; onGoals: (id: string) => void;
}) {
  const goals: Goal[] = orderGoals(state.goals.filter((g) => g.status === 'active'));

  return (
    <SheetWrap title="Почему это главное" onClose={onClose}>
      <div className="pick"><div className="k">Порядок задач
        <Tip>SKOKA сам выбирает, что показать, и порядок всегда один — от того, без
          чего месяц не сходится, к тому, чего ты хочешь.</Tip></div></div>
      {[
        ['Дожить до прихода', 'Свободных денег меньше, чем нужно повседневные нужды до следующего дохода'],
        ['Покрыть обязательные платежи', 'Ипотека, кредит, садик — то, что нельзя не заплатить'],
        ['Не попасть на проценты', 'Грейс по карте и просрочки дороже любых накоплений'],
        ['Закрыть дорогой долг', 'Ставка по нему выше, чем доход от любых сбережений'],
        ['Собрать запас на месяц', 'Разница между неприятностью и катастрофой'],
        ['Цели', 'Когда всё обязательное закрыто — то, ради чего копишь'],
      ].map(([title, why], i) => (
        <div className="row" key={title}>
          <div className="rl"><b>{i + 1}. {title}</b><span className="rn">{why}</span></div>
        </div>
      ))}

      <div className="pick"><div className="k">Важность твоих целей
        <Tip>Важность задаётся в самой цели — там же, где её сумма и срок. Поднять
          цель выше безопасности она не может: пока не хватает на обязательное,
          главным останется оно.</Tip></div></div>
      {goals.length === 0 ? (
        <div className="note">Целей пока нет. Появятся — встанут в очередь по важности.</div>
      ) : (
        goals.map((g) => (
          <button className="li w" key={g.id} onClick={() => onGoals(g.id)}>
            <div className="nm"><b>{g.name}</b>
              <span>{GOAL_PRIORITY[g.priority] ?? g.priority}</span></div>
            <div className="rt"><Icon name="chevron-right" size={13} /></div>
          </button>
        ))
      )}
    </SheetWrap>
  );
}

const GOAL_PRIORITY: Record<string, string> = {
  critical: 'Критическая важность',
  high: 'Высокая важность',
  medium: 'Средняя важность',
  low: 'Низкая важность',
};

/**
 * Новая цель — тот же лист, что в настройке (`goal-form.tsx`).
 *
 * «Закрыть долг» среди целей нет. Цель копит деньги у человека, а досрочный
 * платёж уходит банку: такая цель показывала «накоплено», пока остаток долга
 * стоял на месте. Досрочное — в самом долге: калькулятор в «Долгах».
 */
function AddGoal({ state, preset, onClose, onSaved }: {
  state: AppState; onClose: () => void; onSaved: (goalId?: string) => void;
  preset?: { name: string; kind: 'emergency_fund' | 'purchase' | 'debt_payoff' };
}) {
  // Подушка одна: вторую движки не видят — берут первую.
  const hasBuffer = state.goals.some((g) => g.kind === 'emergency_fund' && g.status !== 'cancelled');
  const own = savingAccounts(state.accounts);
  // Подушка — три месяца обязательных трат, как в настройке.
  const monthlyNeed = add(
    state.estimates.need.known ? state.estimates.need.value : ZERO,
    ...state.obligations.filter((o) => o.isActive).map((o) => o.amount),
  );
  // Цель копит из дохода: начинает с ближайшей зарплаты, а не из остатка
  // недожитого месяца. Прежде цель из приложения просила деньги сразу.
  const firstIncome = nextIncomeDate(state.incomeStreams.filter((x) => x.isActive), state.today) ?? addDays(state.today, 1);
  return (
    <GoalSheet init={{ ...newGoal(preset?.name ?? '', preset?.kind ?? 'purchase'), accountId: defaultGoalAccount(state.accounts) || own[0]?.id || '' }}
      isNew accounts={own} hasBuffer={hasBuffer}
      {...(monthlyNeed > ZERO ? {
        buffer: {
          target: formatAmountInput(String(share(monthlyNeed, 3) / 100)),
          text: `3 месяца обязательных трат: ${formatMoney(monthlyNeed)} × 3`,
        },
      } : {})}
      startText="с ближайшей зарплаты" startSub={formatDate(firstIncome)} onClose={onClose}
      onSave={async (v) => {
        const goalAccount = own.find((a) => a.id === v.accountId);
        const current = parseAmountInput(v.current);
        const goal = await addGoal({
          name: v.name, kind: v.kind === 'debt_payoff' ? 'purchase' : v.kind, priority: v.priority,
          targetAmount: parseAmountInput(v.target),
          currentAmount: current,
          monthlyContribution: parseAmountInput(v.monthly),
          ...(/^\d{4}-\d{2}-\d{2}$/.test(v.date) ? { targetDate: v.date as PlainDate } : {}),
          startDate: (/^\d{4}-\d{2}-\d{2}$/.test(v.start) ? v.start : firstIncome) as PlainDate,
          ...(goalAccount ? { accountId: goalAccount.id } : {}),
        });
        // Накопленное на карте для трат закрепляется под цель — иначе те же
        // деньги посчитаются и в «Можно тратить».
        if (goalAccount?.isLiquid && current > ZERO) {
          await setReserve({ accountId: goalAccount.id, forKind: 'goal', forId: goal.id, amount: current });
        }
        onSaved(goal.id);
      }} />
  );
}





/**
 * Вход и регистрация.
 *
 * Аккаунт нужен только для переноса данных между устройствами и защиты от
 * чистки браузера. Без него приложение работает целиком — это принципиально:
 * просить регистрацию до того, как продукт что-то дал, самая дорогая просьба.
 */
function Auth({ onClose, onDone, onLater, onDocs, mode: initial = 'register', saved }: {
  onClose: () => void; onDone: (u: User) => void;
  /** С какой вкладки открыть: зависит от того, откуда человек пришёл. */
  mode?: 'login' | 'register';
  /**
   * Человек пришёл сюда сразу после принятия плана.
   *
   * Тогда экран обязан сказать главное: план уже сохранён на устройстве, и
   * отказ от аккаунта его не отменяет. Иначе регистрация читается как условие
   * доступа к собственной работе.
   */
  saved?: boolean;
  /** Продолжить без аккаунта. Появляется только после неудачной попытки. */
  onLater?: () => void;
  /** Открыть документы — до создания аккаунта, а не после. */
  onDocs?: () => void;
}) {
  const [mode, setMode] = useState<'login' | 'register'>(initial);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  /** Была неудачная попытка: тогда виден выход «без аккаунта». */
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  /*
   * «Забыли пароль?» — только если сервер умеет отправлять письма: кнопка,
   * за которой ничего не происходит, хуже её отсутствия.
   */
  const [resetOn, setResetOn] = useState(false);
  const [forgot, setForgot] = useState(false);
  useEffect(() => { void passwordResetAvailable().then(setResetOn); }, []);
  /*
   * Вкладки — две разные задачи: «неверный пароль» входа не должен висеть
   * над формой регистрации.
   */
  const switchMode = (next: 'login' | 'register') => { setMode(next); setErr(null); };
  /**
   * Согласия. Ни одно не отмечено заранее — в том числе необязательное.
   *
   * Обязательные (условия и обработка данных) спрашиваются отдельно от
   * необязательного: связывать их в одну отметку значит получать согласие на
   * рекламу в обмен на возможность пользоваться приложением.
   */
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      const u = mode === 'register'
        ? await apiRegister(email.trim(), password)
        : await apiLogin(email.trim(), password);
      if (mode === 'register') {
        // Записи согласий делаются после успешной регистрации: до неё
        // соглашаться было не с чем, аккаунта нет. Они уедут на сервер
        // ближайшим снимком — вместе с версией, временем и способом.
        for (const [kind, granted] of [
          ['terms', agreeTerms], ['privacy', agreePrivacy], ['marketing', agreeMarketing],
        ] as const) {
          await recordConsent({
            kind, documentVersion: DOC_VERSION, method: 'checkbox', granted,
          });
        }
        // Согласие на рассылку — и на сервер: оттуда придёт письмо «Присылать новости SKOKA?».
        if (agreeMarketing) void setMarketing(true, DOC_VERSION).catch(() => {});
      }
      onDone(u);
    } catch (e) {
      // Ошибка регистрации ничего не отменяет: план и черновик лежат на
      // устройстве, человек остаётся на этом экране со своими данными.
      setErr(e instanceof Error ? e.message : 'Не получилось');
      setFailed(true);
      setBusy(false);
    }
  };

  if (forgot) {
    return <ForgotPassword email={email} onBack={() => setForgot(false)} onClose={onClose} />;
  }

  return (
    <SheetWrap title={mode === 'register' ? 'Создать аккаунт' : 'Войти'} onClose={onClose}
      tip={saved
        ? 'План уже сохранён на этом устройстве и никуда не денется. Аккаунт нужен, чтобы '
          + 'открыть его с другого устройства и не потерять при чистке браузера — создать '
          + 'его можно в профиле в любой момент.'
        : undefined}>
      <div className="segs">
        <button className={`seg${mode === 'register' ? ' on' : ''}`} aria-pressed={mode === 'register'} onClick={() => switchMode('register')}>Регистрация</button>
        <button className={`seg${mode === 'login' ? ' on' : ''}`} aria-pressed={mode === 'login'} onClick={() => switchMode('login')}>Вход</button>
      </div>
      <div className="form">
        {saved && (
          <div className="okline">
            План сохранён на этом устройстве. Аккаунт нужен, чтобы он не пропал
            вместе с браузером.
          </div>
        )}
        <label>Почта
          <input value={email} onChange={(e) => setEmail(e.target.value)}
            type="email" inputMode="email" autoComplete="email" autoFocus /></label>
        <label>Пароль
          {mode === 'register' && (
            <Tip>Данные нужны только для переноса между устройствами:
              все расчёты как и раньше идут на твоём устройстве.</Tip>
          )}
          <PasswordField value={password} onChange={setPassword}
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'} /></label>
        {/* Требование формы — на виду: кнопка не нажимается, пока оно не выполнено. */}
        {mode === 'register' && <div className="note">Не короче восьми знаков.</div>}
        {/*
          Согласия — одной плотной группой (22.10.2026). Обязательные помечены
          звёздочкой, а документ открывается прямо из строки: соглашаться, не
          прочитав, — это не согласие. Ссылка — кнопка внутри подписи: тап по
          ней открывает документ, а не ставит галочку.
        */}
        {mode === 'register' && (
          <div className="consents">
            <label className="check">
              <input type="checkbox" checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)} />
              {/* SKOKA — для взрослых (решение владельца 22.10.2026): подписка с автопродлением,
                  финансовые данные в ИИ и рекламные письма с подростком упираются в согласие
                  родителей, а его не проверить. Возраст — в той же отметке, без лишнего шага. */}
              <span>Мне есть 18 лет, принимаю <DocLink onDocs={onDocs}>условия использования</DocLink><i className="req" aria-label="обязательно">*</i></span>
            </label>
            <label className="check">
              <input type="checkbox" checked={agreePrivacy}
                onChange={(e) => setAgreePrivacy(e.target.checked)} />
              <span>Даю согласие на <DocLink onDocs={onDocs}>обработку персональных данных</DocLink><i className="req" aria-label="обязательно">*</i></span>
            </label>
            <label className="check">
              <input type="checkbox" checked={agreeMarketing}
                onChange={(e) => setAgreeMarketing(e.target.checked)} />
              <span>Хочу получать новости и предложения SKOKA — на почту и в приложении</span>
            </label>
          </div>
        )}
        {err && <div className="note warn">{err}</div>}
        {/* Занятая почта — не ошибка, а знак, что человек здесь уже был.
            Сообщить причину и оставить его искать вкладку значит отправить
            разбираться самому; выход должен стоять там же, где отказ. */}
        {err && /занят/i.test(err) && mode === 'register' && (
          <button className="btn" onClick={() => switchMode('login')}>
            Войти с этой почтой
          </button>
        )}
        <button
          className="btn pri"
          disabled={busy || !email.trim() || password.length < 8
            || (mode === 'register' && !(agreeTerms && agreePrivacy))}
          onClick={submit}>
          {mode === 'register' ? 'Создать аккаунт' : 'Войти'}
        </button>
        {mode === 'login' && resetOn && (
          <button className="lnk wide" onClick={() => setForgot(true)}>Забыли пароль?</button>
        )}
        {/*
          Выход «без аккаунта» — словами, а не крестиком (15.10.2026), сразу
          под главной кнопкой (22.10.2026). Цена отказа — подписью ниже, с
          отступом: что именно может стереть данные, без слов «кэш» и «хранилище».
        */}
        {onLater && (
          <>
            <button className="btn ghost" disabled={busy} onClick={onLater}>Позже</button>
            <div className="note later-note">
              Без аккаунта данные хранятся только в этом браузере. Они пропадут, если
              очистить историю и данные сайтов или удалить SKOKA с экрана «Домой».
              Браузер может стереть их и сам, если долго не открывать приложение.
            </div>
          </>
        )}
      </div>
    </SheetWrap>
  );
}

/** Документ из подписи согласия: открывается поверх формы, отметки не сбрасываются. */
function DocLink({ onDocs, children }: { onDocs: (() => void) | undefined; children: React.ReactNode }) {
  if (!onDocs) return <>{children}</>;
  return (
    <button type="button" className="lnk doclink"
      onClick={(e) => { e.preventDefault(); onDocs(); }}>{children}</button>
  );
}

/**
 * Расхождение версий.
 *
 * Сервер отказал в записи: там версия, которой это устройство не видело.
 * Молча выбрать победителя нельзя — в финансовом приложении это тихая
 * потеря введённых данных. Решает человек, но выбирать ему есть из чего:
 * обе картины рядом, со временем последнего изменения, и под каждой
 * кнопкой — сколько операций пропадёт.
 *
 * Раньше показывалась только серверная версия, зелёной стояла «Взять
 * серверную», а текст называл сервер «изменённым с другого устройства».
 * У владельца на сервере лежала версия от 6 октября — его же телефона, —
 * а всё введённое за неделю было только на телефоне: главная кнопка стёрла
 * бы его. Теперь главная — у той стороны, что новее.
 */
function ConflictSheet({ conflict, local, onClose, onTakeRemote, onForceLocal }: {
  conflict: Conflict; local: AppState; onClose: () => void;
  onTakeRemote: () => void; onForceLocal: () => void;
}) {
  const remote = conflict.state;
  const [changedAt, setChangedAt] = useState<string | null | undefined>(undefined);
  useEffect(() => { void getLocalChangedAt().then(setChangedAt).catch(() => setChangedAt(null)); }, []);
  // Старые установки времени изменения не помнят — тогда последняя запись операции.
  const lastTx = local.transactions.reduce<string | null>((m, t) => (!m || t.createdAt > m ? t.createdAt : m), null);
  const localAt = changedAt ?? lastTx;
  const remoteAt = remote?.savedAt ?? null;
  const remoteTxs = remote?.state?.transactions ?? [];
  const remoteIds = new Set(remoteTxs.map((t) => t.id));
  const localIds = new Set(local.transactions.map((t) => t.id));
  const onlyHere = local.transactions.filter((t) => !remoteIds.has(t.id)).length;
  const onlyThere = remoteTxs.filter((t) => !localIds.has(t.id)).length;
  const newer: 'local' | 'remote' | null = localAt && remoteAt
    ? (localAt > remoteAt ? 'local' : localAt < remoteAt ? 'remote' : null)
    : localAt ? 'local' : remoteAt ? 'remote' : null;
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
  }) : 'неизвестно');
  const lost = (n: number, where: string) => {
    if (n === 0) return `${where} ничего не пропадёт`;
    const one = n % 10 === 1 && n % 100 !== 11;
    return `${where} ${one ? 'пропадёт' : 'пропадут'} ${plural(n, 'операция', 'операции', 'операций')}, ${one ? 'которой' : 'которых'} нет на другой стороне`;
  };
  const side = (title: string, accounts: number, txs: number, at: string | null, atWord: string, on: boolean) => (
    <div className={`conflict-side${on ? ' on' : ''}`}>
      <b>{title}{on ? <span className="tag ok">новее</span> : null}</b>
      <span>счетов {accounts} · операций {txs}</span>
      <span>{atWord} — {when(at)}</span>
    </div>
  );
  const keepLocal = (
    <div className="conflict-act" key="local">
      <button className={`btn ${newer === 'local' ? 'pri' : ''}`} onClick={onForceLocal}>Оставить данные с этого устройства</button>
      <small>{lost(onlyThere, 'С сервера')}</small>
    </div>
  );
  const takeRemote = (
    <div className="conflict-act" key="remote">
      <button className={`btn ${newer === 'remote' ? 'pri' : ''}`} onClick={onTakeRemote}>Взять данные с сервера</button>
      <small>{lost(onlyHere, 'С этого устройства')}</small>
    </div>
  );
  return (
    <SheetWrap title="Данные расходятся" onClose={onClose}>
      <div className="form">
        <div className="note">
          На этом устройстве и на сервере разные версии.{' '}
          {newer ? 'Оставь более свежую — вторая будет заменена.' : 'Выбери, какую оставить, — вторая будет заменена.'}
        </div>
        {changedAt !== undefined && (
          <>
            {side('На этом устройстве', local.accounts.length, local.transactions.length, localAt, 'последнее изменение', newer === 'local')}
            {remote && side('На сервере', remote.state?.accounts?.length ?? 0, remoteTxs.length, remoteAt, 'сохранено', newer === 'remote')}
            {newer === 'remote' ? [takeRemote, keepLocal] : [keepLocal, takeRemote]}
          </>
        )}
      </div>
    </SheetWrap>
  );
}

/**
 * Встреча данных устройства с данными аккаунта (SF-03).
 *
 * Человек начинал гостем, ввёл свою картину — и входит в аккаунт, где уже
 * что-то есть. Складывать две картины нельзя ни в каком виде: сумма двух
 * остатков не остаток, а число, которого не существует. Молча брать одну из
 * них — тоже: любая потеря здесь необратима и незаметна.
 *
 * Поэтому обе показываются рядом, и выбирает человек. Слияние по записям —
 * отдельная работа; пока её нет, честнее предложить выбор, чем притвориться,
 * что мы умеем больше.
 */
function MergeChoice({ local, remote, onKeepRemote, onKeepLocal, onClose }: {
  local: AppState;
  remote: { accounts?: number; transactions?: number; savedAt?: string; liquid?: Money } | null;
  onKeepRemote: () => void;
  onKeepLocal: () => void;
  onClose: () => void;
}) {
  const liquid = local.accounts
    .filter((a) => a.isLiquid)
    .reduce((n, a) => add(n, a.balance), ZERO);

  return (
    <SheetWrap title="Две картины денег" onClose={onClose}>
      <div className="note warn" style={{ marginTop: 0 }}>
        В аккаунте уже есть данные, и на этом устройстве тоже. Складывать их
        нельзя: сумма двух остатков — число, которого не существует. Выбери,
        какую картину оставить.
      </div>

      <div className="li"><div className="nm">
        <b>На этом устройстве</b>
        <span>
          счетов {local.accounts.length} · операций {local.transactions.length}
          {' '}· целей {local.goals.length}
        </span>
      </div><div className="am num">{formatMoney(liquid)}</div></div>

      <div className="li"><div className="nm">
        <b>В аккаунте</b>
        <span>
          счетов {remote?.accounts ?? 0} · операций {remote?.transactions ?? 0}
          {remote?.savedAt ? ` · сохранено ${new Date(remote.savedAt).toLocaleString('ru-RU')}` : ''}
        </span>
      </div>
        {/* Числом, а не одним количеством записей: выбирают по деньгам. */}
        {remote?.liquid !== undefined && (
          <div className="am num">{formatMoney(remote.liquid)}</div>
        )}
      </div>

      <button className="btn pri" onClick={onKeepRemote}>Оставить данные аккаунта</button>
      <button className="btn danger" onClick={onKeepLocal}>
        Оставить эту, перезаписав аккаунт
      </button>
      <div className="note">
        Перенести отдельные записи из одной картины в другую пока нельзя —
        обещать этого не буду. Если нужны обе, выгрузи данные до выбора.
      </div>
    </SheetWrap>
  );
}

/**
 * Подтверждение платежа по обязательству.
 *
 * Сумма редактируема: у переменных платежей вроде ЖКХ она каждый месяц своя,
 * а подтверждение по оценке вместо факта тихо разошлось бы с балансом счёта.
 */
/**
 * Подтверждение платежа по обязательству (§5.3).
 *
 * Три вещи, которых здесь раньше не было, и каждая берётся из настоящего
 * случая.
 *
 * Фактическая дата, а не дата по расписанию: платёж, запланированный на
 * двадцатое, мог уйти семнадцатого. В историю и аналитику должна попасть
 * фактическая, иначе месячная разбивка врёт. Цикл при этом закрывается по
 * плановой дате — это ключ, а не событие.
 *
 * Выбор счёта: заплатить можно с любого, а не только с того, к которому
 * обязательство привязано.
 *
 * Признак «баланс уже учитывает» — для первого дня. Человек оплатил утром, а
 * остатки назвал вечером уже за вычетом. Списать ещё раз значило бы посчитать
 * деньги дважды; не записать расход — потерять след, и тогда сверка отнесёт
 * эти деньги в «Прочее» как настоящую трату.
 */
function SettleObligation({ state, target, onClose, onSaved }: {
  state: AppState;
  target: { id: string; due: string; amount: Money };
  onClose: () => void; onSaved: () => void;
}) {
  const o = state.obligations.find((x) => x.id === target.id);
  const today = todayPd();
  /*
    Счёт по умолчанию — тот, где лежит конверт.

    `Obligation.accountId` — счёт, **с которого платёж обычно списывается**, и
    он запросто расходится с тем, где человек эти деньги держит: у владельца
    ипотека привязана к Т-black, а 36 000 ₽ под неё закреплены на отдельном
    счёте. Подставлять привязку значило предлагать списать деньги оттуда, где
    их нет, — и человек каждый раз молча правил выбор за приложением.
  */
  const held = state.reserves
    .filter((r) => r.forKind === 'obligation' && r.forId === target.id && r.amount > ZERO)
    .sort((a, b) => b.amount - a.amount)[0];
  const spendable = state.accounts.filter((a) => !a.archived);
  const keyboard = useOperationKeyboard();
  const padKeys = usePadKeys(keyboard.amountRef);
  const [categories, setCategories] = useState(state.categories);
  // Сумма — выражение калькулятора, как в ручной записи: у переменных
  // платежей вроде ЖКХ её правят каждый месяц, а иногда и складывают.
  const [expr, setExpr] = useState(String(target.amount / 100).replace('.', ','));
  const [accId, setAccId] = useState(
    held?.accountId ?? o?.accountId ?? spendable[0]?.id ?? '',
  );
  /*
    Категория — та, что у обязательства; у старых без категории — «Прочее»,
    как и раньше подставляла запись платежа. Пустая строка заставила бы
    человека выбирать категорию там, где раньше этого не требовалось.
  */
  const [catId, setCatId] = useState(() => {
    const own = o?.categoryId;
    if (own && state.categories.some((c) => c.id === own && !c.archived)) return own;
    return state.categories.some((c) => c.id === 'other' && !c.archived) ? 'other' : '';
  });
  /*
    Фактическая дата, а не дата по расписанию: платёж, запланированный на
    двадцатое, мог уйти семнадцатого. Будущее запрещено — оплата уже была.
  */
  const [date, setDate] = useState<PlainDate>(today);
  const [time, setTime] = useState(() => new Date().toTimeString().slice(0, 5));
  const [whenOpen, setWhenOpen] = useState(false);
  const [note, setNote] = useState('');
  const [already, setAlready] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  // Ключ живёт столько же, сколько открытая форма: второе нажатие той же
  // кнопки — повтор одного действия, а вторая настоящая оплата открывает
  // форму заново и получает свой ключ.
  const [requestId] = useState(() => newRequestId());
  const calc = useMemo(() => evalAmount(expr), [expr]);
  if (!o) return null;
  const value = calc.value !== null && calc.value > 0 ? (calc.value as Money) : ZERO;
  const due = target.due as PlainDate;
  const split = splitPayment(state, o.id, due, value);
  /*
    Кредит: сверх платежа по графику — досрочное погашение, как во вкладке
    «Долг», а не аванс на следующий месяц (21.10.2026). Что станет с долгом и
    можно ли гасить досрочно — тем же расчётом.
  */
  const debt = split.loan
    ? splitDebt({
      id: `loan:${split.loan.id}`, kind: 'loan', name: split.loan.name, left: '', line: '', loan: split.loan, suggested: ZERO,
      due: paymentInstances({ obligations: [o], reserves: state.reserves, today: state.today, from: due, to: due })[0] ?? null,
    }, value, date, state.today)
    : null;

  const tap = (k: string) => {
    setErr(null); setFlash(false);
    if (k === '⌫') { setExpr((p) => p.slice(0, -1)); return; }
    if (k === ',' && /[,.][^+\-*/]*$/.test(expr)) return; // вторая запятая в числе
    setExpr((p) => p + k);
  };

  const save = async () => {
    if (busy) return;
    if (value === ZERO) { setFlash(true); setErr('Введи сумму'); return; }
    if (!spendable.some((a) => a.id === accId)) { setErr('Выбери действующий счёт'); return; }
    if (!categories.some((c) => c.id === catId && !c.archived && (c.kind ?? 'expense') === 'expense')) {
      setErr('Выбери категорию'); return;
    }
    if (debt?.blocked) { setErr(debt.blocked); return; }
    const pay = debt ? debt.payment : value;
    const extra = debt ? debt.extra : ZERO;
    // «Баланс уже учитывает» — про платёж по графику; досрочное с ним не записать.
    if (extra > ZERO && already) { setErr(`Сверх платежа — ${formatMoney(extra)} досрочно. Сними «Баланс уже учитывает» или впиши не больше ${formatMoney(debt!.payment)}.`); return; }
    setErr(null); setBusy(true);
    try {
      if (pay > ZERO) {
        await settleObligation({
          obligationId: o.id,
          dueDate: due,
          amount: pay,
          paidDate: date,
          time,
          accountId: accId,
          categoryId: catId,
          requestId,
          ...(note.trim() ? { note: note.trim() } : {}),
          ...(already ? { applyToBalance: false } : {}),
        });
      }
      if (extra > ZERO && split.loan) {
        await addLoanPrepayment({
          loanId: split.loan.id, accountId: accId, amount: extra, date, time, envelopeOf: o.id,
          ...(note.trim() ? { note: note.trim() } : {}),
          ...(split.loan.prepayMode ? { mode: split.loan.prepayMode } : {}),
        });
      }
      rememberType('expense');
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось подтвердить');
      setBusy(false);
    }
  };

  /*
    Та же форма, что у ручной записи расхода: сумма с калькулятором, счёт,
    категория, дата со временем и заметка. Платёж по обязательству — обычный
    расход с одной связью, и отдельная раскладка экрана заставляла человека
    заново искать, где тут сумма и где счёт.
  */
  padKeys.current = tap;
  return (
    <SheetWrap title="Оплата платежа" onClose={onClose} bare operationHeight={keyboard.height} keyboardInset={keyboard.keyboardInset} typing={keyboard.typing}>
      <div className="opbar">
        <button className="opclose" onClick={onClose} aria-label="Закрыть"><CloseX /></button>
        <div className="optitle">
          <b>{o.name}</b>
          <span>платёж по расписанию {formatDate(target.due)}</span>
        </div>
      </div>

      <button ref={keyboard.amountRef} type="button" aria-label="Сумма платежа" className={`opsum${flash ? ' flash' : ''}`} onClick={() => { setFlash(false); keyboard.returnToAmount(); }}>
        <div className="optotal num">
          {calc.error === 'divzero' ? 'на ноль не делится'
            : calc.error === 'overflow' ? 'слишком много'
            : `− ${formatMoney(value)}`}
        </div>
        <div className="opexpr">
          {expr ? groupExpr(expr) : ' '}
          {!keyboard.editingNote && <i className="opcaret" />}
        </div>
      </button>

      <div className="oprows">
        <div className="single-account">
          <AccountField label="Счёт" pickerTitle="Списать со счёта" accounts={spendable} value={accId} onChange={setAccId} />
        </div>
        <CategoryField categories={categories} kind="expense" value={catId}
          onUpdated={setCategories} onChange={setCatId} />
        <WhenField open={whenOpen} onOpen={setWhenOpen} label="Дата и время платежа"
          text={`${date === today ? 'Сегодня' : dayTitle(date)}, ${time}`}
          date={date} time={time} max={today} onDate={(d) => setDate(pd(d))} onTime={setTime} />
        <NoteField value={note} onChange={setNote} inputRef={keyboard.noteRef} onFocus={keyboard.focusNote} onBlur={keyboard.blurNote} onEnter={keyboard.returnToAmount} />
        {/*
          «Баланс уже учитывает» — для первого дня. Человек оплатил утром, а
          остатки назвал вечером уже за вычетом. Списать ещё раз значило бы
          посчитать деньги дважды; не записать расход — потерять след.
        */}
        <label className="oprow checkrow">
          <span>Баланс уже учитывает этот платёж</span>
          <input type="checkbox" checked={already} onChange={(e) => setAlready(e.target.checked)} />
        </label>
      </div>

      {o.amountType === 'variable' && (
        <div className="note">Сумма переменная — поставь фактическую из выписки.</div>
      )}
      {/* Конверт называется вслух: его снятие — следствие записи, и человек
          должен знать о нём до нажатия, а не обнаружить после. */}
      {held && (
        <div className="note">
          Под этот платёж закреплено {formatMoney(held.amount)} на «
          {state.accounts.find((a) => a.id === held.accountId)?.name ?? 'счёте'}».
          После записи конверт снимется на списанную сумму.
        </div>
      )}
      {/* Куда пойдут деньги: отдали меньше — это часть, больше — лишнее в
          счёт следующего раза (16.10.2026), у кредита — досрочно (21.10.2026). */}
      {(() => {
        if (debt?.blocked) return <div className="note warn">{debt.blocked}</div>;
        const text = debt && value > ZERO ? debt.note : splitNote(split, state.today);
        return text ? <div className="note paysplit">{text}</div> : null;
      })()}
      {/* Что будет с остатком — при любом положении галочки: без этого она
          требовала знать, как устроен расчёт. */}
      {(() => {
        const acc = spendable.find((a) => a.id === accId);
        if (!acc || value === ZERO) return null;
        return (
          <div className="note">
            {already
              ? `Остаток «${acc.name}» не изменится: запишу расход только в историю.`
              : `Остаток «${acc.name}» уменьшится на ${formatMoney(value)}: ${formatMoney(acc.balance)} → ${formatMoney(sub(acc.balance, value))}.`}
          </div>
        );
      })()}
      {err && <div className="note warn" role="alert">{err}</div>}

      {/* Калькулятор прижат к низу, как в ручной записи: без полосы
          режимов ввода он иначе висел бы под заметкой, а внизу пустело. */}
      {!keyboard.editingNote && <Collapse open={!whenOpen} className="padbox bottom"><div className="pad">
        {['7', '8', '9', '÷', '4', '5', '6', '×', '1', '2', '3', '−', ',', '0', '⌫', '+'].map((k) => (
          <button key={k} className={/[÷×−+]/.test(k) ? 'padop' : k === '⌫' ? 'paddel' : ''}
            aria-label={k === '⌫' ? 'Стереть' : k} onClick={() => tap(k === '÷' ? '/' : k === '×' ? '*' : k === '−' ? '-' : k)}>{/[÷×−+]/.test(k) ? <ArithmeticIcon symbol={k} /> : k === '⌫' ? <Icon name="delete-left" size={20} /> : k}</button>
        ))}
      </div></Collapse>}

      <button className="btn pri" data-pad-enter disabled={busy} onClick={() => void save()}>
        {busy ? 'Записываю…' : 'Записать платёж'}
      </button>
    </SheetWrap>
  );
}

/**
 * Настройка: всё созданное видно и правится.
 *
 * Формы добавления без списков — систематическая ошибка: доход сохранялся, но
 * его негде было увидеть, и повторное открытие пустой формы выглядело как
 * обнуление. Любая созданная сущность должна иметь список, правку и удаление.
 */
/**
 * Уровень подписки и остаток пакетов.
 *
 * Показывает то, за что человек платит: сколько разборов и чеков осталось
 * и до какого числа. Пока приёма платежей нет, здесь же стоит переключатель
 * уровня — и он **не притворяется оплатой**: подписка пишется теми же полями,
 * которые заполнит настоящий платёж, чтобы проверялся тот самый путь.
 *
 * Переключатель появляется, только если сервер его разрешил. В боевой сборке
 * он выключен: Pro стоит живых денег за разборы и чеки.
 */
/**
 * Письма: что приходит человеку на почту и чем он управляет.
 *
 * Разделение принципиальное и не про вежливость. Письма о безопасности,
 * восстановлении пароля, подписке и оплате — часть услуги: отключить их
 * значит лишить человека единственного канала, по которому к нему придёт
 * сообщение о чужом входе или о списании. Рекламные письма к услуге
 * отношения не имеют, и у них свой переключатель.
 *
 * Рекламные письма — это согласие на рассылку (docs/legal/03 и 04), а не
 * отдельная настройка: переключатель записывает согласие или его отзыв с
 * версией, временем и способом, и оно уезжает на сервер со снимком, как
 * остальные согласия. По умолчанию выключено — согласие не ставится за
 * человека (22.10.2026; раньше выбор жил в localStorage и был включён).
 */
function MailSettings({ state, user, onSaved }: { state: AppState; user: User | null; onSaved: () => Promise<void> | void }) {
  const local = currentConsent(state.consents, 'marketing').granted;
  // Сервер знает больше: отписку по ссылке из письма и подтверждена ли почта.
  const [server, setServer] = useState<Marketing | null>(null);
  useEffect(() => {
    if (!user) return;
    void getMarketing().then(async (m) => {
      setServer(m);
      // Отписались по ссылке в письме — отзыв согласия записывается и здесь.
      if (local && !m.granted) {
        await recordConsent({ kind: 'marketing', documentVersion: DOC_VERSION, method: 'button', granted: false });
        await onSaved();
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  // Прежний выбор в браузере больше не читается — убираем, чтобы не путал.
  useEffect(() => { try { localStorage.removeItem('skoka.mail.promo'); } catch { /* приватный режим */ } }, []);
  const promo = server ? server.granted : local;
  const toggle = async (next: boolean): Promise<void> => {
    await recordConsent({ kind: 'marketing', documentVersion: DOC_VERSION, method: 'checkbox', granted: next });
    if (user) await setMarketing(next, DOC_VERSION).then(setServer).catch(() => {});
    await onSaved();
  };

  return (
    <section className="blk">
      <div className="blkhead"><b>Уведомления и письма</b></div>
      <SetRow icon="envelope" title={<>Системные письма
          <Tip>Системные письма отключить нельзя: ими приходят сообщения о входе в
            аккаунт и о списании денег.</Tip></>}
        sub="безопасность, пароль, подписка и оплата" right={<span className="tag soft">всегда</span>} />
      <label className="li sw setrow">
        <span className="picon" style={{ '--c': 'var(--ink-2)' } as CSSProperties}><Icon name="bell" size={16} /></span>
        <div className="nm"><b>Рекламные письма</b>
          <span>новости и предложения SKOKA — на почту и в приложении</span></div>
        <input type="checkbox" checked={promo} onChange={(e) => void toggle(e.target.checked)} />
        <span className="swbox" aria-hidden="true" />
      </label>
      {promo && server && !server.confirmed && (
        <div className="note">Подтверди почту: в письме «Присылать новости SKOKA?» нажми «Да, присылать». Без этого писем не будет.</div>
      )}
    </section>
  );
}


/** Сколько дней осталось до конца: «5» в карточке пробного. */
const daysLeft = (iso: string): number => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));

/** «SKOKA Pro» — только тем, у кого его нет: вход к покупке, отдельной карточкой. */
function ProSection() {
  const access = useAccess();
  if (!access || access.offline || access.tier === 'guest' || access.tier === 'pro' || access.tier === 'trial') return null;
  return <section className="blk"><ProRow /></section>;
}

/** «SKOKA Pro» — только тем, у кого его нет: вход к покупке. */
function ProRow() {
  const access = useAccess();
  const openPro = useOpenPro();
  if (!access || access.offline || access.tier === 'guest') return null;
  if (access.tier === 'pro' || access.tier === 'trial') return null;
  return (
    <button className="li w" onClick={() => openPro('general')}>
      <div className="ico"><Icon name="sparkles" size={18} /></div>
      <div className="nm"><b>SKOKA Pro</b>
        <span>{access.billing && access.earlyYear ? `новым пользователям — ${access.earlyYear.toLocaleString('ru-RU')} ₽ за первый год`
            : access.trial.available ? `${access.trial.days} дней бесплатно · голосом, чеки, вопросы SKOKA`
              : 'голосом, чеки, выписки, вопросы SKOKA'}</span></div>
      <span className="ch"><Icon name="chevron-right" size={13} /></span>
    </button>
  );
}

/** Карточка подписки — как карта в кошельке: тёмная, с отметкой PRO. */
function ProPass({ title, sub, big, onClick }: { title: string; sub: string; big?: ReactNode; onClick?: () => void }) {
  const body = (
    <>
      <span className="pp-l"><ProLockup /><b>{title}</b><span>{sub}</span></span>
      {big && <span className="pp-big">{big}</span>}
      {onClick && <span className="pp-ch"><Icon name="chevron-right" size={13} /></span>}
    </>
  );
  return onClick
    ? <button className="pro-pass" onClick={onClick}>{body}</button>
    : <div className="pro-pass">{body}</div>;
}

function MeterRow({ name, m }: { name: string; m: { left: number; limit: number } }) {
  const pct = m.limit > 0 ? Math.round((100 * (m.limit - m.left)) / m.limit) : 0;
  return (
    <div className="pw-m">
      <div className="pw-mt"><span>{name}</span><b className="num">осталось {m.left} из {m.limit}</b></div>
      <div className="pw-mtrack"><i style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

/**
 * Подписка в профиле (макет docs/design/pro-2026-10-21).
 *
 * У Free — карточка Pro вместо «осталось 1 из 1»: отсутствие подписки не
 * исчерпанный пакет. У пробного — сколько дней осталось и что потом. У Pro —
 * до какого числа и остатки по каждому виду отдельно, с днём обновления.
 */
function Subscription({ onManage }: { onManage: () => void }) {
  const access = useAccess();
  const openPro = useOpenPro();
  const [busy, setBusy] = useState(false);
  if (!access || access.offline) return null;

  const pick = async (tier: Tier): Promise<void> => {
    setBusy(true);
    try { await setTier(tier); } finally { setBusy(false); }
  };
  const until = access.endsAt ? dayLabel(access.endsAt.slice(0, 10)) : null;
  const meters = (
    <>
      <MeterRow name="Голосом и словами" m={access.phrase} />
      <MeterRow name="Чеки и выписки" m={access.receipt} />
      <MeterRow name="Вопросы SKOKA" m={access.analysis} />
    </>
  );

  return (
    <section className="blk">
      <div className="blkhead"><b>Подписка
        {access.switchable && (
          <Tip>Приёма платежей ещё нет: уровень ставится руками, чтобы проверить,
            что открывается и что считается. Данные при понижении остаются.</Tip>
        )}</b></div>

      {access.tier === 'pro' && access.graceUntil && (
        <div className="pw-st alarm" role="alert"><b>Не получилось продлить</b>
          <span>Pro работает до {dayLabel(access.graceUntil.slice(0, 10))}. Оплати или смени карту в подписке.</span>
          <button className="btn pri" onClick={onManage}>Открыть подписку</button></div>
      )}
      {access.tier === 'pro' && (
        <>
          <ProPass title={until ? `до ${until}` : 'Pro подключён'} onClick={onManage}
            sub={access.renews ? `продлится ${until ?? ''}` : 'автопродление выключено'} />
          {meters}
          {access.resetsOn && <div className="pw-mw">обновятся {dayLabel(access.resetsOn)}, в день оплаты</div>}
        </>
      )}
      {access.tier === 'trial' && (
        <>
          <ProPass title="Пробный период" sub={`${until ? `до ${until} · ` : ''}потом Free, ничего не спишется`}
            {...(access.endsAt ? { big: <>{daysLeft(access.endsAt)}<small>{pluralWord(daysLeft(access.endsAt), 'день', 'дня', 'дней')}</small></> } : {})} />
          {meters}
          {access.billing && <button className="btn pri" onClick={() => openPro('general')}>Остаться в Pro</button>}
        </>
      )}
      {(access.tier === 'free' || access.tier === 'guest') && (
        <ProPass title={access.billing && access.earlyYear ? `${access.earlyYear.toLocaleString('ru-RU')} ₽ за первый год`
          : access.trial.available ? `${access.trial.days} дней бесплатно` : 'SKOKA Pro'}
          sub="голосом, чеки, выписки, вопросы SKOKA" onClick={() => openPro('general')} />
      )}

      {access.switchable && (
        <div className="opmodes" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {(['free', 'trial', 'pro'] as const).map((t) => (
            <button
              key={t}
              className={`opmode${access.tier === t ? ' on' : ''}`}
              disabled={busy}
              onClick={() => void pick(t)}>
              <span>{TIER_NAME[t]}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

const TIER_NAME: Record<Tier, string> = {
  guest: 'Гость', free: 'Free', trial: 'Пробный', pro: 'Pro',
};

/**
 * «Ещё»: профиль и то, что настраивают редко, — категории, обычные траты,
 * данные (макет 14.10.2026).
 *
 * Доход, платежи и цели уехали в «Деньги»: это деньги человека, и искать их
 * среди настроек было неочевидно. Аккаунт, подписка и письма — в профиле:
 * это вопросы про себя, а не про деньги.
 */
function More(props: {
  /** «Спроси SKOKA» в шапке. */
  ask: ReactNode;
  state: AppState;
  onPick: (s: Sheet) => void;
  user: User | null;
  /** Pro или пробный период: у них есть разбор чеков, а значит, и новые категории из него. */
  pro: boolean;
  onCategoriesChanged: () => void;
  onReset: () => void; onRerunOnboarding: () => void;
  /** Внутри листа «Настройки»: заголовок даёт лист. */
  inSheet?: boolean;
}) {
  const { state } = props;
  const [managedCategories, setManagedCategories] = useState(state.categories);

  return (
    <>
      {!props.inSheet && <header className="pgtitle"><h1>Ещё</h1>{props.ask}</header>}

      {/*
        Профиль и подписка — одной строкой (22.10.2026): «Подписка» и так
        внутри профиля, и вторая строка повторяла первую. Тем, у кого Pro нет,
        строка «SKOKA Pro» остаётся — это их вход к нему.
      */}
      {/*
        Профиля здесь нет (03.11.2026): он открывается кружком с инициалами
        в шапке, рядом с шестерёнкой, — владелец: «зачем, если он есть
        отдельно». Остаётся «SKOKA Pro» тем, у кого его нет: это вход к покупке.
      */}
      <ProSection />

      <section className="blk"><div className="blkhead"><b>Учёт</b></div>
        {(['expense', 'income'] as const).map(kind => <CategoryField key={kind} manage categories={managedCategories} kind={kind} value="" onChange={() => {}} onUpdated={next => { setManagedCategories(next); props.onCategoriesChanged(); }}
          trigger={(open) => <SetRow icon="tags" tone={kind === 'income' ? 'var(--ok)' : 'var(--ink-2)'}
            title={kind === 'income' ? 'Категории приходов' : 'Категории расходов'}
            sub={`${managedCategories.filter((c) => c.kind === kind && !c.archived).length} шт.`} onClick={open} />}
          manageHead={props.pro ? <NewCatsModeRow mode={state.newCategoryMode} onChange={(m) => void setNewCategoryMode(m).then(props.onCategoriesChanged)} /> : undefined} />)}
        <SetRow icon="chart-simple" title="Обычные траты в месяц"
          sub={`нужды ${state.needBaseline > ZERO ? formatMoney(state.needBaseline) : 'не заданы'} · свободные ${state.wantBaseline > ZERO ? formatMoney(state.wantBaseline) : 'не заданы'}`}
          onClick={() => props.onPick('baseline')} />
      </section>

      {/*
        «Данные». «Заполнить примером» убран (03.11.2026): стирал настоящие
        данные ради демонстрации. Выписка из банка — в «Записать → Файл», здесь
        её нет (решение владельца 04.11.2026).
      */}
      <section className="blk"><div className="blkhead"><b>Данные</b></div>
        {state.transactions.length > 0 && (
          <SetRow icon="download" title="Выгрузить операции" sub="CSV — откроется в Excel и Google Таблицах"
            onClick={() => { track('csv_exported'); void downloadOperationsCsv(state); }} />
        )}
        <SetRow icon="rotate-left" title="Настроить заново" sub="пройти первые шаги снова — текущие данные удалятся"
          onClick={props.onRerunOnboarding} />
        <SetRow icon="trash-can" danger title="Удалить все данные" sub="на этом устройстве — без возврата"
          onClick={props.onReset} />
        <p className="setnote">{state.transactions.length} {pluralWord(state.transactions.length, 'операция', 'операции', 'операций')}
          {' · '}{state.accounts.length} {pluralWord(state.accounts.length, 'счёт', 'счёта', 'счетов')}
          {props.user ? ' — на этом устройстве и в аккаунте' : ' — только на этом устройстве'}</p>
      </section>

      {/* «Помощь» — последним блоком (22.10.2026, docs/design/support-2026-10-22). */}
      <section className="blk"><div className="blkhead"><b>Помощь</b></div>
        <SetRow icon="book-open" title="Как пользоваться" sub="коротко о главном — с картинками" onClick={() => props.onPick('guides')} />
        <SetRow icon="circle-question" title="Частые вопросы" sub="ответы простыми словами" onClick={() => props.onPick('faq')} />
        <SetRow icon="comments" title="Написать в поддержку" sub="ответ придёт в «Центр уведомлений»" onClick={() => props.onPick({ inbox: 'new' })} />
        <SetRow icon="envelope" title="Написать на почту" sub="support@skokadeneg.ru" href="mailto:support@skokadeneg.ru" />
      </section>
    </>
  );
}

/** «Деньги → Доход»: источники дохода и стабильный бюджет. */
function IncomeSection({ state, plan, onPick }: { state: AppState; plan: PlanResult; onPick: (s: Sheet) => void }) {
  const income = state.incomeStreams.filter((s) => s.isActive);
  return (
    <>
      <section className="blk">
        <div className="blkhead"><b>Доход</b>
          <button className="icobtn sm addbtn" onClick={() => onPick('income')} aria-label="Добавить"><Icon name="plus" size={15} /></button></div>
        {income.length === 0 ? (
          <div className="li"><div className="nm"><b>Не задан</b>
            <span>Без него неизвестно, сколько дней до зарплаты</span></div></div>
        ) : income.map((s) => (
          <button className="li w" key={s.id} onClick={() => onPick({ editIncome: s.id })}>
            {/* Ритм словами, а не «{день} числа»: у недельного дохода числа
                месяца нет, а у сдельщика нет и ритма. */}
            <div className="nm"><b>{s.name}</b>
              <span>{rhythmText(rhythmOf(s))}{s.isVariable ? ' · сумма плавает' : ''}
                {s.isActive ? '' : ' · выключен'}
                {' · '}{state.accounts.find((a) => a.id === s.accountId)?.name ?? '—'}</span></div>
            <div className="am num">{formatMoney(s.amount)}</div><span className="ch"><Icon name="chevron-right" size={13} /></span>
          </button>
        ))}
        {income.length > 0 && (
          <div className="note">
            В планировании участвует {formatMoney(plan.income)}.
            <Tip>У плавающих сумм берётся нижняя граница.</Tip>
          </div>
        )}
      </section>

      {/*
        Стабильный бюджет открывается отсюда всегда, а не только когда
        приложение сочло его подходящим. Режим человека касается, и прятать
        его за условием значит решать за него.
      */}
      <section className="blk">
        <div className="blkhead"><b>Стабильный бюджет</b></div>
        <button className="li w" onClick={() => onPick('stable')}>
          <div className="nm"><b>{stableSnapshot(state) ? 'Включён' : 'Выключен'}</b>
            <span>{stableSnapshot(state)
              ? `в запасе ${formatMoney(stableSnapshot(state)!.buffer)} · `
                + `выплата ${formatMoney(stableSnapshot(state)!.plan.payout)} `
                + cadenceTitle(stableSnapshot(state)!.plan.cadence)
              : 'платить себе из запаса по расписанию — при нерегулярном доходе'}</span></div>
          <span className="ch"><Icon name="chevron-right" size={13} /></span>
        </button>
      </section>
    </>
  );
}

/**
 * «Деньги → Платежи»: сверху — что и когда платить, ниже — сколько должен и
 * что гасить первым. Платёж по кредиту стоит в обоих местах: сверху — как
 * платёж месяца, внизу — как остаток долга.
 */
function PaymentsSection({ state, result, onPick, onPrepay, onAddLoan, onEditLoan, onEditCard, noDebts = false }: {
  /** Без блока «Долги»: в «Деньгах» долги стоят на обзоре. */
  noDebts?: boolean;
  state: AppState; result: AvailableResult; onPick: (s: Sheet) => void;
  onPrepay: (loanId: string) => void; onAddLoan: () => void; onEditLoan: (loanId: string) => void;
  onEditCard: (accountId: string) => void;
}) {
  // Подписки — отдельным блоком (04.11.2026): видно, сколько уходит на сервисы и от чего можно отказаться.
  const active = state.obligations.filter((o) => o.isActive);
  const obligations = active.filter((o) => !isSubscription(o));
  const subs = active.filter(isSubscription);
  return (
    <>
      <section className="blk">
        <div className="blkhead"><b>Обязательные платежи</b>
          <button className="icobtn sm addbtn" onClick={() => onPick('obligation')} aria-label="Добавить"><Icon name="plus" size={15} /></button></div>
        {obligations.length === 0 ? (
          <div className="li"><div className="nm"><b>Нет платежей</b>
            <span>Без них «Доступно» завышено</span></div></div>
        ) : obligations.map((o) => (
          <button className="li w" key={o.id} onClick={() => onPick({ payment: o.id })}>
            <div className="nm"><b>{o.name}</b>
              {/* Периодичность видна в списке: иначе годовой платёж выглядит
                  ровно как ежемесячный, и ошибку ввода нечем заметить. */}
              <span>{o.schedule
                ? `${scheduleLabel(o.schedule)} · ближайший ${formatDate(nextOccurrenceFrom(o.schedule, state.today) ?? state.today)}`
                : `${o.dayOfMonth} числа`}{o.amountType === 'variable' ? ' · сумма меняется' : ''}
                {o.settledCycle ? ` · оплачен за ${formatDate(o.settledCycle)}` : ''}</span></div>
            <div className="am num">{formatMoney(o.amount)}</div><span className="ch"><Icon name="chevron-right" size={13} /></span>
          </button>
        ))}
      </section>

      <SubscriptionsBlock state={state} subs={subs} onPick={onPick} />

      {!noDebts && <Debts state={state} result={result} onPrepay={onPrepay} onAdd={onAddLoan}
        onEdit={onEditLoan} onEditCard={onEditCard} />}
    </>
  );
}

/**
 * Подписки — отдельно от обязательных платежей (04.11.2026).
 *
 * Для расчёта это те же платежи; отдельно — чтобы было видно, сколько уходит
 * на сервисы. Итог — в месяц и в год: годовая сумма сильнее подталкивает
 * отказаться от лишнего. Порядок — по сумме в месяц, дорогие сверху.
 */
function SubscriptionsBlock({ state, subs, onPick }: { state: AppState; subs: Obligation[]; onPick: (s: Sheet) => void }) {
  const access = useAccess();
  const perMonth = (o: Obligation): Money =>
    Math.round(o.amount * (o.schedule ? monthlyEquivalentFactor(o.schedule) : 1)) as Money;
  const sorted = [...subs].sort((a, b) => perMonth(b) - perMonth(a));
  const month = add(...sorted.map(perMonth));
  return (
    <section className="blk subsblk">
      <div className="blkhead"><b>Подписки</b>
        {sorted.length > 0 && <span className="subs-sum num">{formatMoney(month)}<small> в месяц</small></span>}
        <button className="icobtn sm addbtn" onClick={() => onPick('subscriptionNew')} aria-label="Добавить подписку"><Icon name="plus" size={15} /></button></div>
      {sorted.length > 0 ? <p className="subs-year num">≈ {formatMoney((month * 12) as Money)} в год</p> : (
        <button className="li w" onClick={() => onPick('subscriptionNew')}>
          <div className="nm"><b>Подписок пока нет</b>
            <span>Кинопоиск, Яндекс Плюс, ChatGPT — видно, сколько уходит в месяц и в год</span></div>
          <Icon name="chevron-right" size={13} className="ch" />
        </button>
      )}
      {sorted.map((o) => {
        const svc = serviceOf(o.name);
        // SKOKA Pro без автопродления больше не спишется — предлагаем убрать из платежей.
        const proStopped = svc?.id === 'skoka' && access && !access.offline && access.tier !== 'guest' && !access.renews;
        return (
          <button className="li w subrow" key={o.id} onClick={() => onPick({ payment: o.id })}>
            <SubGlyph name={o.name} />
            <div className="nm"><b>{o.name}</b>
              <span className={proStopped ? 'warn' : undefined}>{proStopped
                ? 'автопродление выключено — убрать из платежей?'
                : o.schedule
                  ? `${scheduleLabel(o.schedule)} · ${formatDate(nextOccurrenceFrom(o.schedule, state.today) ?? state.today)}`
                  : `${o.dayOfMonth} числа`}</span></div>
            <div className="am num">{formatMoney(o.amount)}</div>
          </button>
        );
      })}
    </section>
  );
}

/** Значок подписки: логотип сервиса на белой плашке или кружок с первой буквой. */
function SubGlyph({ name }: { name: string }) {
  const svc = serviceOf(name);
  return svc
    ? <span className="subico"><img src={svc.src} alt="" width={36} height={36} /></span>
    : <span className="subico letter" aria-hidden="true">{[...name.trim()][0]?.toUpperCase() ?? '•'}</span>;
}

/** Правка дохода. */
/**
 * Редактор источника дохода.
 *
 * Тот же разговор, что и в настройке, и это не дублирование, а обязанность:
 * жизнь меняется — человек уходит на сделку, зарплату двигают, подработка
 * заканчивается, — и менять всё это он должен там, где живёт, а не проходя
 * настройку заново.
 *
 * Перед сохранением показывается, что станет с горизонтом: смена ритма — это
 * не правка подписи, от неё зависит вся главная цифра.
 */
function EditIncome({ state, id, onClose, onSaved }: {
  /** Без `id` — новый доход: та же форма, что и правка, а не урезанная. */
  state: AppState; id?: string; onClose: () => void; onSaved: () => void;
}) {
  const s = id ? state.incomeStreams.find((x) => x.id === id) : undefined;
  const creating = id === undefined;
  if (!creating && !s) return null;
  // Счёт, на который приходят деньги: при правке его тоже можно сменить.
  const accounts = state.accounts.filter((a) => !a.archived && a.kind !== 'credit_card');
  const accountOf = accounts.find((a) => a.id === s?.accountId)?.id ?? accounts.find((a) => a.isLiquid)?.id ?? accounts[0]?.id ?? '';
  // Ожидаемый приход «когда как»: тот, что ещё впереди.
  const expect = s ? state.expectedIncomes.find((e) => e.streamId === s.id
    && (e.status === 'planned' || e.status === 'partial') && e.expectedDate >= state.today) : undefined;
  const money2 = (m: Money): string => (m > ZERO ? formatAmountInput(String(m / 100)) : '');
  const init: IncomeDraft = (() => {
    if (!s) return { ...newIncome('Зарплата', '10'), anchor: state.today, accountId: accountOf };
    const r = rhythmOf(s);
    return {
      name: s.name, amount: money2(s.amount), day: String(s.dayOfMonth), variable: s.isVariable,
      rhythm: r.kind,
      days: r.kind === 'dates' ? r.days.join(', ') : String(s.dayOfMonth),
      anchor: r.kind === 'weekly' || r.kind === 'biweekly' ? r.anchor : state.today,
      expectDate: expect?.expectedDate ?? '',
      expectAmount: expect ? money2(expect.amount) : '',
      expectIsCheckpoint: expect?.horizonRole === 'checkpoint',
      isCheckpoint: roleOfStream(s) === 'checkpoint',
      advance: s.advance ?? false,
      accountId: accountOf,
      active: s.isActive,
    };
  })();

  /** Что станет со сроком: тем же расчётом, что и на главной. */
  const horizonOf = (v: IncomeDraft) => {
    const value = parseAmountInput(v.amount);
    const rhythm = draftRhythm(v, s?.shiftRule ?? 'previous_business_day');
    const draft: IncomeStream = {
      ...(s ?? {
        id: 'draft', name: v.name, accountId: v.accountId ?? '', amount: value, isVariable: v.variable,
        dayOfMonth: rhythm.kind === 'dates' ? rhythm.days[0] ?? 10 : 10, shiftRule: 'previous_business_day' as const, isActive: true,
      }),
      rhythm, amountBasis: value === ZERO ? 'unknown' : v.variable ? 'estimate' : 'known',
      isActive: creating ? true : v.active !== false, horizonRole: v.isCheckpoint ? 'checkpoint' : 'top_up',
    };
    const common = {
      today: state.today, expectations: state.expectedIncomes, facts: incomeFacts(state.transactions),
      ...(state.trackingStartedAt ? { since: state.trackingStartedAt } : {}),
    };
    return {
      before: resolveHorizon({ ...common, streams: state.incomeStreams }),
      after: resolveHorizon({ ...common, streams: [...state.incomeStreams.filter((x) => x.id !== id), draft] }),
    };
  };
  const horizonText = (h: ReturnType<typeof horizonOf>['after']): string => (h.date
    ? `${formatDate(h.date)}${h.basis === 'estimated' ? ' (оценка)' : ''}`
    : 'даты нет — по запасу хода');

  return (
    <IncomeSheet init={init} isNew={creating} title={creating ? 'Новый доход' : s!.name}
      accounts={accounts} editing={!creating} onClose={onClose}
      preview={(v) => {
        const { before, after } = horizonOf(v);
        return (
          <>
            <CardHead>Что станет со сроком</CardHead>
            <div className="rccard rcsel">
              <div className="rcselrow"><span className="rclabel">Сейчас</span>
                <span className="rcvalue"><span className="rcvaltext">{horizonText(before)}</span></span></div>
              <div className="rcselrow"><span className="rclabel">{creating ? 'С этим доходом' : 'После правки'}</span>
                <span className="rcvalue"><span className="rcvaltext">{horizonText(after)}</span></span></div>
            </div>
            {after.basis === 'unknown' && (
              <div className="warnline">
                Срок пропадёт: приложение перестанет говорить «до такого-то» и будет считать, на сколько хватит имеющихся денег.
              </div>
            )}
          </>
        );
      }}
      onSave={async (v) => {
        const value = parseAmountInput(v.amount);
        const rhythm = draftRhythm(v, s?.shiftRule ?? 'previous_business_day');
        const firstDay = rhythm.kind === 'dates' ? (rhythm.days[0] ?? 10) : (s?.dayOfMonth ?? 10);
        const amountBasis = value === ZERO ? 'unknown' as const : v.variable ? 'estimate' as const : 'known' as const;
        const role = v.isCheckpoint ? 'checkpoint' as const : 'top_up' as const;
        const accId = v.accountId ?? accountOf;
        let streamId = s?.id;
        if (!s) {
          streamId = (await addIncome({
            name: v.name, accountId: accId, amount: value, dayOfMonth: firstDay, isVariable: v.variable,
            rhythm, amountBasis, horizonRole: role,
            ...(v.advance && rhythm.kind === 'dates' ? { advance: true } : {}),
          })).id;
        } else {
          await updateIncome(s.id, {
            name: v.name.trim() || s.name, accountId: accId, amount: value, dayOfMonth: firstDay,
            isVariable: v.variable, rhythm, amountBasis, horizonRole: role,
            isActive: v.active !== false, advance: Boolean(v.advance) && rhythm.kind === 'dates',
          });
        }
        /*
          Ожидаемый приход «когда как» — тот же, что в настройке. Прежде его
          можно было назвать только там: в приложении у «Когда как» не было
          ни даты, ни суммы (22.10.2026). Правка заменяет прежнюю запись,
          «Не жду» её снимает.
        */
        if (v.rhythm === 'irregular' && streamId) {
          const date = /^\d{4}-\d{2}-\d{2}$/.test(v.expectDate) ? v.expectDate as PlainDate : null;
          const amount = parseAmountInput(v.expectAmount);
          const same = expect && date && expect.expectedDate === date && expect.amount === amount
            && (expect.horizonRole === 'checkpoint') === v.expectIsCheckpoint;
          if (expect && !same) await cancelExpectedIncome(expect.id);
          if (date && !same) {
            await addExpectedIncome({
              name: v.name.trim() || 'Приход', amount, expectedDate: date, accountId: accId, streamId,
              ...(v.expectIsCheckpoint ? { horizonRole: 'checkpoint' as const } : {}),
            });
          }
        }
        onSaved();
      }}
      {...(s ? { onDelete: async () => { await deleteIncome(s.id); onSaved(); } } : {})} />
  );
}

/**
 * Правка обязательства.
 *
 * До 17.09.2026 здесь правились только название, сумма и «какого числа» — а
 * это число календарь у платежа с расписанием попросту игнорирует: человек
 * менял его и не видел никакого эффекта. Ритм при этом изменить было нельзя
 * вовсе, и платёж, заведённый ежемесячным по ошибке, оставался таким навсегда.
 *
 * Теперь расписание правится целиком и действует **с сегодняшнего дня**:
 * новый ритм не выдумывает задним числом платежей, которых не было, а уже
 * подтверждённые оплаты остаются как есть.
 */
function EditObligation({ state, id, onClose, onSaved }: {
  state: AppState; id: string; onClose: () => void; onSaved: () => void;
}) {
  const o = state.obligations.find((x) => x.id === id);
  const today = todayPd();
  const was = o ? obligationSchedule(o, today) : null;
  const [settled, setSettled] = useState(Boolean(o?.settledCycle));
  // Счёт виден и при правке: при создании он был, а потом пропадал.
  const payFrom = state.accounts.filter((a) => !a.archived && (a.isLiquid || a.id === o?.accountId));
  if (!o || !was) return null;
  const reserved = state.reserves.filter((r) => r.forKind === 'obligation' && r.forId === id);
  // Ритм тронули — считаем с сегодня. Не тронули — оставляем как было, чтобы
  // сохранение названия не сдвигало календарь.
  const rhythmChanged = (sched: ScheduleDraft) => sched.date !== was.anchor
    || repeatOf(was) !== sched.repeat
    || JSON.stringify(endOf(was)) !== JSON.stringify(sched.end);

  return (
    <ObligationSheet isNew={false} title={o.name}
      init={{
        name: o.name, amount: formatAmountInput(String(o.amount / 100)), variable: o.amountType === 'variable',
        sched: { date: was.anchor, repeat: repeatOf(was), end: endOf(was) },
        accountId: o.accountId ?? payFrom[0]?.id ?? '',
      }}
      accounts={payFrom}
      tip={() => (o.settledCycle
        ? undefined
        : 'Подтверждать платёж удобнее с главной: там он появляется в день срока и создаёт расход в истории.')}
      extra={(v) => (
        <>
          {rhythmChanged(v.sched) && (
            <p className="note">
              Новый ритм считается с сегодняшнего дня. Уже подтверждённые оплаты остаются как есть — прошлое не переписывается.
            </p>
          )}
          {o.settledCycle && (
            <div className="rccard rcsel">
              <CardToggle label={`Оплачен за ${formatDate(o.settledCycle)}`} checked={settled} onChange={setSettled}
                sub={settled ? 'подтверждённый платёж резерв не держит — деньги уже ушли'
                  : 'резерв вернётся, а расход останется — удали его в операциях, если платежа не было'} />
            </div>
          )}
          {reserved.length > 0 && (
            <p className="note">
              Под этот платёж закреплено{' '}
              {formatMoney(add(...reserved.map((r) => r.amount)))} на{' '}
              {reserved.map((r) => state.accounts.find((a) => a.id === r.accountId)?.name ?? '—').join(', ')}.
            </p>
          )}
          {/*
            Последствия — до сохранения. Правка платежа меняет не подпись, а
            деньги: «Доступно» уменьшается ровно на разницу.
          */}
          <EffectRows effect={obligationChange(state, id, {
            amount: parseAmountInput(v.amount),
            amountType: v.variable ? 'variable' : 'fixed',
          })} />
        </>
      )}
      onClose={onClose}
      onSave={async (v) => {
        const next = scheduleOf(v.sched, rhythmChanged(v.sched) ? today : undefined);
        const amount = parseAmountInput(v.amount);
        await updateObligation(id, {
          name: v.name.trim() || o.name,
          ...(v.accountId ? { accountId: v.accountId } : {}),
          amount,
          ...(v.variable ? { amountEstimate: amount } : {}),
          amountType: v.variable ? 'variable' : 'fixed',
          schedule: next,
          // Держим согласованным с расписанием: два разных ответа на
          // вопрос «какого числа» рано или поздно разойдутся на экране.
          dayOfMonth: clampDay(Number(v.sched.date.slice(8, 10))),
        });
        if (!settled && o.settledCycle) await unsettleObligation(id);
        onSaved();
      }}
      deleteConfirm="Удалить платёж? Закреплённые под него конверты тоже уйдут."
      onDelete={async () => { await deleteObligation(id); onSaved(); }} />
  );
}

/** Правка цели. */
/**
 * Конверты на счёте: под что закреплены деньги, лежащие здесь.
 *
 * Резерв имеет адрес — конкретный счёт. Пока он был просто числом у кредитки,
 * одни и те же деньги считались дважды: как свободный остаток счёта и как уже
 * закрывающие платёж. Привязка к счёту делает это невозможным.
 *
 * Конвертов с одного счёта может быть несколько, поэтому после сохранения
 * шторка не закрывается, а показывает состояние счёта: сколько закреплено,
 * сколько осталось свободным и подо что. Дальше можно закрепить ещё.
 */
function Envelopes({ state, accountId, horizon, onClose, onSaved }: {
  state: AppState; accountId: string;
  /** Дата ближайшего прихода: под платёж нужно всё, что он потребует до неё. */
  horizon: PlainDate | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const acc = state.accounts.find((a) => a.id === accountId);
  /*
    Сколько нужно под платёж — все его неоплаченные даты до прихода, а не одна
    сумма платежа: няня по 9 000 ₽ каждую неделю — это 27 000 ₽ до 5 ноября
    (16, 23 и 30 октября). Конверт один на платёж и закрывает даты по порядку.
  */
  const dueOf = (o: Obligation): { need: Money; dates: PlainDate[] } => {
    const list = horizon ? unsettledInstances({
      obligations: [o], today: state.today, to: horizon,
      ...(state.trackingStartedAt ? { trackingStartedAt: state.trackingStartedAt } : {}),
    }) : [];
    return list.length > 0
      ? { need: add(...list.map((i) => i.unpaid)), dates: list.map((i) => i.scheduledDate) }
      : { need: o.amountType === 'fixed' ? o.amount : (o.amountEstimate ?? o.amount), dates: [] };
  };
  const targets: Array<{ kind: 'obligation' | 'credit' | 'goal'; id: string; label: string; need: Money; dates?: PlainDate[] }> = [
    ...state.accounts
      .filter((a) => a.kind === 'credit_card')
      .map((a) => ({
        kind: 'credit' as const, id: a.id,
        label: `Платёж по «${a.name}»`,
        need: state.creditTerms.find((t) => t.accountId === a.id)?.statementDebt ?? ZERO,
      })),
    ...state.obligations.filter((o) => o.isActive).map((o) => {
      const due = dueOf(o);
      return { kind: 'obligation' as const, id: o.id, label: o.name, need: due.need, dates: due.dates };
    }),
    ...state.goals.filter((g) => g.status === 'active').map((g) => ({
      kind: 'goal' as const, id: g.id, label: `Цель «${g.name}»`, need: g.monthlyContribution,
    })),
  ];

  const mine = state.reserves.filter((r) => r.accountId === accountId);
  const [mode, setMode] = useState<'edit' | 'saved'>(mine.length > 0 ? 'saved' : 'edit');
  const [pick, setPick] = useState('');
  const [amount, setAmount] = useState('');
  const [err, setErr] = useState<string | null>(null);
  if (!acc) return null;

  const taken = add(...mine.map((r) => r.amount));
  const free = sub(acc.balance, taken);
  const labelOf = (kind: string, id: string): string =>
    targets.find((t) => t.kind === kind && t.id === id)?.label ?? 'требование удалено';

  const summary = (
    <>
      <div className="row"><div className="rl">Остаток счёта</div>
        <div className="rv num">{formatMoney(acc.balance)}</div></div>
      <div className="row"><div className="rl">Закреплено</div>
        <div className="rv num">{formatMoney(taken)}</div></div>
      <div className="row lvl-subtotal"><div className="rl">Свободно на счёте</div>
        <div className={`rv num${free < 0 ? ' bad' : ''}`}>{formatMoney(free)}</div></div>
      {free < ZERO && (
        <div className="note warn">
          Закреплено больше, чем есть на счёте. Уменьши конверт или поправь остаток.
        </div>
      )}
    </>
  );

  return (
    <SheetWrap title={`Счёт «${acc.name}»`} onClose={onClose}>
      <div className="form">
        {summary}

        {mode === 'saved' ? (
          <>
            <div className="pick"><div className="k">Закреплено под</div></div>
            {mine.map((r) => (
              <div className="li" key={r.id}>
                <button className="nm env-edit" aria-label={`Изменить: ${labelOf(r.forKind, r.forId)}`}
                  onClick={() => {
                    setMode('edit'); setPick(`${r.forKind}:${r.forId}`);
                    setAmount(formatAmountInput(String(r.amount / 100).replace('.', ',')));
                  }}>
                  <b>{labelOf(r.forKind, r.forId)}</b>
                  <span>{formatMoney(r.amount)} · изменить</span></button>
                <button className="del" aria-label="Убрать"
                  onClick={async () => {
                    setErr(null);
                    try {
                      await setReserve({ accountId, forKind: r.forKind, forId: r.forId, amount: ZERO });
                      await onSaved();
                    } catch (e) {
                      setErr(e instanceof Error ? e.message : 'Не удалось убрать конверт');
                    }
                  }}><CloseX /></button>
              </div>
            ))}
            {err && <div className="note warn">{err}</div>}
            <button className="btn pri"
              onClick={() => { setMode('edit'); setPick(''); setAmount(''); }}>
              Закрепить ещё
            </button>
            <button className="btn ghost" onClick={onClose}>Готово</button>
          </>
        ) : (
          <>
            {/* Строками, как в остальном приложении: дюжина длинных чипов
                переносилась и прыгала после выбора. */}
            <div className="pick"><div className="k">Под что закрепляем
              <Tip>С одного счёта можно закрепить несколько конвертов — под каждый платёж
                или цель отдельно.</Tip></div></div>
            {targets.map((t) => {
              const key = `${t.kind}:${t.id}`;
              const already = mine.find((r) => r.forKind === t.kind && r.forId === t.id);
              return (
                <button key={key} className="li w" aria-pressed={pick === key}
                  onClick={() => {
                    setPick(key);
                    // Подставляется вся сумма платежа, а не прежнее закреплённое:
                    // платёж подорожал с 6 000 до 9 000 — в поле 9 000 (сколько
                    // позволяет счёт), а не старые 6 000.
                    const held = already?.amount ?? ZERO;
                    const start = t.need > held ? add(held, minM(sub(t.need, held), clampPos(free))) : held;
                    setAmount(start > ZERO ? formatAmountInput(String(start / 100)) : '');
                  }}>
                  <div className="nm"><b>{t.label}</b>
                    <span>
                      {t.need > ZERO ? `нужно ${formatMoney(t.need)}` : 'сумма не задана'}
                      {t.dates && t.dates.length > 1 && horizon
                        ? ` до ${formatDate(horizon)} · ${t.dates.every((d) => d.slice(0, 7) === t.dates![0]!.slice(0, 7))
                          ? `${t.dates.map((d) => Number(d.slice(8, 10))).join(', ')} ${formatDate(t.dates[0]!).split(' ')[1]}`
                          : t.dates.map((d) => formatDate(d)).join(', ')}`
                        : t.dates && t.dates.length === 1 ? ` · ${formatDate(t.dates[0]!)}` : ''}
                      {already ? ` · закреплено ${formatMoney(already.amount)}` : ''}
                    </span></div>
                  <span className="ch">{pick === key ? <Icon name="check" size={14} /> : null}</span>
                </button>
              );
            })}
            {targets.length === 0 && (
              <div className="note">
                Пока нечего закреплять: добавь обязательный платёж, кредитку или цель.
              </div>
            )}

            {pick && (
              <div className="env-sticky">
                <label>Сколько закрепить под «{labelOf(pick.split(':')[0] ?? '', pick.split(':')[1] ?? '').replace(/^Цель «(.*)»$/, '$1')}»
                  <Tip>Деньги останутся на счёте, но будут обещаны
                    «{labelOf(pick.split(':')[0] ?? '', pick.split(':')[1] ?? '')}».</Tip>
                  <AmountInput value={amount} onChange={setAmount} /></label>
                {/* Последствие — теми же словами, что в сводке выше. */}
                {(() => {
                  const [kind, id] = pick.split(':');
                  const was = mine.find((r) => r.forKind === kind && r.forId === id)?.amount ?? ZERO;
                  const after = sub(free, sub(parseAmountInput(amount), was));
                  return (
                    <div className="note">
                      Свободно на счёте: {formatMoney(free)} → {formatMoney(after)}.
                    </div>
                  );
                })()}
                {err && <div className="note warn">{err}</div>}
                <button className="btn pri" disabled={parseAmountInput(amount) === ZERO}
                  onClick={async () => {
                    setErr(null);
                    const value = parseAmountInput(amount);
                    const [kind, id] = pick.split(':') as ['obligation' | 'credit' | 'goal', string];
                    try {
                      await setReserve({ accountId, forKind: kind, forId: id, amount: value });
                      flashMessage(`Закреплено ${formatMoney(value)} · ${labelOf(kind, id)}`);
                      setMode('saved'); setPick(''); setAmount('');
                      await onSaved();
                    } catch (e) {
                      setErr(e instanceof Error ? e.message : 'Не удалось сохранить');
                    }
                  }}>Сохранить</button>
              </div>
            )}

            {mine.length > 0 && (
              <button className="btn ghost" onClick={() => setMode('saved')}>Назад к счёту</button>
            )}
          </>
        )}
      </div>
    </SheetWrap>
  );
}

/**
 * Остаток в форме счёта — не поле, а число с датой сверки и кнопкой
 * «Скорректировать» (макет 14.10.2026, экран 3).
 *
 * Прежде остаток правился прямо в форме и перезаписывался молча: ни следа в
 * операциях, ни разницы в отчётах, а заодно сохранение имени сбрасывало дату
 * сверки и теряло минус у овердрафта.
 */
function CheckedOn({ state, account }: { state: AppState; account: AppState['accounts'][number] }) {
  const checked = state.transactions
    .filter((t) => t.type === 'adjustment' && t.accountId === account.id)
    .map((t) => t.occurredDate).sort().pop() ?? account.balanceAsOf;
  return checked ? <span className="rcasof">сверен {dayMonth(checked, state.today)}</span> : null;
}

/** Вид счёта в форме: те же три, что при создании, кроме кредитки. */
const KIND_CHOICES = [['debit', 'Карта'], ['cash', 'Наличные'], ['savings', 'Накопительный']] as const;

/**
 * «Копим на этом счёте» — выбрать цели из счёта (14.10.2026).
 *
 * Счёт цели выбирается и в самой цели («Где копим»), и здесь: отметил — цель
 * копится на этом счёте, снял — счёта у неё нет. Деньги не переводятся:
 * меняется только, куда пойдут следующие взносы «В цель». Нужной цели нет —
 * «Новая цель» здесь же.
 */
function AccountGoals({ state, accountId, onClose, onChanged }: {
  state: AppState; accountId: string; onClose: () => void; onChanged: () => void;
}) {
  const [creating, setCreating] = useState(false);
  const create = useContext(CreateGoalContext);
  const list = state.goals.filter((g) => g.status === 'active' || g.status === 'paused');
  // Галочка отмечается сразу, не дожидаясь, пока приложение перечитает цели.
  const [over, setOver] = useState<Record<string, boolean>>({});
  const set = async (goalId: string, here: boolean) => {
    setOver((p) => ({ ...p, [goalId]: here }));
    await updateGoal(goalId, { accountId: here ? accountId : '' });
    onChanged();
  };
  return (
    <SheetWrap title="Копим на этом счёте" onClose={onClose}>
      <div className="fieldlist account-list">
        {list.map((g) => {
          const saved = goalAccountId(state, g);
          const here = over[g.id] ?? saved === accountId;
          const on = here ? accountId : saved === accountId ? null : saved;
          const other = on && on !== accountId ? state.accounts.find((a) => a.id === on)?.name : null;
          return (
            <label className="li w goalpick" key={g.id}>
              <CategoryGlyph icon={glyphOf(g)} color={colorOfGoal(g)} size="s" />
              <span className="nm"><span className="account-name">{g.name}</span>
                <span>{on === accountId ? 'копим здесь' : other ? `сейчас — ${other}` : 'счёт не выбран'}</span></span>
              <input type="checkbox" checked={here} onChange={(e) => void set(g.id, e.target.checked)}
                aria-label={`Копить «${g.name}» на этом счёте`} />
            </label>
          );
        })}
        {list.length === 0 && <p className="note">Целей пока нет — заведи первую.</p>}
        {create && (
          <button className="li w newrow" onClick={() => setCreating(true)}>
            <CategoryGlyph icon="plus" color="gray" size="s" />
            <span className="nm"><span className="account-name">Новая цель</span></span>
          </button>
        )}
      </div>
      <div className="note">Деньги не переводятся — сюда пойдут следующие взносы «В цель».</div>
      {creating && create?.((id) => { setCreating(false); if (id) void set(id, true); })}
    </SheetWrap>
  );
}

/** Правка счёта: имя, тип, значок, цвет, галочка «Деньги для трат»; остаток — корректировкой. */
function EditAccount({ state, accountId, onClose, onSaved, onChanged, onEnvelopes, onAdjust }: {
  /** Изменилось то, что лист показывает (цели на счёте), — перечитать, не закрывая. */
  onChanged: () => void;
  state: AppState; accountId: string; onClose: () => void; onSaved: () => void;
  onEnvelopes: (id: string) => void;
  onAdjust: (id: string) => void;
}) {
  const acc = state.accounts.find((a) => a.id === accountId);
  const [pickGoals, setPickGoals] = useState(false);
  const [name, setName] = useState(acc?.name ?? '');
  const [kind, setKind] = useState<AppState['accounts'][number]['kind']>(acc?.kind ?? 'debit');
  const [liquid, setLiquid] = useState(acc?.isLiquid ?? true);
  const [icon, setIcon] = useState(acc ? accountIcon(acc) : 'credit-card');
  const [color, setColor] = useState<CategoryColor>(acc ? accountColor(acc) : 'blue');
  const [err, setErr] = useState<string | null>(null);
  if (!acc) return null;

  // Вид — переключателем; старые виды (вклад, брокерский) — в накопительных.
  const segKind = KIND_CHOICES.find(([k]) => groupOf(k) === groupOf(kind))?.[0] ?? 'debit';
  return (
    <SheetWrap title={acc.name} onClose={onClose} className="rcpt">
      <div className="rcform">
        {/* Тип решает, в какой группе счёт стоит на «Счетах» и в выборе счёта. */}
        <Segs label="Вид счёта" options={KIND_CHOICES} value={segKind}
          onChange={(k) => setKind(groupOf(kind) === groupOf(k) ? kind : k)} />
        {/* Остаток не правится полем: корректировкой, с записью в операциях. */}
        <BigAmount label="Остаток" readOnly={formatMoney(acc.balance)} right={<CheckedOn state={state} account={acc} />} />
        <button type="button" className="rcview more" onClick={() => onAdjust(acc.id)}>
          <Icon name="scale-balanced" size={13} /> Скорректировать</button>
        <div className="rccard rcsel">
          <CardInput label="Название" value={name} onChange={setName} width={170} />
          <CardLook icon={icon} color={color} onChange={(i, c) => { setIcon(i); setColor(c); }} />
          <CardToggle label="Тратим отсюда" checked={liquid} onChange={setLiquid}
            sub={liquid ? 'входит в «Можно тратить»'
              : 'не входит в «Можно тратить» — так помечают накопления под цель или под платёж по кредитке'} />
        </div>
        {err && <div className="warnline bad">{err}</div>}

        <button className="btn pri"
          onClick={async () => {
            try {
              await updateAccount(accountId, {
                name: name.trim() || acc.name,
                kind,
                // Значок и цвет по умолчанию не записываем: они следуют за видом
                // счёта, а записанные намертво пережили бы смену вида.
                icon: icon === accountIcon({ kind }) ? '' : icon,
                color: bankOf(icon) || color === accountColor({ kind }) ? '' : color,
                isLiquid: liquid,
              });
              onSaved();
            } catch (e) { setErr(e instanceof Error ? e.message : 'Не удалось сохранить'); }
          }}>Сохранить</button>

        <div className="rcafter">
          {(() => {
            // Цели, которые копятся на этом счёте («Где копим»), — строками, с суммой каждой.
            // Выбрать их можно отсюда же — «Выбрать цели»: не только из самой цели.
            if (acc.kind === 'credit_card') return null;
            const here = goalsOnAccount(state, accountId);
            return (
              <>
                <CardHead>Копим на этом счёте</CardHead>
                <div className="rccard rcsel">
                  {here.length === 0 && (
                    <div className="rcselrow"><span className="rclabel">Здесь пока не копим ни на одну цель</span></div>
                  )}
                  {here.map((g) => (
                    <div className="rcselrow" key={g.id}><span className="rclabel">{g.name}{g.status === 'paused' ? ' · на паузе' : ''}</span>
                      <span className="rcvalue"><span className="rcvaltext num">{formatMoney(g.currentAmount)}</span></span></div>
                  ))}
                  <button type="button" className="rcselrow" onClick={() => setPickGoals(true)}>
                    <span className="rclabel rcstrong">Выбрать цели</span>
                    <span className="rcvalue"><Icon name="chevron-right" size={14} className="rcchev" /></span></button>
                </div>
                {pickGoals && <AccountGoals state={state} accountId={accountId} onClose={() => setPickGoals(false)} onChanged={onChanged} />}
              </>
            );
          })()}
          {(() => {
            const mine = state.reserves.filter((r) => r.accountId === accountId);
            if (mine.length === 0) return null;
            const taken = add(ZERO, ...mine.map((r) => r.amount));
            return (
              <>
                <CardHead>Закреплено на счёте</CardHead>
                <div className="rccard rcsel">
                  {mine.map((r) => (
                    <button type="button" className="rcselrow" key={r.id} onClick={() => onEnvelopes(accountId)}>
                      <span className="rclabel">{reserveLabel(state, r)}</span>
                      <span className="rcvalue"><span className="rcvaltext num">{formatMoney(r.amount)}</span>
                        <Icon name="chevron-right" size={14} className="rcchev" /></span></button>
                  ))}
                  <div className="rcselrow"><span className="rclabel">Свободно на счёте</span>
                    <span className="rcvalue"><span className="rcvaltext num">{formatMoney(sub(acc.balance, taken))}</span></span></div>
                </div>
              </>
            );
          })()}
          <button className="btn" onClick={() => onEnvelopes(accountId)}>
            {state.reserves.some((r) => r.accountId === accountId)
              ? 'Изменить закреплённое'
              : 'Закрепить часть под платёж или цель'}
          </button>
          {/* Архив — тихим текстом: это не главное действие формы. */}
          <button className="textact"
            onClick={async () => {
              if (confirm('Архивировать счёт? Операции по нему останутся, вернуть его можно из «Архива».')) {
                await archiveAccount(accountId); onSaved();
              }
            }}>Архивировать счёт</button>
        </div>
      </div>
    </SheetWrap>
  );
}

/**
 * Периодичность платежа.
 *
 * Годовая страховка и недельная няня существовали и раньше, но записать их
 * было нечем: модель знала только число месяца, и 60 000 ₽ раз в год
 * превращались в 60 000 ₽ каждый месяц.
 */

function AddObligation({ state, onClose, onSaved, onLoan, subscription = false }: {
  state: AppState; onClose: () => void; onSaved: () => void;
  /** «+» у блока «Подписки» (04.11.2026): та же форма, сверху сервисы, сохраняется подпиской. */
  subscription?: boolean;
  /** Ипотека, кредит и рассрочка заводятся кредитом — как в настройке. */
  onLoan: (init: DLoan) => void;
}) {
  const payFrom = payAccounts(state.accounts);
  return (
    <ObligationSheet isNew title={subscription ? 'Новая подписка' : 'Обязательный платёж'} accounts={payFrom}
      {...(subscription ? { services: true } : { onLoan })}
      init={{
        name: '', amount: '', variable: false,
        sched: { date: todayPd(), repeat: DEFAULT_REPEAT, end: NEVER_ENDS },
        accountId: payFrom[0]?.id ?? state.accounts[0]?.id ?? '',
      }}
      tip={(v) => 'Обязательство держит резерв до подтверждения платежа: пока деньги не ушли, '
        + 'показывать их свободными нельзя.'
        + (v.sched.repeat === 'year'
          ? ' Годовой платёж считается один раз в год, а не каждый месяц — на месячный план он ляжет только в свой месяц.'
          : '')}
      onClose={onClose}
      onSave={async (v) => {
        await addObligation({
          name: v.name, accountId: v.accountId ?? payFrom[0]?.id ?? '', amount: parseAmountInput(v.amount),
          dayOfMonth: Number(v.sched.date.slice(8, 10)),
          isVariable: v.variable,
          schedule: scheduleOf(v.sched),
          ...(subscription ? { kind: 'subscription' as const } : {}),
        });
        onSaved();
      }} />
  );
}

function SetBaseline({ state, onClose, onSaved }: { state: AppState; onClose: () => void; onSaved: () => void }) {
  const est = (a: Amount) => (a.known && a.value > ZERO ? formatAmountInput(String(a.value / 100)) : '');
  const [need, setNeed] = useState(est(state.estimates.need));
  const [want, setWant] = useState(est(state.estimates.want));
  const [pinNeed, setPinNeed] = useState(state.usual.need.source === 'pinned');
  const [pinWant, setPinWant] = useState(state.usual.want.source === 'pinned');
  const now = (u: AppState['usual']['need']) => (u.source === 'history'
    ? `Сейчас по истории — ${formatMoney(u.value)}, ${usualSource(u)}`
    : null);

  return (
    <SheetWrap title="Обычные траты" onClose={onClose}
      tip={'«Обычно в месяц» считается по твоим тратам: серединный месяц из последних полных, до шести. '
        + 'Эта оценка — стартовая точка: чем дольше ведёшь учёт, тем меньше она весит. '
        + 'Закреплённое число история не меняет.'}>
      <div className="form">
        {/* «Кроме платежей выше» ссылалось на список, которого в этом окне нет. */}
        <label>Повседневные нужды в месяц, без обязательных платежей
          <AmountInput value={need} onChange={setNeed} placeholder="продукты, транспорт, бытовое" /></label>
        {now(state.usual.need) && <div className="note" style={{ marginTop: -6 }}>{now(state.usual.need)}</div>}
        <label className="check"><input type="checkbox" checked={pinNeed} onChange={(e) => setPinNeed(e.target.checked)} />
          Не менять по истории</label>
        <label>Свободные траты в месяц
          <AmountInput value={want} onChange={setWant} placeholder="кафе, развлечения" /></label>
        {now(state.usual.want) && <div className="note" style={{ marginTop: -6 }}>{now(state.usual.want)}</div>}
        <label className="check"><input type="checkbox" checked={pinWant} onChange={(e) => setPinWant(e.target.checked)} />
          Не менять по истории</label>
        <button className="btn pri" disabled={parseAmountInput(need) === ZERO}
          onClick={async () => {
            await setBaselines(parseAmountInput(need), parseAmountInput(want));
            await setUsualPinned('need', pinNeed);
            await setUsualPinned('want', pinWant);
            onSaved();
          }}>
          Сохранить
        </button>
      </div>
    </SheetWrap>
  );
}

/*
 * Меню главной добавляет в картину. Пример и удаление всех данных живут в
 * «Ещё → Данные»: рядом с обычными действиями они срабатывали по ошибке.
 */
function Menu(p: { onClose: () => void; onPick: (s: Sheet) => void }) {
  return (
    <SheetWrap title="Настройка" onClose={p.onClose}>
      <button className="li w" onClick={() => p.onPick('income')}>
        <div className="nm"><b>Добавить доход</b><span>сколько и когда приходит</span></div><span className="ch"><Icon name="chevron-right" size={13} /></span></button>
      <button className="li w" onClick={() => p.onPick('obligation')}>
        <div className="nm"><b>Добавить обязательный платёж</b><span>ипотека, ЖКХ, садик, подписки</span></div><span className="ch"><Icon name="chevron-right" size={13} /></span></button>
      <button className="li w" onClick={() => p.onPick('account')}>
        <div className="nm"><b>Добавить счёт</b><span>карта, наличные, накопительный, кредитка</span></div><span className="ch"><Icon name="chevron-right" size={13} /></span></button>
      <button className="li w" onClick={() => p.onPick('goal')}>
        <div className="nm"><b>Добавить цель</b><span>подушка, закрыть долг, покупка, резерв</span></div><span className="ch"><Icon name="chevron-right" size={13} /></span></button>
      <button className="li w" onClick={() => p.onPick('baseline')}>
        <div className="nm"><b>Обычные траты в месяц</b><span>нужны для плана и темпа трат</span></div><span className="ch"><Icon name="chevron-right" size={13} /></span></button>
    </SheetWrap>
  );
}

// ─────────────────────────────────────────────────────── вспомогательное

/**
 * Чего не хватает, чтобы цифра стала верной (PRD §4.11).
 *
 * Именно перечень, а не оценка полноты: человеку нужно знать, что сделать,
 * а не насколько он «неполон». Пустой список означает, что спрашивать больше
 * нечего — и тогда на экране не появляется ничего.
 *
 * «Не знаю» про обычные траты — это ответ, а не пробел: переспрашивать его
 * незачем, иначе приложение выпрашивает то, чего человек и правда не знает.
 */
/** Чего не хватает для картины — и куда за этим идти. */
type MissingFact = { key: string; label: string; open: MissingOpen };
export type MissingOpen = 'income' | 'account' | 'balance' | 'card' | 'obligation' | 'baseline';

function missingFactList(s: AppState): MissingFact[] {
  const out: MissingFact[] = [];
  const liquid = s.accounts.filter((a) => a.isLiquid && !a.archived && a.kind !== 'credit_card');
  if (liquid.length === 0) out.push({ key: 'account', label: 'Счёт, с которого живёшь', open: 'account' });
  // Пустая карта без единой операции — остаток просто не назвали: с нулём
  // ни «можно тратить», ни запас не посчитать.
  else if (liquid.every((a) => a.balance === ZERO) && s.transactions.length === 0) {
    out.push({ key: 'balance', label: 'Сколько сейчас на карте', open: 'balance' });
  }
  if (s.incomeStreams.length === 0) out.push({ key: 'income', label: 'Доход', open: 'income' });
  const cc = s.accounts.find((a) => a.kind === 'credit_card');
  if (cc && s.creditTerms.length === 0) out.push({ key: 'card', label: 'Условия кредитки', open: 'card' });
  if (s.obligations.length === 0 && s.obligationsKnown !== true) out.push({ key: 'obligation', label: 'Обязательные платежи', open: 'obligation' });
  if (!s.baselines.need.known || (s.baselines.need.known && s.baselines.need.value === ZERO)) {
    out.push({ key: 'baseline', label: 'Обычные траты в месяц', open: 'baseline' });
  }
  return out;
}

const kopAbs = (m: Money): Money => (m < 0 ? (-m as Money) : m);
const clampDay = (n: number): number => (Number.isFinite(n) ? Math.min(31, Math.max(1, Math.trunc(n))) : 1);

function shiftDays(d: PlainDate, n: number): PlainDate {
  const dt = new Date(`${d}T00:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10) as PlainDate;
}

function dayTitle(date: string): string {
  const today = todayPd();
  if (date === today) return 'Сегодня';
  if (date === shiftDays(today, -1)) return 'Вчера';
  return formatDate(date);
}


/**
 * Конверты под платёж, который уже оплачен.
 *
 * Не «все конверты обязательства»: пока платёж не подтверждён, конверт делает
 * ровно то, для чего заведён. Речь только о том, что осталось после оплаты.
 */
const staleHolds = (state: AppState, obligationId: string) =>
  state.reserves.filter(
    (r) => r.forKind === 'obligation' && r.forId === obligationId && r.amount > ZERO,
  );

/**
 * Сколько потрачено из корзины в этой версии раскладки.
 *
 * Считается по версии, а не по календарю: пересогласование посреди дня и
 * запись задним числом иначе попали бы не в ту корзину. И теми же правилами,
 * что остаток корзины: оплата обязательного платежа и взнос в цель идут из
 * отложенного, возврат возвращает деньги. Своя выборка здесь считала их
 * тратой, и «Выделено − Потрачено» расходилось с «Осталось».
 */
function spentFrom(
  state: AppState, allocation: AcceptedAllocation | null, part: 'need' | 'want',
): Money {
  if (!allocation) return ZERO;
  const view = allocationViewOf(state, allocation);
  return part === 'need'
    ? sub(allocation.lines.need, view.needLeft)
    : sub(allocation.lines.want, view.wantLeft);
}

/** Какая часть месяца прошла, 0..1. По ней траты сравниваются с календарём. */
function monthShare(today: PlainDate): number {
  const day = Number(today.slice(8, 10));
  return day / daysInMonth(Number(today.slice(0, 4)), Number(today.slice(5, 7)));
}

const MONTHS = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
function formatDate(d: string): string {
  const [, m, day] = d.split('-');
  return `${Number(day)} ${MONTHS[Number(m) - 1]}`;
}

const stageMonths = (stage: string): number =>
  stage === 'critical' ? 1 : stage === 'base' ? 3 : 6;

/** Под что закреплён конверт — для списков в интерфейсе. */
function reserveLabel(state: AppState, r: AppState['reserves'][number]): string {
  if (r.forKind === 'credit') {
    return `Платёж по «${state.accounts.find((a) => a.id === r.forId)?.name ?? 'кредитке'}»`;
  }
  if (r.forKind === 'obligation') {
    return state.obligations.find((o) => o.id === r.forId)?.name ?? 'платёж удалён';
  }
  if (r.forKind === 'goal') {
    return `Цель «${state.goals.find((g) => g.id === r.forId)?.name ?? '—'}»`;
  }
  // Буфер стабильного бюджета: деньги, из которых человек платит себе.
  // Названия плана тут нет намеренно — план один, и «запас» человек узнаёт
  // быстрее любого придуманного имени.
  if (r.forKind === 'stable') return 'Запас стабильного бюджета';
  return 'бронь';
}

/**
 * Съёмка чека (§6.11).
 *
 * Разбор чека — самая дорогая и самая хрупкая часть ввода, поэтому экран
 * устроен вокруг отказа, а не вокруг успеха. Если сборка строк не удалась,
 * человек получает просьбу переснять с понятной причиной, а не список позиций,
 * которому нельзя верить: половина чека хуже, чем его отсутствие.
 *
 * Снимок никуда не сохраняется — ни на телефоне, ни на сервере. Он живёт ровно
 * до ответа разбора.
 */
function ScanReceipt({ state, initialAccountId, onClose, onSaved, onFile }: {
  state: AppState; onClose: () => void; onSaved: () => void;
  /** Открыть «Файл»: скрин из банка разбирает он, а не камера. */
  initialAccountId?: string;
  onFile?: (accountId: string) => void;
}) {
  const [categories, setCategories] = useState(state.categories);
  const cats = topLevel(categories);
  const spendable = state.accounts.filter((a) => !a.archived);
  const today = todayPd();
  const [stage, setStage] = useState<'pick' | 'work' | 'done' | 'fail'>('pick');
  const [step, setStep] = useState('');
  const [parsed, setParsed] = useState<ParsedReceipt | null>(null);
  /** Позиции правятся, поэтому живут в состоянии, а не читаются из разбора. */
  const [items, setItems] = useState<ReceiptItem[]>([]);
  /**
   * Часть плана по каждой позиции. Видна и нужна только там, где категория
   * смешанная: у остальных её задаёт сама категория, как при ручной записи.
   */
  const [parts, setParts] = useState<PlanPart[]>([]);
  /** Категория каждой позиции: в одном чеке бывают продукты и бытовая химия. */
  const [itemCats, setItemCats] = useState<string[]>([]);
  /** Категорию подтвердила модель или человек; иначе строка отмечена. */
  const [sure, setSure] = useState<boolean[]>([]);
  const [sorting, setSorting] = useState(false);
  /** Строки, которые человек поправил сам: ответ модели их не трогает. */
  const touched = useRef<Set<number>>(new Set());
  const touchedCat = useRef<Set<number>>(new Set());
  /** Новые категории для позиций, которым в справочнике нет места. */
  const nc = useNewCategories({
    mode: state.newCategoryMode, categories, onCategories: setCategories, ids: itemCats,
    touched: (n) => touchedCat.current.has(n),
    apply: (changes) => {
      setItemCats((prev) => prev.map((c, n) => changes.get(n) ?? c));
      setSure((prev) => prev.map((x, n) => x || changes.has(n)));
    },
  });
  const [editItem, setEditItem] = useState<number | null>(null);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  /**
   * Уменьшенная копия снимка для архива.
   *
   * Хранится обещанием, а не значением: если человек нажмёт «Записать» раньше,
   * чем копия готова, запись её дождётся. Проверка поймала ровно это — один
   * прогон сохранил чек без фотографии и ничего не сказал.
   */
  const photoRef = useRef<Promise<string | null> | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => { void apiMe().then((u) => setSignedIn(u !== null)).catch(() => setSignedIn(false)); }, []);
  const [accId, setAccId] = useState(
    importAccountId(state.accounts, initialAccountId),
  );
  /** Пока модель не ответила — и если не ответит, — позиция стоит здесь. */
  const fallbackCat = cats.find((c) => c.id === 'groceries')?.id ?? cats[0]?.id ?? 'other';
  const [date, setDate] = useState<PlainDate>(today);
  const [time, setTime] = useState('12:00');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const catalog = useMemo(() => modelCatalog(categories), [categories]);
  /** Категории, которые человек уже давал этим позициям в прошлых чеках. */
  const memory = useMemo(() => rememberedCategories(state.transactions, categories), [state.transactions, categories]);

  const handle = async (file: File | undefined) => {
    if (!file) return;
    setStage('work'); setErr(null); setParsed(null);
    try {
      setStep('Готовлю снимок');
      const img = await prepareReceipt(file);
      // Архивную копию делаем сразу: файл у нас в руках только здесь.
      photoRef.current = prepareReceipt(file, ARCHIVE_SIDE)
        .then((small) => `data:image/jpeg;base64,${small.base64}`)
        .catch(() => null);
      // Тот же снимок показывает «Просмотреть»: сверить позиции с бумагой.
      void photoRef.current.then(setPreview);
      setStep(`Читаю чек · ${(img.bytes / 1024).toFixed(0)} КБ`);
      const r = await parseReceiptPhoto(img.base64, img.mimeType);
      setParsed(r);
      setAccId((selected) => importAccountId(state.accounts, selected, r.account));
      setItems(r.items);
      // Словарь отвечает сразу — человек уже видит разложенный чек. Модель
      // приходит следом и поправляет то, чего словарь не знает: названия в
      // чеке сокращены, и правил на них не напасёшься.
      setParts(r.items.map((i) => classifyItem(i.name).part));
      // Позиция, которой человек уже давал категорию, встаёт туда же сразу,
      // и модель её не трогает — как правку, сделанную сейчас.
      const known = r.items.map((i) => memory.items.get(nameKey(i.name)) ?? null);
      setItemCats(known.map((c) => c ?? fallbackCat));
      setSure(known.map((c) => c !== null));
      touched.current = new Set();
      touchedCat.current = new Set(known.flatMap((c, n) => (c ? [n] : [])));
      if (r.date) setDate(r.date);
      if (r.time) setTime(r.time);
      setStage(r.trusted ? 'done' : 'fail');
      if (r.trusted && r.items.length > 0) {
        const names = r.items.map((i) => i.name);
        // Правки человека важнее ответа модели: он смотрит на чек, она —
        // на строку названия. Поэтому подменяем только то, к чему он ещё
        // не притронулся.
        void classifyBasket(names).then((byModel) => {
          if (!byModel) return;
          setParts((prev) => prev.map((p, n) => (touched.current.has(n) ? p : byModel[n] ?? p)));
        });
        setSorting(true);
        void categorizeBasket(names, catalog).then((byModel) => {
          setSorting(false);
          if (!byModel) return;
          setItemCats((prev) => prev.map((c, n) => (touchedCat.current.has(n) ? c : byModel[n] ?? c)));
          setSure((prev) => prev.map((x, n) => x || byModel[n] !== null));
          // Позиции, которой не нашлось категории, — новая.
          void nc.suggest(names.flatMap((name, n) => (byModel[n] === null && !touchedCat.current.has(n) ? [{ n, name }] : [])));
        });
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось разобрать чек');
      setStage('fail');
    }
  };

  const catOf = (n: number) => nc.all.find((c) => c.id === itemCats[n]);
  /** Смешанная категория (`defaultPart: null`) решает часть плана по позиции. */
  const partOf = (n: number): PlanPart => catOf(n)?.defaultPart ?? parts[n] ?? 'need';
  // Итог считается по позициям, а не по напечатанному на чеке: человек мог
  // поправить цену, которую распознавание прочло неверно, и тогда права
  // правка, а не бумага. Расхождение показываем, решает человек.
  const total = items.reduce((a, i) => a + i.sum, 0);
  /** Сколько из чека пришлось на каждую категорию — по убыванию. */
  const byCategory = [...items.reduce((m, i, n) => {
    const id = itemCats[n] ?? fallbackCat;
    return m.set(id, (m.get(id) ?? 0) + i.sum);
  }, new Map<string, number>())].sort((a, b) => b[1] - a[1]);
  const printed = parsed?.total ?? null;
  const drift = printed !== null ? total - printed : 0;

  const save = async () => {
    if (!parsed || total <= 0 || busy) return;
    const live = (id: string | undefined) => nc.all.some((c) => c.id === id && !c.archived && c.kind !== 'income');
    if (!items.every((_, n) => live(itemCats[n]))) { setErr('Выбери категорию у каждой позиции'); return; }
    setBusy(true); setErr(null);
    try {
      // Снимок сохраняется один раз: смешанный чек порождает две операции,
      // и обе ссылаются на одну фотографию.
      const shot = await (photoRef.current ?? Promise.resolve(null));
      const receiptId = shot ? await putReceiptPhoto(shot) : null;
      // Новые категории появляются вместе с тратами.
      const real = await nc.materialize(items.map((_, n) => itemCats[n]!));

      // Одна операция на категорию и часть плана. У операции одна категория и
      // одна часть (И1, ограничение tx_part_only_expense), и это не обход
      // правила, а честная запись: в одном пакете было несколько разных трат.
      const groups = new Map<string, { categoryId: string; part: PlanPart; amount: number; items: TxItem[] }>();
      items.forEach((i, n) => {
        const categoryId = real(itemCats[n]!);
        const part = partOf(n);
        const g = groups.get(`${categoryId}:${part}`) ?? { categoryId, part, amount: 0, items: [] };
        g.amount += i.sum;
        g.items.push({ name: i.name, qty: i.qty, price: kop(i.price), sum: kop(i.sum) });
        groups.set(`${categoryId}:${part}`, g);
      });
      for (const g of groups.values()) {
        if (g.amount <= 0) continue;
        await addExpense({
          accountId: accId,
          amount: kop(g.amount),
          categoryId: g.categoryId,
          part: g.part,
          date,
          time,
          ...(parsed.merchant ? { note: parsed.merchant } : {}),
          items: g.items,
          source: 'receipt_photo',
          ...(receiptId ? { receiptId } : {}),
        });
      }
      rememberType('expense');
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось сохранить');
      setBusy(false);
    }
  };

  /** Снимок, сделанный раньше, — из галереи. */
  const gallery = (label: string) => (
    <label className="btn ghost file">
      {label}
      <input type="file" accept="image/*" onChange={(e) => void handle(e.target.files?.[0])} />
    </label>
  );

  return (
    <SheetWrap title="Кассовый чек" onClose={onClose} className="rcpt"
      tip={stage === 'pick' && signedIn !== false
        ? 'Разгладь чек и сними сверху, целиком и при ровном свете. Мятый и снятый под '
          + 'углом чек распознаётся плохо: строки слипаются, и состав приходится отбрасывать.'
        : undefined}>
      {stage === 'pick' && (
        <>
          {signedIn === false ? (
            <>
              <div className="note alarm">
                Разбор чека работает после входа в аккаунт.
                <Tip>Он идёт на сервере и стоит денег, поэтому привязан к аккаунту. Сказать
                  об этом после того, как ты снял чек, было бы издевательством — говорю
                  до. Сумму по чеку всегда можно записать вручную.</Tip>
              </div>
              <button className="btn ghost" onClick={onClose}>Запишу вручную</button>
            </>
          ) : null}
        </>
      )}

      {stage === 'work' && <div className="note">{step}…</div>}
      {/*
        Камера открывается сразу, поверх листа (макет 14.10.2026): лист нужен
        только для разбора. Пока снимок читается, она стоит с замершим кадром
        и «Читаю чек…». Закрыл камеру — закрылся и лист.
      */}
      {signedIn !== false && (stage === 'pick' || stage === 'work') && (
        <ReceiptCamera reading={stage === 'work'} onShot={(f) => void handle(f)} onClose={onClose} />
      )}

      {stage === 'fail' && !err && (parsed?.kind === 'statement' || parsed?.kind === 'bank_slip') ? (
        /* Скрин из банка — не чек: его операции разбирает «Файл», каждую своей строкой. */
        <>
          <div className="note alarm">Похоже, это скрин из банка — его разбирает «Файл»</div>
          {onFile && <button className="btn pri" onClick={() => onFile(accId)}>Открыть «Файл»</button>}
          {gallery('Выбрать другое фото')}
          <button className="btn ghost" onClick={onClose}>Заполню вручную</button>
        </>
      ) : stage === 'fail' && (
        <>
          <div className="note alarm">{err ?? parsed?.problems[0] ?? 'Чек не разобрался'}
            <Tip>Состав не показываем: если арифметика не сошлась, часть позиций наверняка
              прочитана неверно, а незаметная ошибка хуже отсутствия данных. Сумму всегда
              можно записать вручную.</Tip></div>
          <button className="btn pri" onClick={() => { setErr(null); setStage('pick'); }}>Снять ещё раз</button>
          {gallery('Выбрать другое фото')}
          <button className="btn ghost" onClick={onClose}>Заполню вручную</button>
        </>
      )}

      {stage === 'done' && parsed && (
        <>
          <ReceiptHead total={total} count={items.length} preview={preview} />
          <ReceiptSelectors accounts={spendable} accId={accId} onAccount={setAccId}
            when={{
              label: `${date === today ? 'Сегодня' : dayTitle(date)}, ${time}`,
              input: (
                <DateButton overlay label="Дата и время чека" value={date} max={today}
                  onChange={(d) => setDate(pd(d))} time={time} onTime={setTime} />
              ),
            }} />
          {/* Итога в документе не было — сумма сложена из позиций. Сверить её
              не с чем, кроме памяти человека, поэтому говорим прямо. */}
          {parsed.totalFromItems && (
            <div className="note warn">
              Итог не напечатан — сумма сложена из позиций. Сверь её с той, что списал банк.
            </div>
          )}
          {drift !== 0 && printed !== null && (
            <div className="note warn">
              На чеке напечатано {formatMoney(kop(printed))} — на {formatMoney(kop(Math.abs(drift)))}{' '}
              {drift > 0 ? 'больше' : 'меньше'} суммы позиций. Записана будет сумма позиций:
              ты правил их, а значит доверяешь им, а не бумаге.
            </div>
          )}
          <SplitBanner show={byCategory.length > 1} />
          {(sorting || nc.busy) && <div className="note">Подбираю категории…</div>}
          <NewCatsAsk nc={nc} nameOf={(n) => items[n]?.name ?? ''} />
          <NewCatsLines nc={nc} ids={items.map((_, n) => itemCats[n] ?? fallbackCat)} />
          <ReceiptItems lines={items} categories={nc.all} onCategories={setCategories}
            categoryOf={(n) => itemCats[n] ?? fallbackCat}
            unsure={(n) => !sorting && !nc.busy && !sure[n]}
            isNew={(n) => isNewCategory(itemCats[n])}
            onPick={(n, id) => {
              touchedCat.current.add(n);
              nc.resolve(n);
              setItemCats((p) => p.map((x, j) => (j === n ? id : x)));
              setSure((p) => p.map((x, j) => (j === n ? true : x)));
            }}
            onOpen={(n) => setEditItem(n)} />
          {parsed.free.length > 0 && (
            <div className="note">
              Не в трате, потому что достались бесплатно: {parsed.free.map((f) => f.name).join(', ')}.
            </div>
          )}
          {err && <div className="note alarm">{err}</div>}
          <ReceiptBar total={total} disabled={busy || total <= 0}
            label={busy ? 'Сохраняю…' : `Записать ${formatMoney(kop(total))}`}
            onSave={() => void save()} onCancel={onClose} />
        </>
      )}

      {editItem !== null && items[editItem] && (
        <ReceiptItemSheet
          item={items[editItem]}
          part={parts[editItem] ?? 'need'}
          categories={nc.all} onCategories={setCategories}
          categoryId={itemCats[editItem] ?? fallbackCat}
          onClose={() => setEditItem(null)}
          onSave={(next, nextPart, categoryId) => {
            setItems((p) => p.map((x, j) => (j === editItem ? next : x)));
            touched.current.add(editItem);
            touchedCat.current.add(editItem);
            nc.resolve(editItem);
            setParts((p) => p.map((x, j) => (j === editItem ? nextPart : x)));
            setItemCats((p) => p.map((x, j) => (j === editItem ? categoryId : x)));
            setSure((p) => p.map((x, j) => (j === editItem ? true : x)));
            setEditItem(null);
          }}
          onDelete={() => {
            setItems((p) => p.filter((_, j) => j !== editItem));
            setParts((p) => p.filter((_, j) => j !== editItem));
            setItemCats((p) => p.filter((_, j) => j !== editItem));
            setSure((p) => p.filter((_, j) => j !== editItem));
            nc.removeRow(editItem);
            setEditItem(null);
          }} />
      )}
      <NewModeQuestion nc={nc} names={[...nc.pending.keys()].map((n) => items[n]?.name ?? '')} />
    </SheetWrap>
  );
}

/**
 * Правка позиции чека.
 *
 * Распознавание ошибается в цене и в названии, и без правки человеку остаётся
 * либо записать неверные данные, либо отказаться от состава целиком. Сумма
 * позиции считается из цены и количества, а не вводится отдельно: три числа,
 * два из которых противоречат третьему, — это способ получить чек, который не
 * сходится сам с собой.
 */
function ReceiptItemSheet({ item, part, categories, onCategories, categoryId, onClose, onSave, onDelete }: {
  item: ReceiptItem; part: PlanPart;
  categories: Category[]; onCategories: (c: Category[]) => void;
  categoryId: string;
  onClose: () => void;
  onSave: (next: ReceiptItem, part: PlanPart, categoryId: string) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(item.name);
  const [qty, setQty] = useState(String(item.qty));
  const [price, setPrice] = useState(formatAmountInput(String(item.price / 100)));
  const [p, setP] = useState<PlanPart>(part);
  const [cat, setCat] = useState(categoryId);
  /** Категория смешанная — часть плана решается по позиции, и её видно. */
  const mixed = categories.find((c) => c.id === cat)?.defaultPart === null;

  const q = Number(qty.replace(',', '.'));
  const priceKop = parseAmountInput(price);
  const sum = Number.isFinite(q) && q > 0 ? Math.round(priceKop * q) : 0;
  const ready = name.trim() !== '' && sum > 0;

  return (
    <SheetWrap title="Позиция чека" onClose={onClose}>
      <div className="form">
        <label>Название
          <input value={name} onChange={(e) => setName(e.target.value)} /></label>
        <div className="obfields sum-day">
          <label>Цена за единицу
            <AmountInput value={price} onChange={setPrice} placeholder="89,90" /></label>
          <label>Количество
            <input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="decimal" /></label>
        </div>
        <div className="oprows">
          <CategoryField categories={categories} kind="expense" value={cat} onChange={setCat} onUpdated={onCategories} />
          {mixed && (
          <div className="oprow">Часть плана
            <div className="opseg">
              <button className={p === 'need' ? 'on' : ''} onClick={() => setP('need')}>Повседневные нужды</button>
              <button className={p === 'want' ? 'on' : ''} onClick={() => setP('want')}>Свободные траты</button>
            </div>
          </div>
          )}
          {/* Подпись с «i» — одним куском: строка раскладывает детей по краям,
              и значок без обёртки встал бы посередине. */}
          <div className="oprow"><span>Стоимость позиции
            <Tip>Стоимость считается из цены и количества. Вводить её отдельно нельзя: три
              числа, два из которых противоречат третьему, дают чек, не сходящийся сам с собой.</Tip></span>
            <span className="opval" style={{ paddingRight: 2, backgroundImage: 'none' }}>
              {formatMoney(kop(sum))}
            </span>
          </div>
        </div>
        <button className="btn pri" disabled={!ready}
          onClick={() => onSave({ name: name.trim(), qty: q, price: priceKop, sum }, p, cat)}>
          Сохранить
        </button>
        <button className="textact danger" onClick={onDelete}>Убрать из чека</button>
      </div>
    </SheetWrap>
  );
}

/**
 * «Цели» — вкладкой нижнего меню (27.10.2026, docs/design/goals-tab-2026-10-27).
 *
 * Сверху — сколько накоплено на всё и сколько откладывается в месяц: одна
 * карточка, как «Капитал» в «Деньгах». Дальше — что отложить сейчас и сами
 * цели. Целей нет — экран предлагает первую: вопрос и четыре частых цели
 * плитками, как счета в настройке.
 */
const GOAL_STARTERS: ReadonlyArray<readonly [string, string, string, 'emergency_fund' | 'purchase' | 'debt_payoff']> = [
  ['umbrella', 'Подушка', 'на чёрный день', 'emergency_fund'],
  ['plane', 'Отпуск', 'поездка, отдых', 'purchase'],
  ['laptop', 'Техника', 'телефон, ноутбук', 'purchase'],
  ['credit-card', 'Закрыть долг', 'кредитку, кредит', 'debt_payoff'],
];
function GoalsPage({ state, needFloor, ask, onAddGoal, onGoal, onFund, onStable }: {
  state: AppState; needFloor: Money; ask: ReactNode;
  onAddGoal: (preset?: { name: string; kind: 'emergency_fund' | 'purchase' | 'debt_payoff' }) => void;
  onGoal: (id: string) => void; onFund: (id: string) => void; onStable: () => void;
}) {
  const live = state.goals.filter((g) => g.status === 'active' || g.status === 'paused');
  const any = live.length > 0 || state.goals.some((g) => g.status === 'achieved') || stableSnapshot(state) !== null;
  const saved = add(ZERO, ...live.map((g) => g.currentAmount));
  const target = add(ZERO, ...live.map((g) => g.targetAmount));
  const pct = target > ZERO ? Math.min(100, Math.round((saved / target) * 100)) : 0;
  const monthly = add(ZERO, ...live.filter((g) => g.status === 'active').map((g) => (g.monthlyContribution > ZERO ? g.monthlyContribution
    : g.targetDate ? monthlyForDeadline(g, g.targetDate, state.today) : ZERO)));
  return (
    <>
      <header className="pgtitle"><h1>Цели</h1>{ask}</header>
      {!any ? (
        <section className="blk goalempty">
          <div className="goalempty-hero">
            <span className="goalempty-ico"><Icon name="bullseye-arrow" size={26} /></span>
            <b>На что копишь?</b>
            <p>Назови сумму и срок — SKOKA посчитает, сколько откладывать с каждого прихода.</p>
          </div>
          <div className="addtiles">
            {GOAL_STARTERS.map(([icon, name, sub, kind]) => (
              <button key={name} className="addtile" onClick={() => onAddGoal({ name: kind === 'debt_payoff' ? '' : name, kind })}>
                <span className="addtile-i"><Icon name={icon} size={22} /></span>
                <b>{name}</b><span>{sub}</span>
              </button>
            ))}
          </div>
          <button className="btn ghost" onClick={() => onAddGoal()}>Своя цель</button>
        </section>
      ) : (
        <>
          {live.length > 0 && (
            <section className="blk goalsum">
              <div className="blkhead"><b>Накоплено</b>
                <button className="btn inline blkadd" onClick={() => onAddGoal()} aria-label="Добавить цель"><Icon name="plus" size={13} />Цель</button></div>
              <div className="li"><div className="nm">
                <b className="mega-s num">{formatMoney(saved)}</b>
                <span>из {formatMoney(target)} · {plural(live.length, 'цель', 'цели', 'целей')}</span>
              </div></div>
              <div className="goalbar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${pct}%` }} /></div>
              {monthly > ZERO && (
                <div className="li goalsum-m"><div className="nm"><span>Откладывается в месяц</span></div>
                  <div className="am num">≈ {formatMoney(monthly)}</div></div>
              )}
            </section>
          )}
          <GoalsSection state={state} needFloor={needFloor} onAddGoal={() => onAddGoal()} onGoal={onGoal} onFund={onFund} onStable={onStable} />
        </>
      )}
    </>
  );
}

/**
 * Подушка и цели (макет 14.10.2026); с 27.10.2026 — во вкладке «Цели».
 *
 * Подушка и запас стабильного бюджета лежат «на чёрный день»: у них нет
 * срока и нет покупки в конце. Отпуск и ноутбук — цели: у них есть сумма, к
 * которой идут, и иногда дата. Это разные решения при нехватке денег, поэтому
 * и блоки разные — один под другим, без вложенных вкладок: вкладки уже стоят
 * заголовком «Денег».
 *
 * Срок называется только тогда, когда его есть из чего посчитать: без
 * назначенного взноса стоит «Срок пока не рассчитан», а не выдуманная дата.
 */
function GoalsSection({ state, needFloor, onAddGoal, onGoal, onFund, onStable }: {
  state: AppState;
  /** Месяц расходов: от него считается запас подушки. */
  needFloor: Money;
  onAddGoal: () => void;
  onGoal: (id: string) => void;
  onFund: (id: string) => void;
  onStable: () => void;
}) {
  // На паузе — тоже: цель не исчезает, она только не просит денег.
  const active = state.goals.filter((g) => g.status === 'active' || g.status === 'paused');
  const reserves = active.filter((g) => g.kind === 'emergency_fund');
  const goals = active.filter((g) => g.kind !== 'emergency_fund');
  // Собранные — внизу с галочкой: раньше они молча пропадали из списка.
  const achieved = state.goals.filter((g) => g.status === 'achieved');
  const stable = stableSnapshot(state);
  const funded = incomeFacts(state.transactions);
  const nudge = savingsNudge({
    incomeFacts: funded, today: state.today, incomeStreams: state.incomeStreams, goals: state.goals,
  });
  const card = (g: typeof active[number]) => (
    <GoalRow key={g.id} state={state} goal={g} needFloor={needFloor} detail onOpen={() => onGoal(g.id)} />
  );

  return (
    <>
      {nudge.asks.length > 0 && (
        <section className="blk">
          <div className="blkhead"><b>Отложить с этого прихода</b></div>
          {nudge.asks.slice(0, 3).map((a) => {
            const g = state.goals.find((x) => x.id === a.goalId);
            const acc = g ? state.accounts.find((x) => x.id === goalAccountId(state, g)) : undefined;
            return (
            <div className="li fundrow" key={a.goalId}>
              {/* Значок — как у строки цели; ниже — сколько и на какой счёт: заголовок уже сказал, что это взнос прихода. */}
              <span className={`catico s-r c-${g?.kind === 'emergency_fund' ? 'blue' : 'green'}`}>
                <Icon name={g?.kind === 'emergency_fund' ? 'umbrella' : 'bullseye-arrow'} size={14} /></span>
              <div className="nm"><b>{a.name}</b>
                <span><b className="num">{formatMoney(a.amount)}</b>{acc ? ` · ${acc.name}` : ''}</span></div>
              <button className="btn inline" onClick={() => onFund(a.goalId)}>Отложил</button>
            </div>
            );
          })}
        </section>
      )}
      {(reserves.length > 0 || stable) && (
        <section className="blk">
          <div className="blkhead"><b>На чёрный день</b></div>
          {reserves.map(card)}
          {stable && (
            <button className="li w" onClick={onStable}>
              <div className="ico"><LockIcon /></div>
              <div className="nm"><b>Запас стабильного бюджета</b>
                <span className="num">{formatMoney(stable.buffer)}</span>
                <span>Счёт: {state.accounts.find((a) => a.id === stable.plan.accountId)?.name ?? '—'}</span>
                <span>{stable.outlook.due
                  ? `Следующая выплата себе ${dayMonthYear(stable.outlook.due.scheduledDate)}`
                  : 'Выплат впереди не назначено'}</span>
              </div>
              <span className="ch"><Icon name="chevron-right" size={13} /></span>
            </button>
          )}
        </section>
      )}

      <section className="blk">
        {/* Добавить — словами в заголовке и строкой «Новая цель» в конце
            списка (16.10.2026): маленький «+» в углу не находили. */}
        <div className="blkhead"><b>Цели</b></div>
        {[...goals, ...achieved].map(card)}
        <NewGoalRow onAdd={onAddGoal}
          hint={reserves.length === 0 ? 'подушка, отпуск, техника, крупная покупка' : 'отпуск, техника, крупная покупка'} />
      </section>

    </>
  );
}


